// =============================================================================
// app/src/lib/consultas.ts — tudo que fala com o banco passa por aqui.
//
// Nenhuma tela monta query solta: assim, quando uma RPC muda de assinatura,
// existe um arquivo só para corrigir. Os contratos espelham core/sql/functions.
// =============================================================================
import type {
  IndicadoresDono, ResultadoOperacionalRow, ValorDoEstoqueRow,
  FiadoRow,
  CanalPedido, PedidoItem, PedidoRecebimento, PedidoResumo,
  AnexoDaConta, CategoriaConta, ContaPagar, ContaRecorrente, TipoDeAnexo,
  AgendaOcorrencia, AgendaPrioridade, AgendaRecorrencia, AgendaTarefa,
  AcessosPorDiaRow, AcessosPorRotaRow, AgenteDeployAceito, AgenteStatus,
  BuscarClientesRow, BuscarPecasRow, CaixaDiarioRow, CaixaMensalRow, CaixaPorHoraRow, CashCategory,
  CanalOrcamento, CaracteristicasDaPeca, CustomerVehicle,
  PecaVendidaNoDia, ReposicaoDecisao, SituacaoReposicao, SugestaoPedido,
  CashFlowType, CashMovement, Customer, CustosMensaisRow, DeployResumo, EmitirAVerRow,
  Conferencia, LinhaDaConferencia,
  FaxinaDecisao, FaxinaFamilia, FaxinaItem, SituacaoFaxina,
  CenarioDiaDaSemana,
  NavegacaoDoCatalogo, PartType, PecasParaOCarro,
  TaxaDeCartao,
  OrigemAVer,
  EmploymentTerms, ErroRecenteRow, FiltroCaracteristicas,
  FolhaMensalRow,
  Goal, GoalMetric, HistoricoClienteRow, LimitesEfetivos, LimitesOperacao, LimitesPorPessoa,
  LiberacaoMeuDinheiro,
  MargensRow, MetaProgressoRow, MetricasNegocio,
  ColaboradorDoMural, NotaDoMural,
  OpcoesBuscaGuiada,
  OrcamentoEmitidoRow, Papel, PaymentMethod, PayrollEntry, PlantaDaLojaSalva, PlantaRestauradaResultado,
  PlantaSalvaResultado, Product, VersaoDaPlanta, EnderecoDaPecaResultado,
  ItemDaVendaParaGerencia, VendaParaGerencia, VendaPeloCodigo,
  ProductApplication, ProductAttribute, ProductAttributeValue,
  CaixaDoDia, PrintJob, ProductCode, SaudeDaFila, ProdutoFinanceiroRow, Profile, Promotion, Quote, QuoteItem, ReposicaoRow,
  QuitacaoDaVenda,
  EntregaPendente, FluxoDia, PagamentoDeLote, Reservation, ReservationItem,
  ReservationPayment, ResumoDoDia,
  PartnerAccount, PartnerOrder, PartnerOrderItem, ResumoParceiroB2B, SituacaoPedidoB2B,
  ResultadoMensalRow, SalePayment, SaudeSistema, Stock, StockMoveReason, StockMovement,
  CarrinhoDaPecaRow, Devolucao, DevolucaoItem, FornecedorDaPecaRow, ISODate,
  MeuCarrinhoRow, ProductImageInfo, RegistrarDevolucaoRow,
  StoreCredit, StoreCreditUse, StoreZone, Supplier, SupplierChannel, Unit, VehiclePlate,
  VendaCoringaRow, VendasPorVendedorRow, VpsSerieRow,
  BaseDaSimulacao, CenarioFinanceiro, CenarioMes,
  ContaDoLote, LoteDeDocumentos, ResultadoDoCancelamento,
} from '@core/types';
import {
  COLUNAS_DA_FAMILIA, COLUNAS_DO_MODELO_BASE, familiasDe, palavraParaBusca, podePerguntarAFipe,
  type ModeloBase, type RespostaDaFipe,
} from './carro';
import { COLUNAS_DA_LISTA, type ContaParaLista } from './contasPagar';
import { nomeDaPecaEncomendada } from './encomenda';
import { COLUNAS_DO_ACERTO, type AcertoDoFiado } from './fiado';
import { hojeISO } from './formato';
// 094 — import à parte, e não junto do COLUNAS_DA_LISTA acima: aquela linha é
// vizinha de outro PR aberto, e mexer nela travaria o merge dos dois.
import { COLUNAS_DA_FOLHA, type LinhaDaFolhaNasContas } from './contasPagar';
import { urlDaFotoItem, urlDaImagem } from './imagem';
import { lerPlanta, type CodigoZona } from './loja3d';
import { areasDaPlanta, marcaDaCalota, type AreaDaMarca, type MarcaDaCalota } from './marcasDasCalotas';
import { montarLugares, type LugaresDaPeca } from './ondeFica';
import type { TratamentoDaPlaca } from './placa';
import { supabase } from './supabase';
import { SKU_CORINGA } from './coringa';
import { localNaZona } from './zonas';
// 095 — linha própria, longe do bloco de imports de cima: aquele trecho é
// vizinho de outro PR aberto (o salário nas contas), e mexer nele travaria o
// merge dos dois.
import {
  COLUNAS_DA_COMPRA, inicioDoMesSeguinte, type CompraNaHora, type EnvioDaCompra,
} from './compraNaHora';

/** Erro do PostgREST vira exceção — o React Query cuida do resto. */
function ou<T>(resposta: { data: T | null; error: unknown }): T {
  if (resposta.error) throw resposta.error;
  return resposta.data as T;
}

/**
 * O TETO DE 1.000 LINHAS DO SERVIDOR, e por que ler em páginas.
 *
 * O PostgREST corta TODA leitura de tabela em 1.000 linhas
 * (PGRST_DB_MAX_ROWS, infra/docker-compose.yml) — e corta CALADO: devolve
 * mil e não diz que havia mais. Um `.limit(2000)` no cliente não levanta
 * esse teto; só esconde que ele existe.
 *
 * Quem soma no navegador passa então a somar um PEDAÇO. Medido em
 * 11/09/2026, com 833 vendas válidas no período: a tela de Vendas mostrava
 * R$ 52.184,78 em "Vendido" contra R$ 82.615,22 de verdade — R$ 30.430,44
 * a menos, e por isso a soma vendedor a vendedor (nenhum deles perto das
 * mil) não fechava com o total de "Todos". As "peças mais vendidas" já
 * perdiam 217 itens de 1.217 pelo mesmo motivo.
 *
 * `emPaginas` pede fatias de 1.000 com `range` até a fatia vir curta.
 * Reaproveitar o MESMO builder entre as fatias é seguro e proposital:
 * `range()` faz `searchParams.set` (sobrescreve offset/limit, não acumula)
 * e cada `await` dispara um fetch novo. O teto de páginas está aqui para
 * que um período absurdo falhe ALTO, com recado, em vez de devagar.
 */
const LINHAS_POR_PAGINA = 1000;
const MAXIMO_DE_PAGINAS = 10;
const TETO_DE_LINHAS = MAXIMO_DE_PAGINAS * LINHAS_POR_PAGINA;

/**
 * Exportada só para o teste de regressão (consultas.test.ts).
 *
 * `recadoDoTeto` é o que a tela mostra quando o teto estoura. O padrão manda
 * diminuir o intervalo de datas, que é o remédio de quase todo chamador (lê
 * um período). Quem lê a tabela INTEIRA, sem data no pedido, passa o seu:
 * em Contas a pagar a faixa de vencimento filtra no navegador, depois da
 * leitura, e mexer nela não mudaria nada (revisão do #273).
 */
export async function emPaginas<T>(
  pagina: (de: number, ate: number) => PromiseLike<{ data: T[] | null; error: unknown }>,
  recadoDoTeto?: string,
): Promise<T[]> {
  const tudo: T[] = [];
  for (let p = 0; p < MAXIMO_DE_PAGINAS; p += 1) {
    const de = p * LINHAS_POR_PAGINA;
    const fatia = ou(await pagina(de, de + LINHAS_POR_PAGINA - 1)) ?? [];
    tudo.push(...fatia);
    if (fatia.length < LINHAS_POR_PAGINA) return tudo;
  }
  throw new Error(recadoDoTeto ?? (
    'Período grande demais para somar de uma vez '
    + `(mais de ${TETO_DE_LINHAS} linhas). `
    + 'Diminua o intervalo de datas.'
  ));
}

// --- Busca (tela 2) ----------------------------------------------------------

/**
 * Busca universal, opcionalmente estreitada por característica (migração 017).
 *
 * `filtros` é um mapa attribute_id → valor com semântica E: cada par
 * acrescentado ESTREITA o resultado ("amortecedor Gol" + lado=direito).
 *
 * O piso de 2 letras vale só quando NÃO há filtro. Com filtro marcado a busca
 * sem texto é legítima e útil ("me mostre tudo que é lado direito"), e a RPC
 * trata esse caso — devolve o conjunto inteiro que casa, em ordem alfabética.
 */
/** A PEÇA DIVERSA do balcão (coringa 9999, migração 016), para o botão que a
 *  põe no carrinho num toque. Só o que o carrinho precisa — e o preço vem
 *  junto porque a linha nasce com o preço vigente, igual à que entra pela
 *  busca. Hoje o coringa não tem preço cadastrado (é cada peça por si), então
 *  a linha nasce em zero e o vendedor digita; se um dia tiver, o botão passa a
 *  respeitá-lo sozinho. */
export interface PecaCoringa {
  id: string;
  sku: string;
  descricao: string;
  preco_balcao: number | null;
  preco_promocional: number | null;
}

/** Nulo quando o coringa não está cadastrado — aí o botão não aparece, e o
 *  balcão continua com o caminho da busca. */
export async function pecaCoringa(): Promise<PecaCoringa | null> {
  return ou(await supabase
    .from('products')
    .select('id, sku, descricao, preco_balcao, preco_promocional')
    .eq('sku', SKU_CORINGA)
    .maybeSingle()) ?? null;
}

export async function buscarPecas(
  q: string,
  limite = 25,
  filtros?: FiltroCaracteristicas,
): Promise<BuscarPecasRow[]> {
  const termo = q.trim();
  const temFiltro = filtros != null && Object.keys(filtros).length > 0;
  if (termo.length < 2 && !temFiltro) return [];
  return ou(await supabase.rpc('buscar_pecas', {
    q: termo,
    limite,
    // null e {} se comportam igual na RPC; mandar null deixa o log mais limpo.
    p_filtros: temFiltro ? filtros : null,
  })) ?? [];
}

// --- Ficha da peça (tela 3) --------------------------------------------------

export interface PecaCompleta {
  produto: Product;
  estoque: Stock | null;
  codigos: ProductCode[];
  aplicacoes: ProductApplication[];
  /** Valores das características, por attribute_id — VÁRIOS por
   *  característica desde a migração 036 (uma linha por valor). */
  caracteristicas: CaracteristicasDaPeca;
}

/**
 * Colunas de `products` que o app pede explicitamente.
 *
 * POR QUE NÃO É `*` (auditoria 2026-08-04, achado 1.7): `preco_custo` e
 * `preco_ecommerce` foram REVOGADAS do papel `authenticated` na migração 014,
 * porque a RLS filtra linha e não coluna — a policy de leitura libera a linha
 * inteira a qualquer perfil ativo, e o custo do catálogo vazava para os cinco
 * funcionários. Com a coluna revogada, `select *` passa a dar erro de permissão
 * para TODO MUNDO, inclusive o dono; daí a lista explícita.
 *
 * Quem pode ver custo lê pela RPC `produto_financeiro()`, que checa o papel
 * dentro. Ao adicionar coluna nova em products, adicione aqui também.
 */
export const COLUNAS_PRODUTO =
  'id, sku, descricao, categoria, aplicacao, marca, codigo_fabrica, codigo_barras, ' +
  'codigo_barras_fabricante, ' +
  'unidade, ativo, codigo_oem, posicao, ncm, origem_mercadoria, peso_kg, altura_cm, ' +
  'largura_cm, comprimento_cm, preco_balcao, preco_promocional, observacoes, ' +
  'cadastro_completo, grupo_similar, tipo, detalhes, conferencia, created_at, updated_at';

/** Custo e margem de uma peça. Devolve null quando o papel não pode ver. */
export async function produtoFinanceiro(id: string): Promise<ProdutoFinanceiroRow | null> {
  const { data, error } = await supabase.rpc('produto_financeiro', { p_product_id: id });
  // Erro 42501 é o "não pode ver" esperado — a tela some com o bloco em vez de
  // mostrar erro para quem simplesmente não tem acesso ao financeiro.
  if (error) return null;
  return Array.isArray(data) ? data[0] ?? null : data ?? null;
}

export async function obterPeca(id: string): Promise<PecaCompleta> {
  const dados = ou(
    await supabase
      .from('products')
      .select(`${COLUNAS_PRODUTO}, stock(*), product_codes(*), product_applications(*), `
        + 'product_attribute_values(attribute_id, valor)')
      .eq('id', id)
      .single(),
  ) as Product & {
    stock: Stock | Stock[] | null;
    product_codes: ProductCode[] | null;
    product_applications: ProductApplication[] | null;
    product_attribute_values: Array<Pick<ProductAttributeValue, 'attribute_id' | 'valor'>> | null;
  };

  const {
    stock, product_codes, product_applications, product_attribute_values, ...produto
  } = dados;
  return {
    produto: produto as Product,
    estoque: Array.isArray(stock) ? stock[0] ?? null : stock,
    codigos: product_codes ?? [],
    aplicacoes: (product_applications ?? []).sort((a, b) =>
      a.montadora.localeCompare(b.montadora) || a.modelo.localeCompare(b.modelo)),
    caracteristicas: (product_attribute_values ?? []).reduce<CaracteristicasDaPeca>(
      (mapa, v) => {
        (mapa[v.attribute_id] ??= []).push(v.valor);
        return mapa;
      }, {}),
  };
}

export async function movimentosDaPeca(id: string, limite = 30): Promise<StockMovement[]> {
  return ou(
    await supabase
      .from('stock_movements')
      .select('*')
      .eq('product_id', id)
      .order('created_at', { ascending: false })
      .limit(limite),
  ) ?? [];
}

// --- Cadastro (tela 4) -------------------------------------------------------

/**
 * A peça com custo e preço de e-commerce embutidos, para a tela de EDIÇÃO.
 *
 * Existe como uma função só, e não como duas queries na tela, por causa da
 * armadilha já documentada no CLAUDE.md: um useEffect que sincroniza formulário
 * com o servidor apaga o que o usuário digitou quando uma segunda consulta,
 * mais lenta, responde depois. Com uma fonte assíncrona única, não há corrida.
 *
 * Quem não pode ver custo recebe os campos vazios — e a aba de preços já esconde
 * o campo por papel.
 */
export async function obterPecaParaEdicao(id: string): Promise<PecaCompleta> {
  const [peca, financeiro] = await Promise.all([obterPeca(id), produtoFinanceiro(id)]);
  return {
    ...peca,
    produto: {
      ...peca.produto,
      preco_custo: financeiro?.preco_custo ?? null,
      preco_ecommerce: financeiro?.preco_ecommerce ?? null,
      margem_alvo: financeiro?.margem_alvo ?? null,
    },
  };
}

export interface NovaPeca {
  produto: Partial<Product> & { sku: string; descricao: string };
  estoque?: Partial<Stock>;
  codigos?: Array<Pick<ProductCode, 'tipo' | 'valor'>>;
  aplicacoes?: Array<Omit<ProductApplication, 'id' | 'product_id' | 'created_at' | 'vehicle_model_id'>>;
  /**
   * Características (migrações 017/036), por attribute_id — cada uma com a
   * LISTA de valores da peça. Lista vazia significa "não preenchida" e APAGA
   * o que houver — é como o formulário limpa um campo.
   */
  caracteristicas?: CaracteristicasDaPeca;
}

export async function criarPeca(entrada: NovaPeca): Promise<Product> {
  const produto = ou(
    // `.select()` sem argumento devolveria `*` — e `*` inclui as colunas de
    // custo revogadas. Ver COLUNAS_PRODUTO.
    await supabase.from('products').insert(entrada.produto).select(COLUNAS_PRODUTO).single(),
  ) as Product;

  // O restante é complementar: se falhar, a peça já existe e pode ser editada.
  // Só a criação do produto precisa ser tudo-ou-nada, e essa é atômica sozinha.
  await salvarComplementos(produto.id, entrada);
  return produto;
}

export async function atualizarPeca(id: string, entrada: NovaPeca): Promise<Product> {
  const produto = ou(
    await supabase.from('products').update(entrada.produto).eq('id', id)
      .select(COLUNAS_PRODUTO).single(),
  ) as Product;

  // Erro destes deletes TEM que subir: engoli-lo foi metade do bug que duplicava
  // aplicações a cada salvamento (a outra metade era a RLS barrando o gerente em
  // silêncio — rls.sql §1.2). Zero linhas apagadas continua não sendo erro; o
  // que a policy garante agora é que quem pode editar também pode apagar.
  if (entrada.codigos) {
    const { error } = await supabase.from('product_codes').delete().eq('product_id', id);
    if (error) throw error;
  }
  if (entrada.aplicacoes) {
    const { error } = await supabase.from('product_applications').delete().eq('product_id', id);
    if (error) throw error;
  }

  await salvarComplementos(id, entrada);
  return produto;
}

async function salvarComplementos(productId: string, entrada: NovaPeca) {
  const codigos = (entrada.codigos ?? []).filter((c) => c.valor.trim());
  if (codigos.length) {
    const { error } = await supabase
      .from('product_codes')
      .upsert(
        codigos.map((c) => ({ product_id: productId, tipo: c.tipo, valor: c.valor.trim() })),
        { onConflict: 'product_id,tipo,valor', ignoreDuplicates: true },
      );
    if (error) throw error;
  }

  const aplicacoes = (entrada.aplicacoes ?? []).filter((a) => a.montadora.trim() && a.modelo.trim());
  if (aplicacoes.length) {
    const { error } = await supabase
      .from('product_applications')
      .insert(aplicacoes.map((a) => ({ ...a, product_id: productId })));
    if (error) throw error;
  }

  if (entrada.estoque && Object.keys(entrada.estoque).length) {
    const { error } = await supabase
      .from('stock')
      .upsert({ product_id: productId, ...entrada.estoque }, { onConflict: 'product_id' });
    if (error) throw error;
  }

  await salvarCaracteristicas(productId, entrada.caracteristicas);
}

/**
 * Grava as características da peça.
 *
 * Campo esvaziado no formulário vira DELETE, não linha com string vazia: o
 * banco recusa valor em branco (check `valor_nao_vazio`), e uma linha vazia
 * ainda apareceria como "preenchida" no filtro da busca. Por isso os dois
 * caminhos — apagar o que saiu, gravar o que ficou.
 */
async function salvarCaracteristicas(productId: string, valores?: CaracteristicasDaPeca) {
  if (!valores) return;
  const ids = Object.keys(valores);
  if (!ids.length) return;

  // Multivalor (036): substituição inteira das características LISTADAS —
  // apaga as linhas delas e regrava as escolhidas, o padrão das aplicações.
  // O upsert de antes tinha onConflict na PK antiga (product_id,attribute_id),
  // que deixou de existir. Erro do delete SOBE (lição do rls.sql §1.2).
  const { error } = await supabase
    .from('product_attribute_values')
    .delete()
    .eq('product_id', productId)
    .in('attribute_id', ids);
  if (error) throw error;

  const linhas = ids.flatMap((attribute_id) =>
    [...new Set((valores[attribute_id] ?? []).map((v) => v.trim()).filter(Boolean))]
      .map((valor) => ({ product_id: productId, attribute_id, valor })));
  if (linhas.length) {
    const { error: erroInsert } = await supabase
      .from('product_attribute_values')
      .insert(linhas);
    if (erroInsert) throw erroInsert;
  }
}

/**
 * Os valores EM USO de cada característica, para o filtro da busca oferecer
 * também as de texto/número (que não têm `opcoes`) — foi assim que o "Motor"
 * ficou invisível no filtro até 13/08/2026. Tabela pequena; distinto no app.
 */
export async function valoresEmUso(): Promise<Record<string, string[]>> {
  const linhas = ou(
    await supabase.from('product_attribute_values').select('attribute_id, valor'),
  ) as Array<{ attribute_id: string; valor: string }> | null ?? [];
  const mapa: Record<string, string[]> = {};
  for (const l of linhas) (mapa[l.attribute_id] ??= []).push(l.valor);
  for (const id of Object.keys(mapa)) mapa[id] = [...new Set(mapa[id])].sort();
  return mapa;
}

// --- Características de peça (migração 017) ----------------------------------

/**
 * As características cadastradas. `apenasAtivas` é o que o formulário e o filtro
 * da busca usam; a tela de gestão pede todas, para poder reativar.
 */
export async function listarCaracteristicas(apenasAtivas = true): Promise<ProductAttribute[]> {
  let consulta = supabase.from('product_attributes').select('*');
  if (apenasAtivas) consulta = consulta.eq('ativo', true);
  return ou(await consulta.order('ordem').order('nome')) ?? [];
}

export type NovaCaracteristica = Pick<ProductAttribute, 'nome' | 'tipo'>
  & Partial<Pick<ProductAttribute, 'opcoes' | 'ordem' | 'ativo'>>;

export async function criarCaracteristica(entrada: NovaCaracteristica): Promise<ProductAttribute> {
  return ou(
    await supabase.from('product_attributes').insert(normalizarCaracteristica(entrada))
      .select('*').single(),
  ) as ProductAttribute;
}

export async function atualizarCaracteristica(
  id: string,
  entrada: Partial<NovaCaracteristica>,
): Promise<ProductAttribute> {
  return ou(
    await supabase.from('product_attributes').update(normalizarCaracteristica(entrada))
      .eq('id', id).select('*').single(),
  ) as ProductAttribute;
}

/**
 * Alinha o payload com os checks do banco antes de mandar: `opcoes` só existe
 * em tipo 'lista' e, nela, precisa ter pelo menos uma. Sem isto o usuário
 * receberia erro de constraint em vez de um formulário que já não deixa errar.
 */
function normalizarCaracteristica<T extends Partial<NovaCaracteristica>>(entrada: T) {
  const limpo = { ...entrada };
  if (limpo.nome !== undefined) limpo.nome = limpo.nome.trim();
  if (limpo.tipo !== undefined) {
    limpo.opcoes = limpo.tipo === 'lista'
      ? [...new Set((limpo.opcoes ?? []).map((o) => o.trim()).filter(Boolean))]
      : [];
  }
  return limpo;
}

// --- Estoque completo (tela do dono) ----------------------------------------

export interface LinhaEstoque {
  product_id: string;
  sku: string;
  descricao: string;
  categoria: string | null;
  ativo: boolean;
  quantidade: number;
  estoque_min: number;
  local: string | null;
  pendente_registro: number;
}

/**
 * O estoque inteiro, para a tela de conferência do dono.
 *
 * Sai de `stock` e não de `products` porque a tela é sobre o que ESTÁ na
 * prateleira: peça sem linha de estoque nunca foi contada nem recebida, e
 * apareceria como um zero que se confunde com "acabou". O teto existe pelo
 * mesmo motivo do teto de buscar_pecas — evita puxar o catálogo inteiro de uma
 * vez no celular do balcão.
 */
export async function listarEstoque(limite = 500): Promise<LinhaEstoque[]> {
  // Sem `.order()` por coluna do embed: no PostgREST isso ordena as linhas
  // FILHAS, não as de cima — não é o que a tela quer, e ainda quebra a
  // inferência de tipo do supabase-js. A ordenação é da tela, que já oferece
  // quatro critérios ao dono.
  const linhas = ou(
    await supabase
      .from('stock')
      .select('product_id, quantidade, estoque_min, local, pendente_registro, '
        + 'products!inner(sku, descricao, categoria, ativo)')
      .limit(limite),
  ) as unknown as Array<{
    product_id: string; quantidade: number; estoque_min: number;
    local: string | null; pendente_registro: number;
    products: Pick<Product, 'sku' | 'descricao' | 'categoria' | 'ativo'>
      | Array<Pick<Product, 'sku' | 'descricao' | 'categoria' | 'ativo'>>;
  }> | null;

  return (linhas ?? []).map((l) => {
    // O PostgREST devolve o embed como objeto ou array conforme a cardinalidade
    // que ele infere; normalizar aqui evita a tela ter que saber disso.
    const p = Array.isArray(l.products) ? l.products[0] : l.products;
    return {
      product_id: l.product_id,
      sku: p?.sku ?? '',
      descricao: p?.descricao ?? '',
      categoria: p?.categoria ?? null,
      ativo: p?.ativo ?? true,
      quantidade: Number(l.quantidade),
      estoque_min: Number(l.estoque_min),
      local: l.local,
      pendente_registro: Number(l.pendente_registro ?? 0),
    };
  });
}

// --- Promoções (migração 018) ------------------------------------------------

export interface LinhaPromocao {
  product_id: string;
  sku: string;
  descricao: string;
  marca: string | null;
  categoria: string | null;
  preco_balcao: number | null;
  preco_promocional: number | null;
  quantidade: number;
  local: string | null;
}

/**
 * As peças em promoção, para a aba Promoções (pauta de upsell do balcão).
 *
 * Vem direto de products (não de RPC): o preço promocional é dado público do
 * balcão e o check `products_promocao_valida` garante que toda linha com a
 * coluna preenchida é uma promoção de verdade — não há o que re-filtrar.
 */
export async function listarPromocoes(limite = 200): Promise<LinhaPromocao[]> {
  const linhas = ou(
    await supabase
      .from('products')
      .select('id, sku, descricao, marca, categoria, preco_balcao, preco_promocional, '
        + 'stock(quantidade, local)')
      .eq('ativo', true)
      .not('preco_promocional', 'is', null)
      .order('descricao')
      .limit(limite),
  ) as unknown as Array<{
    id: string; sku: string; descricao: string; marca: string | null;
    categoria: string | null; preco_balcao: number | null; preco_promocional: number | null;
    stock: { quantidade: number; local: string | null }
      | Array<{ quantidade: number; local: string | null }> | null;
  }> | null;

  return (linhas ?? []).map((l) => {
    // Embed vem como objeto ou array conforme a cardinalidade que o PostgREST
    // infere — mesma normalização de listarEstoque.
    const s = Array.isArray(l.stock) ? l.stock[0] : l.stock;
    return {
      product_id: l.id,
      sku: l.sku,
      descricao: l.descricao,
      marca: l.marca,
      categoria: l.categoria,
      preco_balcao: l.preco_balcao,
      preco_promocional: l.preco_promocional,
      quantidade: Number(s?.quantidade ?? 0),
      local: s?.local ?? null,
    };
  });
}

// --- Catálogo de veículos (universo FIPE — decisão D15) ----------------------

/** Montadoras do vehicle_catalog, para o autocomplete da aba Aplicação.
 *  É o que faz "VW", "Volks" e "Volkswagen" virarem UMA montadora no cadastro. */
export async function montadorasCatalogo(): Promise<string[]> {
  return ou(await supabase.rpc('montadoras_catalogo')) ?? [];
}

/** Modelos de uma montadora, no mesmo autocomplete. */
export async function modelosCatalogo(montadora: string): Promise<string[]> {
  const m = montadora.trim();
  if (!m) return [];
  return ou(await supabase.rpc('modelos_catalogo', { p_montadora: m })) ?? [];
}

// --- O carro da venda num campo só (08/10/2026) ------------------------------
//
// lib/carro.ts lê o que o vendedor digita ("gol 2010 1.0"). Estas duas
// leituras são a base dele — diretas nas tabelas, sem função nova: a policy
// das duas é `papel_atual() is not null` (rls.sql §11 e §26), então equipe lê
// e o parceiro B2B (papel nulo) não lê nada.

/** Os modelos-base (vehicle_models, 092): 45 linhas, que mudam quando o dono
 *  cadastra um modelo — a tela guarda em cache sem prazo. */
export async function modelosBase(): Promise<ModeloBase[]> {
  return ou(await (supabase.from('vehicle_models')
    .select(COLUNAS_DO_MODELO_BASE.join(', ')) as unknown as PromiseLike<{
      data: ModeloBase[] | null; error: unknown;
    }>)) ?? [];
}

/** Linhas por palavra perguntada à FIPE. Resposta que bate no limite pode
 *  estar cortada: as famílias dela valem, mas a palavra não conta como
 *  respondida (lib/carro.ts, BaseDeCarros.fipe). */
export const LIMITE_DA_FAMILIA = 200;

/**
 * As FAMÍLIAS da FIPE que começam pelas palavras ("kombi" → Kombi da VW;
 * "147" → Fiat e Alfa Romeo). É o que lê o carro que vehicle_models ainda não
 * tem. As palavras vêm de carro.palavrasParaFipe — até 3, numa ida só, porque
 * a leitura precisa saber de TODAS as que podem ser o modelo antes do primeiro
 * modelo conhecido ("pampa pick up": a Pampa, antes de o "up" ganhar). Cada
 * uma vai limpa de `%` e `_` (o vendedor não escreve padrão de busca sem
 * querer) e só se pode ir (podePerguntarAFipe: 3 letras, ou número de 2).
 * Devolve as palavras respondidas POR INTEIRO: a tela só passa por cima de
 * uma palavra desconhecida que a FIPE disse não ser família.
 */
export async function familiasDoCatalogo(palavras: readonly string[]): Promise<RespostaDaFipe> {
  const buscas = [...new Set(palavras.map(palavraParaBusca))].filter(podePerguntarAFipe).slice(0, 3);
  const respostas = await Promise.all(buscas.map(async (p) => {
    const linhas = ou(await (supabase.from('vehicle_catalog')
      .select(COLUNAS_DA_FAMILIA.join(', '))
      .ilike('modelo', `${p}%`)
      .order('modelo')
      .limit(LIMITE_DA_FAMILIA) as unknown as PromiseLike<{
        data: Array<{ montadora: string; modelo: string }> | null; error: unknown;
      }>)) ?? [];
    return { p, linhas };
  }));
  // A mesma linha em duas respostas ("gol" e "golf") conta uma vez: é o
  // número de versões que desempata a montadora.
  const linhas = new Map<string, { montadora: string; modelo: string }>();
  for (const r of respostas) for (const l of r.linhas) linhas.set(`${l.montadora}|${l.modelo}`, l);
  return {
    respondidas: respostas.filter((r) => r.linhas.length < LIMITE_DA_FAMILIA).map((r) => r.p),
    familias: familiasDe([...linhas.values()]),
  };
}

// --- Exclusão de peça --------------------------------------------------------

/**
 * Exclui a peça do catálogo. Só dono, e só peça SEM histórico.
 *
 * Vai pela RPC e não por `.delete()` porque o erro de FK cru viraria "Registro
 * relacionado não encontrado" no mensagemDeErro() — a mensagem errada. A RPC
 * checa antes e devolve o recado que diz o que fazer. Ver core/sql/functions/
 * peca_admin.sql.
 */
export async function excluirPeca(id: string): Promise<void> {
  const { error } = await supabase.rpc('excluir_peca', { p_product_id: id });
  if (error) throw error;
}

/**
 * Próximo SKU da categoria, no padrão CAT-0001 (decisão D6).
 * Lê o maior sufixo existente do prefixo — sem sequence no banco, porque a
 * numeração é por categoria e muda conforme o gerente cria categorias novas.
 */
// --- Similares (migração 027) ------------------------------------------------

export interface NovaSimilar {
  marca: string;
  /** Código da variante: vai em codigo_fabrica e nos fornecedores escolhidos. */
  codigo: string | null;
  preco_balcao: number | null;
}

export interface SimilarDaPeca {
  id: string;
  sku: string;
  marca: string | null;
  codigo_fabrica: string | null;
  preco_balcao: number | null;
  preco_promocional: number | null;
  quantidade: number;
  local: string | null;
}

/**
 * Cria as variantes de marca em lote, no mesmo grupo_similar da base. É RPC
 * (transação no banco): da tela, uma falha no meio deixaria metade das
 * variantes criadas e o retry duplicaria a outra metade.
 */
/**
 * A aplicação é do GRUPO, não da marca (13/08/2026): replica as aplicações
 * desta peça nas outras marcas do grupo_similar. Peça sem grupo, ou sem
 * aplicação nenhuma (proteção do "abri só para acertar o preço"): no-op.
 * Devolve quantas irmãs foram sincronizadas.
 */
export async function sincronizarAplicacoesDoGrupo(productId: string): Promise<number> {
  return ou(await supabase.rpc('sincronizar_aplicacoes_do_grupo', { p_product_id: productId }));
}

export async function criarSimilares(
  baseId: string,
  similares: NovaSimilar[],
  supplierIds: string[],
): Promise<string[]> {
  return ou(
    await supabase.rpc('criar_similares', {
      p_base_id: baseId,
      p_similares: similares.map((s) => ({
        marca: s.marca,
        codigo: s.codigo,
        preco_balcao: s.preco_balcao,
      })),
      p_supplier_ids: supplierIds.length ? supplierIds : null,
    }),
  ) ?? [];
}

export interface SimilarDeResultado {
  id: string;
  grupo_similar: string;
  marca: string | null;
  sku: string;
  quantidade: number;
  preco_balcao: number | null;
  preco_promocional: number | null;
}

/**
 * Similares de TODOS os resultados visíveis da busca, numa consulta só — o
 * mesmo desenho de fornecedoresDePecas/carrinhosDasPecas: a busca redigita a
 * cada 250 ms, e uma consulta por linha derrubaria a fluidez.
 */
export async function similaresDosResultados(
  grupos: string[],
): Promise<SimilarDeResultado[]> {
  if (!grupos.length) return [];
  const linhas = ou(
    await supabase
      .from('products')
      .select('id, grupo_similar, marca, sku, preco_balcao, preco_promocional, stock(quantidade)')
      .in('grupo_similar', grupos)
      .eq('ativo', true)
      .order('marca'),
  ) as Array<{
    id: string; grupo_similar: string; marca: string | null; sku: string;
    preco_balcao: number | null; preco_promocional: number | null;
    stock: { quantidade: number } | Array<{ quantidade: number }> | null;
  }> | null;

  return (linhas ?? []).map(({ stock, ...p }) => {
    const s = Array.isArray(stock) ? stock[0] ?? null : stock;
    return { ...p, quantidade: s?.quantidade ?? 0 };
  });
}

/** As outras peças do grupo — o que a ficha mostra quando uma marca falta. */
export async function similaresDaPeca(
  grupo: string,
  excetoId: string,
): Promise<SimilarDaPeca[]> {
  const linhas = ou(
    await supabase
      .from('products')
      .select('id, sku, marca, codigo_fabrica, preco_balcao, preco_promocional, stock(quantidade, local)')
      .eq('grupo_similar', grupo)
      .neq('id', excetoId)
      .eq('ativo', true)
      .order('marca'),
  ) as Array<{
    id: string; sku: string; marca: string | null; codigo_fabrica: string | null;
    preco_balcao: number | null; preco_promocional: number | null;
    stock: { quantidade: number; local: string | null }
      | Array<{ quantidade: number; local: string | null }> | null;
  }> | null;

  return (linhas ?? []).map(({ stock, ...p }) => {
    const s = Array.isArray(stock) ? stock[0] ?? null : stock;
    return { ...p, quantidade: s?.quantidade ?? 0, local: s?.local ?? null };
  });
}

export async function proximoSku(prefixo: string): Promise<string> {
  const base = prefixo.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 6) || 'PEC';
  const { data } = await supabase
    .from('products')
    .select('sku')
    .like('sku', `${base}-%`)
    .order('sku', { ascending: false })
    .limit(1);

  const ultimo = (data as Array<{ sku: string }> | null)?.[0]?.sku;
  const n = ultimo ? Number(ultimo.split('-').pop()) : 0;
  const proximo = Number.isFinite(n) ? n + 1 : 1;
  return `${base}-${String(proximo).padStart(4, '0')}`;
}

// --- A fila de conferência (093, PR 2 — 08/10/2026) --------------------------
// Quatro RPCs de core/sql/functions/conferencia.sql, com os nomes p_* exatos:
// chamada de RPC é TEXTO, e um nome errado só aparece em produção (404
// PGRST202). Quem nega é o exige_papel no corpo de cada uma.

/** Os campos do cadastro do balcão — os mesmos no cadastrar e no liberar. */
export interface DadosDoCadastroDoBalcao {
  descricao: string;
  marca: string | null;
  tipo: string | null;
  codigo: string | null;
  preco_balcao: number | null;
  local: string | null;
  quantidade: number | null;
}

/** Cadastra a peça pelo balcão. Do vendedor nasce PENDENTE (não vende até a
 *  gerência liberar); de gerente/dono nasce liberada. Devolve o id. */
export async function cadastrarPecaPeloBalcao(d: DadosDoCadastroDoBalcao): Promise<string> {
  return ou(await supabase.rpc('cadastrar_peca_pelo_balcao', {
    p_descricao: d.descricao,
    p_marca: d.marca,
    p_tipo: d.tipo,
    p_codigo: d.codigo,
    p_preco_balcao: d.preco_balcao,
    p_local: d.local,
    p_quantidade: d.quantidade,
  })) as string;
}

/** A fila por estado. A gerência recebe todas; o colaborador, as dele. */
export async function filaDeConferencia(estado: Conferencia): Promise<LinhaDaConferencia[]> {
  return ou(await supabase.rpc('fila_de_conferencia', { p_estado: estado })) ?? [];
}

/** Libera a peça (pendente ou recusada) com os valores do cartão — com ou sem
 *  correção. A rasura é calculada no banco. */
export async function habilitarPeca(productId: string, d: DadosDoCadastroDoBalcao): Promise<void> {
  const { error } = await supabase.rpc('habilitar_peca', {
    p_product_id: productId,
    p_descricao: d.descricao,
    p_marca: d.marca,
    p_tipo: d.tipo,
    p_codigo: d.codigo,
    p_preco_balcao: d.preco_balcao,
    p_local: d.local,
    p_quantidade: d.quantidade,
  });
  if (error) throw error;
}

/** Recusa a peça pendente. O motivo é obrigatório: é o que quem cadastrou lê. */
export async function recusarPeca(productId: string, motivo: string): Promise<void> {
  const { error } = await supabase.rpc('recusar_peca', { p_product_id: productId, p_motivo: motivo });
  if (error) throw error;
}

/** O ÚLTIMO movimento de compra ou de contagem da peça — o que a Entrada lê
 *  para avisar que o saldo ainda é a contagem do balcão, sem entrada depois
 *  (contagemDoBalcaoSemEntrada). Venda e perda não entram: elas não dizem se a
 *  caixa contada já teve nota. Só gerente e dono abrem a Entrada, e a RLS de
 *  stock_movements lhes dá todas as linhas. */
export async function ultimaCompraOuContagem(productId: string): Promise<StockMovement | null> {
  const linhas = ou(
    await supabase
      .from('stock_movements')
      .select('*')
      .eq('product_id', productId)
      .in('reason', ['compra', 'inventario', 'ajuste'] satisfies StockMoveReason[])
      .order('created_at', { ascending: false })
      .limit(1),
  ) ?? [];
  return linhas[0] ?? null;
}

// --- Movimentação (telas 5 e 6) ---------------------------------------------

export interface ItemVenda {
  product_id: string;
  quantidade: number;
  preco_unit: number;
  /** PEÇA DIVERSA (041): o que saiu de verdade nesta linha. */
  descricao?: string;
  /** PEÇA DIVERSA (048): o custo digitado — vira o custo_unit do CMV.
   *  Em peça cadastrada o banco ignora (o custo é o do catálogo). */
  custo?: number;
  /** PEÇA DIVERSA (071): o código do fabricante, de uso interno. O banco só o
   *  guarda na linha do coringa, e ele não sai em documento de cliente. */
  codigo?: string;
  /** 073: o carro DESTA linha, quando é outro que não o da venda. Omitir quer
   *  dizer "o carro da venda" — não repita o mesmo carro em toda linha.
   *  Diferente dos três acima, NÃO é exclusivo do coringa. Os dois vêm juntos
   *  ou nenhum vem: o banco recusa meio carro. */
  veiculo_montadora?: string;
  veiculo_modelo?: string;
}

export async function registrarVenda(p: {
  itens: ItemVenda[];
  forma: PaymentMethod;
  desconto?: number;
  /** A taxa de entrega a domicílio (064): soma no que o cliente paga (caixa e
   *  dívida do fiado), nunca no total de peças — que é a base da comissão, da
   *  meta e da margem. */
  entregaTaxa?: number;
  cliente?: string | null;
  observacao?: string | null;
  /** "A ver" a resgatar — a RPC valida saldo/validade e abate do total. */
  aVerId?: string | null;
  /**
   * Chave de idempotência da TENTATIVA de venda (não da venda). A tela gera uma
   * por carrinho; se o vendedor tocar duas vezes em "confirmar", ou o navegador
   * reenviar por rede instável, a segunda chamada devolve a mesma venda em vez
   * de criar outra — que baixaria o estoque de novo e lançaria o caixa de novo
   * (auditoria 2026-08-04, achado 2.26). Quem resolve corrida é o índice único
   * no banco, não o React.
   */
  chave?: string | null;
  /** Cliente CADASTRADO (migração 023). O nome congelado da venda continua indo
   *  em `cliente`; sem ele, a RPC copia o nome do cadastro no ato. */
  customerId?: string | null;
  /** Encerrar o vale abonando a sobra (migração 034): o cliente abre mão do
   *  que restaria no vale, e a sobra fica registrada como uso com abono=true.
   *  A RPC recusa sem vale em uso, e aplica o teto do vendedor. */
  abonarSobra?: boolean;
  /** 073 — o carro da venda. Desde 08/10/2026 o vendedor DIGITA ("gol 2010
   *  1.0") e lib/carro.ts lê: a montadora sai sempre na grafia da FIPE ("VW -
   *  VolksWagen") — é o que impede "VW", "Volks" e "Volkswagen" de virarem
   *  três carros no relatório — e o modelo leva o nome-base na frente ("Gol
   *  1.0", "Uno Mille"), que modelo_base_de() (092) agrupa. Os dois andam
   *  juntos. `veiculoId` amarra a venda ao carro CADASTRADO do cliente (051). */
  veiculoMontadora?: string | null;
  veiculoModelo?: string | null;
  veiculoAno?: number | null;
  veiculoId?: string | null;
  /**
   * As PARTES do pagamento dividido (075): "R$ 100 em dinheiro e R$ 50 no
   * crédito". Só com `forma: 'misto'`, e 'misto' só com elas — duas ou mais,
   * em formas de dinheiro, somando o que a venda cobra (a RPC confere e
   * recusa o resto). Cada parte vira uma entrada no caixa com a forma real.
   */
  pagamentos?: ReadonlyArray<{ forma: PaymentMethod; valor: number }> | null;
  /**
   * 087 - a BANDEIRA e EM QUANTAS VEZES, no cartao. Nao mudam o que o cliente
   * paga: parcelar nao encarece a venda, a taxa sai da loja. Servem para a
   * loja saber quanto uma venda no credito rende de fato. A RPC busca a taxa
   * vigente da faixa e CONGELA na venda - mandar a taxa daqui deixaria o
   * cliente escolher quanto a maquininha cobrou.
   */
  cartaoBandeira?: string | null;
  cartaoParcelas?: number | null;
}): Promise<string> {
  return ou(
    await supabase.rpc('registrar_venda', {
      p_itens: p.itens,
      p_forma: p.forma,
      p_desconto: p.desconto ?? 0,
      p_cliente: p.cliente ?? null,
      p_observacao: p.observacao ?? null,
      p_canal: 'balcao',
      p_a_ver_id: p.aVerId ?? null,
      p_idempotency_key: p.chave ?? null,
      p_customer_id: p.customerId ?? null,
      p_abonar_sobra: p.abonarSobra ?? false,
      p_entrega_taxa: p.entregaTaxa ?? 0,
      p_veiculo_montadora: p.veiculoMontadora ?? null,
      p_veiculo_modelo: p.veiculoModelo ?? null,
      p_veiculo_ano: p.veiculoAno ?? null,
      p_customer_vehicle_id: p.veiculoId ?? null,
      p_pagamentos: p.pagamentos ?? null,
      p_cartao_bandeira: p.cartaoBandeira ?? null,
      p_cartao_parcelas: p.cartaoParcelas ?? null,
    }),
  );
}

/**
 * As taxas do cartao em vigor HOJE (087), para a telinha do credito montar a
 * lista de parcelas.
 *
 * So as vigentes: a tabela guarda o historico das taxas (vigencia fechada
 * quando a adquirente renegocia), e a tela que oferecesse uma taxa vencida
 * mostraria um numero que nao vai acontecer. Quem congela a taxa na venda e a
 * RPC, no servidor - esta consulta e so para EXIBIR.
 */
export async function taxasDeCartao(hoje: string): Promise<TaxaDeCartao[]> {
  return ou(
    await supabase.from('taxas_de_cartao')
      .select('*')
      .lte('vigente_desde', hoje)
      .or(`vigente_ate.is.null,vigente_ate.gte.${hoje}`)
      .order('bandeira')
      .order('parcelas_de'),
  );
}

/**
 * A linha única de limites_operacao (id = 1). A tela usa para o vendedor saber
 * o teto ANTES de prometer ao cliente — quem faz valer é o banco.
 */
export async function limitesDaOperacao(): Promise<LimitesOperacao> {
  return ou(await supabase.from('limites_operacao').select('*').eq('id', 1).single());
}

/**
 * Os tetos que valem para UMA pessoa (069): o ajuste da conta dela, senão o do
 * papel. Sem argumento, os seus.
 *
 * É a MESMA função que registrar_venda, emitir_orcamento e emitir_a_ver
 * chamam para recusar. A tela usar outra conta — repetir o coalesce aqui em
 * TypeScript — é como ela passa a prometer 20% enquanto o banco corta em 10,
 * e o vendedor descobre com o cliente na frente.
 */
export async function limitesEfetivos(profileId?: string): Promise<LimitesEfetivos> {
  const linhas = ou(
    await supabase.rpc('limites_efetivos', { p_profile: profileId ?? null }),
  ) as LimitesEfetivos[] | null;
  if (!linhas?.[0]) throw new Error('Sem limites — o banco não devolveu nada.');
  return linhas[0];
}

/** Os ajustes de teto de toda a equipe. Só o dono lê (RLS). */
export async function limitesDaEquipe(): Promise<LimitesPorPessoa[]> {
  return ou(await supabase.from('limites_por_pessoa').select('*')) ?? [];
}

/**
 * O dono ajusta os tetos de UMA conta (069).
 *
 * Manda a linha inteira: `null` num campo devolve aquele teto ao papel, e os
 * quatro nulos apagam o ajuste. É por isso que o formulário mostra os quatro
 * juntos — sem isso não haveria como REMOVER uma folga já concedida.
 */
export async function definirLimitesDaPessoa(p: {
  profileId: string;
  descontoMaxPct: number | null;
  precoMinPctDoBalcao: number | null;
  aVerMax: number | null;
  abonoSobraMax: number | null;
  observacao: string | null;
}): Promise<void> {
  const { error } = await supabase.rpc('definir_limites_da_pessoa', {
    p_profile: p.profileId,
    p_desconto_max_pct: p.descontoMaxPct,
    p_preco_min_pct_do_balcao: p.precoMinPctDoBalcao,
    p_a_ver_max: p.aVerMax,
    p_abono_sobra_max: p.abonoSobraMax,
    p_observacao: p.observacao,
  });
  if (error) throw error;
}

/**
 * A régua do PAPEL — o que vale para quem não tem ajuste próprio. Update
 * direto: a RLS de limites_operacao só deixa o dono escrever.
 */
export async function salvarLimitesDaLoja(p: {
  descontoMaxPctVendedor: number;
  descontoMaxPctGerente: number;
  precoMinPctDoBalcao: number;
  aVerMaxGerente: number;
  abonoSobraMaxVendedor: number;
}): Promise<void> {
  const { error } = await supabase.from('limites_operacao')
    .update({
      desconto_max_pct_vendedor: p.descontoMaxPctVendedor,
      desconto_max_pct_gerente: p.descontoMaxPctGerente,
      preco_min_pct_do_balcao: p.precoMinPctDoBalcao,
      a_ver_max_gerente: p.aVerMaxGerente,
      abono_sobra_max_vendedor: p.abonoSobraMaxVendedor,
    })
    .eq('id', 1);
  if (error) throw error;
}

export interface ItemEntrada { product_id: string; quantidade: number; custo_unit?: number }

export async function entradaMercadoria(p: {
  itens: ItemEntrada[];
  supplierId?: string | null;
  gerarCaixa?: boolean;
  forma?: PaymentMethod | null;
  observacao?: string | null;
}): Promise<void> {
  const { error } = await supabase.rpc('entrada_mercadoria', {
    p_itens: p.itens,
    p_supplier_id: p.supplierId ?? null,
    p_gerar_caixa: p.gerarCaixa ?? false,
    p_forma: p.forma ?? null,
    p_observacao: p.observacao ?? null,
  });
  if (error) throw error;
}

export async function ajustarEstoque(p: {
  productId: string;
  novaQuantidade: number;
  motivo?: StockMoveReason;
  observacao?: string | null;
}): Promise<void> {
  const { error } = await supabase.rpc('ajustar_estoque', {
    p_product_id: p.productId,
    p_nova_quantidade: p.novaQuantidade,
    p_motivo: p.motivo ?? 'ajuste',
    p_observacao: p.observacao ?? null,
  });
  if (error) throw error;
}

// --- Caixa (tela 7) ----------------------------------------------------------

export interface FiltroCaixa {
  de: string;   // ISO
  ate: string;  // ISO
  tipo?: CashFlowType | 'todos';
  categoria?: CashCategory | 'todas';
  forma?: PaymentMethod | 'todas';
}

export async function listarCaixa(f: FiltroCaixa): Promise<CashMovement[]> {
  let q = supabase
    .from('cash_movements')
    .select('*')
    .gte('ocorrido_em', f.de)
    .lt('ocorrido_em', f.ate)
    .order('ocorrido_em', { ascending: false })
    // Desempate estável entre as páginas — ver emPaginas.
    .order('id');

  if (f.tipo && f.tipo !== 'todos') q = q.eq('tipo', f.tipo);
  if (f.categoria && f.categoria !== 'todas') q = q.eq('categoria', f.categoria);
  if (f.forma && f.forma !== 'todas') q = q.eq('forma_pagamento', f.forma);

  // EM PÁGINAS, como as vendas. A gaveta é o lugar onde somar um pedaço dói
  // mais: o mês inteiro do Caixa passa de 1.000 movimentos no ritmo atual
  // (564 em agosto/2026, 340 na primeira quinzena de setembro) e, no dia em
  // que passasse, entradas e saídas do mês parariam de bater com a gaveta
  // sem uma linha de aviso.
  return emPaginas((de, ate) => q.range(de, ate));
}

export async function lancarCaixa(p: {
  tipo: CashFlowType;
  categoria: CashCategory;
  forma: PaymentMethod | null;
  valor: number;
  descricao: string | null;
  ocorridoEm?: string;
}): Promise<void> {
  const { data: sessao } = await supabase.auth.getUser();
  const { error } = await supabase.from('cash_movements').insert({
    tipo: p.tipo,
    categoria: p.categoria,
    forma_pagamento: p.forma,
    valor: p.valor,
    descricao: p.descricao,
    ocorrido_em: p.ocorridoEm ?? new Date().toISOString(),
    registrado_por: sessao.user?.id ?? null,
  });
  if (error) throw error;
}

export async function caixaDiario(deISO: string, ateISO: string): Promise<CaixaDiarioRow[]> {
  return ou(
    await supabase.from('v_caixa_diario').select('*')
      .gte('dia', deISO).lte('dia', ateISO)
      .order('dia', { ascending: false }),
  ) ?? [];
}

export async function caixaMensal(): Promise<CaixaMensalRow[]> {
  return ou(
    await supabase.from('v_caixa_mensal').select('*').order('mes', { ascending: false }).limit(24),
  ) ?? [];
}

export async function caixaPorHora(diaISO: string): Promise<CaixaPorHoraRow[]> {
  return ou(
    await supabase.from('v_caixa_por_hora').select('*').eq('dia', diaISO).order('hora'),
  ) ?? [];
}

// --- Relatórios (tela 8) -----------------------------------------------------
// A tela tem dois donos desde 28/09/2026: a REPOSIÇÃO e o resumo do cadastro
// (aqui embaixo) são de gerente e dono, porque são estoque em unidades; tudo o
// que traz R$ de venda — vendasDoPeriodo, itensVendidos, vendasCoringa, margens,
// resumoDoDia — é só do dono, e a tela nem dispara essas consultas para a
// equipe. Quem nega é o banco.

export async function reposicao(): Promise<ReposicaoRow[]> {
  return ou(
    await supabase.from('v_reposicao').select('*').order('sugerir_comprar', { ascending: false }),
  ) ?? [];
}

/** Fila de auditoria do SKU coringa 9999 (view já ordena da mais recente). */
export async function vendasCoringa(): Promise<VendaCoringaRow[]> {
  return ou(await supabase.from('v_vendas_coringa').select('*').limit(100)) ?? [];
}

/**
 * Margem por peça — RPC, não view.
 *
 * A view `v_margens` deixou de existir: para ler `preco_custo` (revogado de
 * `authenticated` na migração 014) ela precisaria ser SECURITY DEFINER, e view
 * definer é ERRO no linter do Supabase — além de ser fácil alguém "melhorar" a
 * view depois e derrubar a checagem de papel sem perceber. Virou função com
 * `exige_papel()` na primeira linha, que é o padrão do resto do sistema.
 *
 * A ordenação (margem mais apertada primeiro) mora na função: é lá que ela
 * sobrevive a quem chamar de outro lugar.
 */
export async function margens(limite = 200): Promise<MargensRow[]> {
  return ou(
    await supabase.rpc('margens', { p_limite: limite }),
  ) ?? [];
}

export interface VendaItemRelatorio {
  quantidade: number;
  preco_unit: number;
  subtotal: number;
  product_id: string;
  products: { sku: string; descricao: string } | null;
}

export async function itensVendidos(deISO: string, ateISO: string): Promise<VendaItemRelatorio[]> {
  const dados = await emPaginas((de, ate) =>
    supabase
      .from('sale_items')
      .select('quantidade, preco_unit, subtotal, product_id, products(sku, descricao), sales!inner(vendida_em)')
      .gte('sales.vendida_em', deISO)
      .lt('sales.vendida_em', ateISO)
      // Peça de venda cancelada voltou para a prateleira — não é "mais
      // vendida". Mesmo motivo do filtro em vendasDoPeriodo, logo abaixo.
      .is('sales.cancelada_em', null)
      // Ordem fixa: sem ela o servidor pode devolver a mesma linha em duas
      // páginas e esquecer outra.
      .order('id')
      .range(de, ate),
  ) as Array<Omit<VendaItemRelatorio, 'products'> & {
    products: { sku: string; descricao: string } | Array<{ sku: string; descricao: string }> | null;
  }> | null;

  // O PostgREST devolve o relacionamento como array quando não consegue provar
  // que é 1-1. Normalizamos aqui para a tela não precisar saber disso.
  return (dados ?? []).map((d) => ({
    ...d,
    products: Array.isArray(d.products) ? d.products[0] ?? null : d.products,
  }));
}

export interface VendaResumo {
  id: string; total: number; vendida_em: string; vendedor: string | null;
  forma_pagamento: PaymentMethod | null; cliente: string | null;
  cancelada_em: string | null;
  // A passagem pelo caixa (055) — nula na maioria das vendas, e tudo bem.
  conferida_em: string | null; conferida_por_nome: string | null;
  valor_recebido: number | null;
  /** As PARTES do pagamento dividido (075): vazio em toda venda que não é
   *  'misto'. O caixa calcula o troco sobre a parte em dinheiro, e a lista
   *  mostra "Misto (Dinheiro R$ 100 + Crédito R$ 50)". */
  sale_payments: Array<Pick<SalePayment, 'forma_pagamento' | 'valor'>>;
}

export async function vendasDoPeriodo(deISO: string, ateISO: string): Promise<VendaResumo[]> {
  return emPaginas((de, ate) =>
    supabase
      .from('sales')
      .select('id, total, vendida_em, vendedor, forma_pagamento, cliente, cancelada_em, conferida_em, conferida_por_nome, valor_recebido, sale_payments(forma_pagamento, valor)')
      .gte('vendida_em', deISO)
      .lt('vendida_em', ateISO)
      // Cancelada FORA, AQUI e não em cada tela. Era responsabilidade de
      // quem chamava: o Caixa descartava, a Relatórios não — e o "Vendido"
      // de lá somava venda estornada desde 17/08/2026 (1d3514c), quando esta
      // consulta passou a trazer as canceladas para a tela do Caixa que
      // nascia. E no servidor a venda estornada também não ocupa página.
      .is('cancelada_em', null)
      .order('vendida_em', { ascending: false })
      .order('id')
      .range(de, ate),
  );
}

// --- Apoio -------------------------------------------------------------------

export async function fornecedores(): Promise<Supplier[]> {
  return ou(
    await supabase.from('suppliers').select('*').eq('ativo', true).order('nome'),
  ) ?? [];
}

// --- Fornecedores: cadastro ---------------------------------------------------
// A tabela existe desde a base v1 e a Entrada de mercadoria já lê dela; o que
// faltava era como PREENCHER. Insert e update são de gerente/dono e quem nega é
// a RLS (rls.sql §1), não estas funções.

/** Inclui os inativos — é a tela do cadastro que precisa poder reativar. */
export async function listarFornecedores(): Promise<Supplier[]> {
  return ou(await supabase.from('suppliers').select('*').order('nome')) ?? [];
}

export interface DadosFornecedor {
  nome: string;
  cidade: string | null;
  cnpj: string | null;
  canal_preferencial: SupplierChannel;
  whatsapp: string | null;
  email: string | null;
  /** Já normalizado (normalizarSite): http/https ou null. */
  site: string | null;
  lead_time_dias: number;
  pedido_minimo: number;
  observacoes: string | null;
  ativo: boolean;
}

/**
 * O `slug` é `not null unique` no schema e serve de identificador estável para
 * a Fase 2 (roteamento de pedido). Ninguém no balcão vai digitar isso: sai do
 * nome. Colisão vira erro 23505 e mensagem em português — dois fornecedores com
 * o mesmo nome é problema de cadastro, não algo para o sistema desempatar
 * sozinho inventando "skypecas-2".
 */
export function slugDeFornecedor(nome: string): string {
  return nome
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 40);
}

const camposDoFornecedor = (f: DadosFornecedor) => ({
  nome: f.nome.trim(),
  cidade: f.cidade?.trim() || null,
  cnpj: f.cnpj?.trim() || null,
  canal_preferencial: f.canal_preferencial,
  whatsapp: f.whatsapp?.trim() || null,
  email: f.email?.trim() || null,
  site: f.site,
  lead_time_dias: f.lead_time_dias,
  pedido_minimo: f.pedido_minimo,
  observacoes: f.observacoes?.trim() || null,
  ativo: f.ativo,
});

export async function criarFornecedor(f: DadosFornecedor): Promise<Supplier> {
  return ou(
    await supabase.from('suppliers')
      .insert({ ...camposDoFornecedor(f), slug: slugDeFornecedor(f.nome) })
      .select('*').single(),
  ) as Supplier;
}

/**
 * O slug NÃO é reescrito ao editar. Ele é a chave que a Fase 2 vai usar para
 * amarrar pedido a fornecedor; corrigir um erro de digitação no nome não pode
 * trocar a identidade de quem já tem cotação e histórico de compra.
 */
// --- Quem costuma ter a peça --------------------------------------------------
// `supplier_products` já existia desde a base v1 e é preenchida sozinha pela
// Entrada de mercadoria (custo + data da cotação). O que faltava era declarar o
// vínculo ANTES de comprar: "disco de freio, quem tem é MGA e Skypeças" — que é
// a informação usada para ir pesquisar no site ou no WhatsApp deles.
//
// `custo` é `not null default 0`: vínculo declarado à mão nasce com custo zero e
// a Entrada preenche de verdade na primeira compra. Zero aqui significa "ainda
// não cotado", não "de graça" — a tela não mostra custo nenhum enquanto a
// cotação não existir.

export interface FornecedorDaPeca {
  supplier_id: string;
  /** Código da peça no catálogo DELE — é o que se digita no site do fornecedor. */
  codigo_fornecedor: string | null;
  custo: number;
  ultima_cotacao: ISODate | null;
  fornecedor: Pick<Supplier,
    'id' | 'nome' | 'canal_preferencial' | 'whatsapp' | 'email' | 'site' | 'ativo'>;
}

/**
 * POR QUE RPC E NÃO CONSULTA DIRETA: `supplier_products` guarda `custo`, cujo
 * SELECT é revogado de `authenticated` (rls.sql §2) — e privilégio de coluna no
 * Postgres derruba a CONSULTA inteira, não devolve a coluna em branco.
 *
 * A primeira versão lia a tabela direto e era negada com 42501. O efeito não era
 * só "não aparece": a tela de edição abria vazia e salvar a partir dela APAGAVA
 * os vínculos existentes. Achado em produção em 10/08/2026.
 */
export async function fornecedoresDaPeca(productId: string): Promise<FornecedorDaPeca[]> {
  const linhas = ou(
    await supabase.rpc('fornecedores_da_peca', { p_product_id: productId }),
  ) as FornecedorDaPecaRow[] | null;

  return (linhas ?? []).map((r) => ({
    supplier_id: r.supplier_id,
    codigo_fornecedor: r.codigo_fornecedor,
    custo: Number(r.custo),
    ultima_cotacao: r.ultima_cotacao,
    fornecedor: {
      id: r.supplier_id,
      nome: r.nome,
      canal_preferencial: r.canal_preferencial,
      whatsapp: r.whatsapp,
      email: r.email,
      site: r.site,
      ativo: r.ativo,
    },
  }));
}

/** O que o chip da busca precisa saber — nada de custo, que ali não tem lugar. */
export interface FornecedorDeResultado {
  product_id: string;
  supplier_id: string;
  nome: string;
  site: string | null;
  codigo_fornecedor: string | null;
}

/**
 * Os fornecedores de VÁRIAS peças de uma vez.
 *
 * A busca devolve até algumas dezenas de linhas e é redigitada a cada 250 ms.
 * Uma consulta por resultado seria uma rajada a cada tecla; esta é uma só, com
 * a lista de ids. Peça sem fornecedor simplesmente não volta.
 */
export async function fornecedoresDePecas(
  productIds: string[],
): Promise<FornecedorDeResultado[]> {
  if (productIds.length === 0) return [];
  const linhas = ou(
    await supabase
      .from('supplier_products')
      .select('product_id, supplier_id, suppliers(nome, site, ativo), codigo_fornecedor')
      .in('product_id', productIds),
  ) as unknown as Array<{
    product_id: string; supplier_id: string; codigo_fornecedor: string | null;
    suppliers: { nome: string; site: string | null; ativo: boolean }
      | Array<{ nome: string; site: string | null; ativo: boolean }> | null;
  }> | null;

  return (linhas ?? [])
    .map(({ suppliers, ...r }) => {
      const f = Array.isArray(suppliers) ? suppliers[0] : suppliers;
      return f && f.ativo ? { ...r, nome: f.nome, site: f.site } : null;
    })
    .filter((r): r is FornecedorDeResultado => r !== null)
    .sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'));
}

/**
 * Grava a lista inteira: o que sumiu dela é desvinculado.
 *
 * TAMBÉM É RPC, e não por simetria: a policy de DELETE de supplier_products é de
 * DONO (rls.sql §1). Pelo caminho direto, um gerente removendo um fornecedor não
 * recebia erro — a RLS não casava linha nenhuma, o delete afetava zero linhas e a
 * tela dizia que salvou. Falha silenciosa é pior que erro.
 *
 * A função preserva `custo` e `ultima_cotacao` de quem permanece: cotação é
 * território da Entrada de mercadoria, não de quem declara o vínculo.
 */
export async function salvarFornecedoresDaPeca(
  productId: string,
  vinculos: Array<{ supplier_id: string; codigo_fornecedor: string | null }>,
): Promise<void> {
  const { error } = await supabase.rpc('salvar_fornecedores_da_peca', {
    p_product_id: productId,
    p_vinculos: vinculos.map((v) => ({
      supplier_id: v.supplier_id,
      codigo_fornecedor: v.codigo_fornecedor?.trim() || null,
    })),
  });
  if (error) throw error;
}

// --- Foto da peça (migração 030) ------------------------------------------------
// O binário NUNCA passa por aqui em leitura: a foto sai pela rota
// /imagens/pecas/<id> do Caddy, que o <img> consome sem cabeçalho (pública —
// decisão documentada na migração). Estas funções só cuidam do "existe foto?"
// e da escrita, que é RPC com papel checado.

/** A base das URLs de imagem é a MESMA da API — é o Caddy dela que serve. */
export function urlDaFotoDaPeca(productId: string, updatedAt: string): string {
  return urlDaImagem(import.meta.env.VITE_SUPABASE_URL as string, productId, updatedAt);
}

/** URL de UMA foto da galeria — endereçada pelo id DA FOTO (031). */
export function urlDaFoto(fotoId: string, updatedAt: string): string {
  return urlDaFotoItem(import.meta.env.VITE_SUPABASE_URL as string, fotoId, updatedAt);
}

/** A galeria de UMA peça, em ordem — a primeira é a principal. */
export async function fotosDaPeca(productId: string): Promise<ProductImageInfo[]> {
  return ou(
    await supabase
      .from('product_images')
      .select('id, product_id, ordem, bytes, largura, altura, updated_at')
      .eq('product_id', productId)
      .order('ordem').order('created_at'),
  ) ?? [];
}

/**
 * As galerias de VÁRIAS peças de uma vez (miniaturas de lista). Quem consome
 * reduz para a principal — com ≤ 8 fotos por peça, filtrar no cliente é mais
 * barato que inventar view para isso.
 */
export async function fotosDasPecas(productIds: string[]): Promise<ProductImageInfo[]> {
  if (productIds.length === 0) return [];
  return ou(
    await supabase
      .from('product_images')
      .select('id, product_id, ordem, bytes, largura, altura, updated_at')
      .in('product_id', productIds)
      .order('ordem').order('created_at'),
  ) ?? [];
}

/** Acrescenta ao FIM da galeria (a 1ª de todas vira a principal). */
export async function adicionarFotoPeca(p: {
  productId: string; base64: string; largura: number; altura: number;
}): Promise<void> {
  const { error } = await supabase.rpc('adicionar_foto_peca', {
    p_product_id: p.productId,
    p_dados: p.base64,
    p_largura: p.largura,
    p_altura: p.altura,
  });
  if (error) throw error;
}

export async function removerFoto(fotoId: string): Promise<void> {
  const { error } = await supabase.rpc('remover_foto', { p_foto_id: fotoId });
  if (error) throw error;
}

/** Renumera a galeria: a escolhida vira a primeira — e a ficha, a miniatura e
 *  a busca passam a mostrá-la. */
export async function definirFotoPrincipal(fotoId: string): Promise<void> {
  const { error } = await supabase.rpc('definir_foto_principal', { p_foto_id: fotoId });
  if (error) throw error;
}

// --- Posições: quem mora onde -------------------------------------------------
// A posição é da PRATELEIRA, não da peça (decisão do dono, 11/08/2026): calotas
// X zeraram e saíram de linha, calotas Z herdam a parede 01 lugar Y. A troca
// sempre foi possível (stock.local é texto livre, sem exclusividade — dividir
// gaveta é legítimo); o que faltava era enxergar quem o sistema AINDA acha que
// mora ali, para a ficha da linha morta não mandar ninguém ao lugar errado.

export interface MoradorDaPosicao {
  product_id: string;
  sku: string;
  descricao: string;
  ativo: boolean;
  quantidade: number;
}

/** ilike sem curinga = igualdade sem diferenciar maiúsculas — "a-p3-g2" acha
 *  "A-P3-G2". Posição é digitada à mão, e caixa não pode esconder morador. */
export async function quemMoraNaPosicao(local: string): Promise<MoradorDaPosicao[]> {
  const termo = local.trim();
  if (termo === '') return [];
  const linhas = ou(
    await supabase
      .from('stock')
      .select('product_id, quantidade, products!inner(sku, descricao, ativo)')
      .ilike('local', termo)
      .limit(20),
  ) as unknown as Array<{
    product_id: string; quantidade: number;
    products: { sku: string; descricao: string; ativo: boolean }
      | Array<{ sku: string; descricao: string; ativo: boolean }> | null;
  }> | null;

  return (linhas ?? [])
    .map(({ products, ...r }) => {
      const p = Array.isArray(products) ? products[0] : products;
      return p ? { ...r, sku: p.sku, descricao: p.descricao, ativo: p.ativo } : null;
    })
    .filter((m): m is MoradorDaPosicao => m !== null)
    .sort((a, b) => b.quantidade - a.quantidade);
}

/**
 * Tira a peça da posição (local = null). Quem nega é a RLS: update de stock é
 * gerente/dono — o vendedor vê o aviso, mas não desaloja ninguém.
 */
export async function limparPosicao(productId: string): Promise<void> {
  const { data, error } = await supabase
    .from('stock')
    .update({ local: null, updated_at: new Date().toISOString() })
    .eq('product_id', productId)
    .select('product_id');
  if (error) throw error;
  // Update barrado pela RLS não dá erro — afeta zero linhas e segue a vida (a
  // armadilha do delete de fornecedor, ontem). Zero linhas aqui vira recado.
  if (!data || data.length === 0) {
    throw new Error('Não deu para liberar a posição — seu perfil não altera o estoque.');
  }
}

export async function atualizarFornecedor(id: string, f: DadosFornecedor): Promise<Supplier> {
  return ou(
    await supabase.from('suppliers')
      .update({ ...camposDoFornecedor(f), updated_at: new Date().toISOString() })
      .eq('id', id).select('*').single(),
  ) as Supplier;
}

export async function perfis(): Promise<Profile[]> {
  return ou(await supabase.from('profiles').select('*').order('nome')) ?? [];
}

export async function salvarPerfil(
  p: Partial<Profile> & { id: string },
): Promise<void> {
  const { error } = await supabase.from('profiles').update(p).eq('id', p.id);
  if (error) throw error;
}

// --- Equipe (tela 10 — só dono) ----------------------------------------------

/**
 * Contas são criadas pelo AGENTE da VPS (rota /infra/v1/equipe), não daqui:
 * criar usuário exige a service key, que nunca pode estar no navegador. O
 * agente revalida o papel do chamador no servidor antes de agir — a mesma
 * cadeia da antiga Edge Function `equipe-admin`, que morreu com o ADR-006
 * (validava a sessão no GoTrue do Supabase, que recusa o token da VPS, e
 * criaria a conta num banco em que ninguém mais loga).
 */
async function chamarEquipeAdmin<T>(corpo: Record<string, unknown>): Promise<T> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (!token) throw new Error('Sessão expirada — entre de novo.');
  const base = import.meta.env.VITE_SUPABASE_URL as string;
  const r = await fetch(`${base}/infra/v1/equipe`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(corpo),
  });
  const resposta = await r.json().catch(() => null) as { erro?: string } | null;
  if (!r.ok) throw new Error(resposta?.erro ?? `O agente respondeu ${r.status}.`);
  return resposta as T;
}

export interface NovoFuncionario {
  nome: string;
  email: string;
  senha: string;
  papel: Papel;
  telefone?: string | null;
  admitido_em?: string | null;
}

export const criarFuncionario = (f: NovoFuncionario) =>
  chamarEquipeAdmin<{ ok: true; id: string }>({ acao: 'criar', ...f });

export const redefinirSenha = (id: string, senha: string) =>
  chamarEquipeAdmin<{ ok: true }>({ acao: 'redefinir_senha', id, senha });

export const excluirFuncionario = (id: string) =>
  chamarEquipeAdmin<{ ok: true }>({ acao: 'excluir', id });

/**
 * Desligar/reativar passa pela Edge Function, não por um update em profiles.
 *
 * POR QUÊ (auditoria 2026-08-04): marcar `ativo = false` zera o papel, mas não
 * derruba a sessão nem impede login — a conta no Auth continua existindo e a
 * senha continua valendo. A tela dizia "desligado" e a pessoa continuava
 * entrando. A Edge Function bane a conta no Auth E marca o perfil na mesma
 * ação, para os dois estados não divergirem.
 */
export const alternarDesligamento = (id: string, desligar: boolean) =>
  chamarEquipeAdmin<{ ok: true; ativo: boolean }>({
    acao: desligar ? 'desligar' : 'reativar',
    id,
  });

export async function termosDaEquipe(): Promise<EmploymentTerms[]> {
  return ou(await supabase.from('employment_terms').select('*')) ?? [];
}

export async function salvarTermos(t: EmploymentTerms | {
  profile_id: string; salario_base: number; comissao_percent: number;
  dia_pagamento: number; observacao?: string | null;
}): Promise<void> {
  const { error } = await supabase
    .from('employment_terms')
    .upsert({
      profile_id: t.profile_id,
      salario_base: t.salario_base,
      comissao_percent: t.comissao_percent,
      dia_pagamento: t.dia_pagamento,
      observacao: 'observacao' in t ? t.observacao ?? null : null,
    }, { onConflict: 'profile_id' });
  if (error) throw error;
  // 094: a folha PREVISTA acompanha o combinado na hora — o salário ou o dia
  // que mudou aqui aparece em Contas a pagar e no fluxo sem esperar a próxima
  // abertura. Só mexe na linha que ninguém editou (prevista e não paga).
  const { error: erroDaPrevista } = await supabase.rpc('gerar_folha_prevista');
  if (erroDaPrevista) {
    throw new Error('O combinado foi salvo, mas a folha prevista do mês não foi atualizada: '
      + ((erroDaPrevista as { message?: string }).message ?? String(erroDaPrevista))
      + ' — salve de novo para tentar outra vez.');
  }
}

// --- Gestão de vendas (tela 11) ----------------------------------------------
// Quem vê o QUE: desde 28/09/2026 só o DONO recebe as vendas de toda a equipe.
// Vendedor e gerente recebem as próprias — o gerente continua cancelando,
// devolvendo e cobrando fiado, mas não enxerga quanto o colega vendeu.
// Quem recorta é a RLS de `sales`; estas funções só perguntam.

export interface VendaDetalhada extends VendaResumo {
  /** 'VD-000001' (061) — o número do recibo. */
  codigo: string;
  /** A entrega, congelada na venda (061). */
  entrega: boolean;
  entrega_endereco: string | null;
  entrega_contato: string | null;
  entrega_observacao: string | null;
  entrega_forma_prevista: PaymentMethod | null;
  entrega_troco_para: number | null;
  entregue_em: string | null;
  entregue_por_nome: string | null;
  /** A taxa de entrega (064) e o que o cliente pagou de fato (total + taxa).
   *  O painel de correcao precisa do segundo para saber quanto do caixa esta
   *  venda ocupa hoje, e portanto qual e a DIFERENCA da correcao. */
  entrega_taxa: number;
  total_cobrado: number;
  /** 073 — o carro da venda, escolhido do catalogo e congelado aqui. Nulo na
   *  venda em que o vendedor nao informou o carro (o campo e opcional). */
  veiculo_montadora: string | null;
  veiculo_modelo: string | null;
  veiculo_ano: number | null;
  /** 073 — o carro CADASTRADO do cliente (051) que esta venda atendeu. */
  customer_vehicle_id: string | null;
  /** A correcao do conteudo (070): em que versao a venda esta, e o porque da
   *  ultima. `versao > 1` e o que faz a tela mostrar a tarja de corrigida. */
  versao: number;
  corrigida_em: string | null;
  motivo_correcao: string | null;
  vendedor_id: string | null;
  cancelada_em: string | null;
  motivo_cancelamento: string | null;
  canal: string;
  desconto: number;
  observacao: string | null;
  /** Cliente CADASTRADO (023) — o painel de edição pré-seleciona por ele. */
  customer_id: string | null;
  /** As peças, só o bastante para o FILTRO da lista achá-las (11/09/2026). */
  sale_items: ItemDaVendaNaLista[];
}

/**
 * A peça de uma VENDA, como o filtro da lista precisa dela.
 *
 * NÃO é a mesma forma da peça do orçamento (ItemNaLista), e isso derrubou a
 * lista de vendas para todo mundo em 11/09/2026: `sale_items` NÃO TEM coluna
 * `descricao` nem `sku` — o nome da peça diversa mora em `descricao_avulsa`, e
 * o da peça cadastrada mora no PRODUTO. O embed `sale_items(descricao, sku,
 * ...)` respondia 400 ("column sale_items_1.descricao does not exist") e a
 * tela inteira caía, porque a lista é uma consulta só. `quote_items` tem as
 * duas colunas (o orçamento congela nome e sku na linha); `sale_items` não.
 *
 * O texto do select não é conferido por ninguém antes de ir ao ar: o typecheck
 * passa, os 1022 testes passam, e o erro só aparece no navegador do vendedor.
 * A conferência que pega isso é uma chamada real à API — ver o teste em
 * consultas.embeds.test.ts, que trava as colunas contra core/types.ts.
 */
export interface ProdutoNaLista { sku: string; descricao: string }
export interface ItemDaVendaNaLista {
  descricao_avulsa: string | null;
  codigo_avulso: string | null;
  products: ProdutoNaLista | null;
}

/**
 * A peça como o filtro de uma lista precisa dela — e nada mais.
 *
 * O dono buscou "dot4" na aba de vendas e não achou a venda do fluido DOT 4
 * (11/09/2026): o filtro olhava código, cliente, vendedor e observação, e a
 * peça vendida não estava em nenhum deles. Quem liga perguntando diz a peça
 * ("a pastilha do Gol"), não o número da venda — o mesmo motivo pelo qual as
 * encomendas já filtravam pelo nome desde 01/09.
 *
 * Três campos, congelados na hora do documento: a descrição, o sku da loja e o
 * código que o balcão digitou na peça diversa (071). É o que se conhece da
 * peça no balcão; preço e quantidade não ajudam a ACHAR a venda.
 */
export interface ItemNaLista {
  descricao: string;
  sku: string | null;
  codigo_avulso: string | null;
}

export async function listarVendas(f: {
  de: string; ate: string; vendedorId?: string | null; incluirCanceladas?: boolean;
}): Promise<VendaDetalhada[]> {
  // Literal em uma linha só de propósito: o supabase-js infere os tipos do
  // TEXTO do select, e concatenação com `+` derruba essa inferência.
  let q = supabase
    .from('sales')
    .select('id, codigo, total, vendida_em, vendedor, vendedor_id, forma_pagamento, cliente, cancelada_em, motivo_cancelamento, canal, desconto, observacao, customer_id, conferida_em, conferida_por_nome, valor_recebido, entrega, entrega_endereco, entrega_contato, entrega_observacao, entrega_forma_prevista, entrega_troco_para, entregue_em, entregue_por_nome, entrega_taxa, total_cobrado, versao, corrigida_em, motivo_correcao, veiculo_montadora, veiculo_modelo, veiculo_ano, customer_vehicle_id, sale_items(descricao_avulsa, codigo_avulso, products(sku, descricao)), sale_payments(forma_pagamento, valor)')
    .gte('vendida_em', f.de)
    .lt('vendida_em', f.ate)
    .order('vendida_em', { ascending: false })
    // Desempate ESTÁVEL entre páginas: duas vendas no mesmo instante podem
    // trocar de lugar de uma página para a outra — uma viria repetida e a
    // outra sumiria da soma.
    .order('id');

  if (f.vendedorId) q = q.eq('vendedor_id', f.vendedorId);
  if (!f.incluirCanceladas) q = q.is('cancelada_em', null);

  // O supabase-js não sabe que `products` é UM por item (a chave está em
  // sale_items) e tipa o embed como lista; em tempo de execução vem objeto.
  // O mesmo tratamento de itensDaVenda, pelo mesmo motivo.
  const bruto = await emPaginas((de, ate) => q.range(de, ate)) as Array<
    Omit<VendaDetalhada, 'sale_items'> & {
      sale_items: Array<Omit<ItemDaVendaNaLista, 'products'> & {
        products: ProdutoNaLista | ProdutoNaLista[] | null;
      }> | null;
    }
  >;
  return bruto.map((v) => ({
    ...v,
    sale_items: (v.sale_items ?? []).map((i) => ({
      ...i,
      products: Array.isArray(i.products) ? (i.products[0] ?? null) : i.products,
    })),
  }));
}

/**
 * UMA venda pelo CÓDIGO do papel — o caminho do gerente para a venda de um
 * colega (28/09/2026).
 *
 * POR QUE ISTO EXISTE. No mesmo dia em que a policy de `sales` passou a dar ao
 * gerente só as vendas DELE, ele continuou encarregado de cancelar, corrigir,
 * devolver e cobrar o fiado das vendas de todo mundo — é o trabalho dele. Sem
 * esta porta, a lista da tela simplesmente não tem a venda do colega, e o
 * único caminho até ela seria chamar o dono. O manual já prometia o gesto
 * ("pelo código do papel"); faltava a função.
 *
 * É definer com `exige_papel(['gerente','dono'])` no corpo, e devolve UMA
 * venda: quem tem o código na mão tem o papel na mão. Nenhum agregado, nenhum
 * custo — não dá para varrer a loja com ela, porque não há "listar".
 *
 * O código aceita as duas formas que o balcão usa ('VD-000123' e '123'); a
 * normalização é do banco, como em todo código da casa. `null` quando não
 * existe venda com aquele código — não é erro: é "digitou errado".
 */
export async function vendaPeloCodigo(codigo: string): Promise<VendaDetalhada | null> {
  const linhas = ou(await supabase.rpc('venda_pelo_codigo', { p_codigo: codigo }));
  const v = (linhas as VendaPeloCodigo[] | null)?.[0];
  if (!v) return null;
  // Montagem CAMPO A CAMPO, e não `...v`: a RPC é a porta ESTREITA, e o tipo
  // `VendaPeloCodigo` (core/types.ts) diz exatamente o que ela devolve. O que
  // NÃO está lá recebe aqui, à mão, o valor NEUTRO — escrito como literal e
  // não como `?? null`, porque um `??` sugeriria que o campo às vezes vem, e
  // ele nunca vem. O typecheck é a trava: se a RPC passar a devolver um deles,
  // esta função para de compilar e alguém decide o que fazer.
  //
  // O que não vem, e por quê (o mesmo texto está no SQL e no tipo):
  //
  //   conferida_em / conferida_por_nome / valor_recebido — o caixa é só do
  //       dono desde 28/09/2026. Nulos desenham "não conferida", que é o que o
  //       gerente pode saber desta venda.
  //   sale_payments — o §19 do rls.sql dá ao gerente só as partes das vendas
  //       DELE; a forma já vem em `forma_pagamento`. Lista vazia é o que a
  //       linha espera de toda venda que não é 'misto'.
  //   versao / corrigida_em / motivo_correcao — a 090 levou `sales_versoes`
  //       inteiro para o dono (o jsonb de itens carrega o custo congelado).
  //       `versao: 1` é a escolha de NÃO mostrar a tarja de "corrigida" por
  //       esta porta; não é um dado. Quem precisar dela fala com o dono.
  //   canal — vazio, que rotuloDoCanalDeVenda desenha como '—'. Chutar
  //       'balcao' escreveria na tela um fato que ninguém consultou.
  //   customer_vehicle_id — o vínculo com o carro CADASTRADO do cliente (051)
  //       só serve ao painel de edição, que é do dono pela própria RPC.
  //
  // E `a_ver_usado`, que a RPC devolve, NÃO entra: a linha da tela não o usa, e
  // campo a campo é o que impede um campo de entrar de carona.
  return {
    id: v.id,
    codigo: v.codigo,
    vendida_em: v.vendida_em,
    cliente: v.cliente,
    customer_id: v.customer_id,
    vendedor: v.vendedor,
    vendedor_id: v.vendedor_id,
    total: Number(v.total),
    desconto: Number(v.desconto),
    forma_pagamento: v.forma_pagamento,
    observacao: v.observacao,
    cancelada_em: v.cancelada_em,
    motivo_cancelamento: v.motivo_cancelamento,
    entrega: v.entrega,
    entrega_endereco: v.entrega_endereco,
    entrega_contato: v.entrega_contato,
    entrega_observacao: v.entrega_observacao,
    entrega_forma_prevista: v.entrega_forma_prevista,
    entrega_troco_para: v.entrega_troco_para,
    entregue_em: v.entregue_em,
    entregue_por_nome: v.entregue_por_nome,
    entrega_taxa: Number(v.entrega_taxa),
    total_cobrado: Number(v.total_cobrado),
    veiculo_montadora: v.veiculo_montadora,
    veiculo_modelo: v.veiculo_modelo,
    veiculo_ano: v.veiculo_ano,
    // --- daqui para baixo, o que a RPC não devolve (ver acima) ---
    canal: '',
    customer_vehicle_id: null,
    versao: 1,
    corrigida_em: null,
    motivo_correcao: null,
    conferida_em: null,
    conferida_por_nome: null,
    valor_recebido: null,
    sale_payments: [],
    // As peças não vêm aqui: quem abre a venda pede
    // itensDaVendaParaGerencia(). Vazio é o certo — este campo só alimenta o
    // filtro por nome de peça da LISTA, e uma venda achada pelo código não
    // passa por filtro nenhum.
    sale_items: [],
  };
}

export interface ItemDaVenda {
  id: string;
  quantidade: number;
  preco_unit: number;
  subtotal: number;
  product_id: string;
  /**
   * O que a sanfona da aba Vendas abre: as OBSERVAÇÕES do balcão (o campo mais
   * valioso do cadastro — macetes e equivalências que só o Serginho sabe) e os
   * códigos, para conferir a peça sem sair da venda.
   */
  products: ItemVendaProduto | null;
  /** PEÇA DIVERSA (071): o código digitado no balcão. Uso interno — aparece
   *  aqui na tela e em lugar nenhum do papel do cliente. */
  codigo_avulso: string | null;
  /** 073 — o carro DESTA linha, quando é outro que não o da venda. Nulo quer
   *  dizer "o carro da venda": quem mostra faz o coalesce com a venda. */
  veiculo_montadora: string | null;
  veiculo_modelo: string | null;
  /** PEÇA DIVERSA (041): o que saiu de verdade nesta linha. */
  descricao_avulsa: string | null;
}

export interface ItemVendaProduto {
  sku: string;
  descricao: string;
  marca: string | null;
  observacoes: string | null;
  codigo_oem: string | null;
  codigo_fabrica: string | null;
}

export async function itensDaVenda(saleId: string): Promise<ItemDaVenda[]> {
  const dados = ou(
    await supabase
      .from('sale_items')
      // Literal em UMA LINHA só, como no listarVendas: o supabase-js infere os
      // tipos do TEXTO do select, e concatenar com `+` derruba a inferência
      // (o build acusa GenericStringError). `preco_custo` não entra: a coluna
      // é revogada de authenticated e derrubaria a consulta com 42501.
      .select('id, quantidade, preco_unit, subtotal, product_id, descricao_avulsa, codigo_avulso, veiculo_montadora, veiculo_modelo, products(sku, descricao, marca, observacoes, codigo_oem, codigo_fabrica)')
      .eq('sale_id', saleId),
  ) as Array<Omit<ItemDaVenda, 'products'> & {
    products: ItemVendaProduto | ItemVendaProduto[] | null;
  }> | null;

  return (dados ?? []).map((d) => ({
    ...d,
    products: Array.isArray(d.products) ? d.products[0] ?? null : d.products,
  }));
}

/**
 * As MESMAS peças, para quem GERENCIA a venda (28/09/2026).
 *
 * `itensDaVenda` acima lê `sale_items` direto, e desde 28/09/2026 a policy
 * dessa tabela só entrega as linhas das vendas de quem está olhando. Para o
 * gerente isso não devolve erro: devolve VAZIO — e vazio em silêncio é o pior
 * dos dois mundos, porque o painel de devolução abre sem peça nenhuma e parece
 * que a venda não tem itens. Foi o trabalho que ele faz todo dia (acertar
 * fiado peça a peça, devolver a peça do colega) que sumiria sem uma mensagem.
 *
 * Então a gerência lê por função definer, com `exige_papel(['gerente','dono'])`
 * no corpo. Ela devolve as colunas de `sale_items` MENOS `custo_unit` — o
 * custo congelado continua fora do alcance de quem não é dono, que é o outro
 * lado da mesma decisão — e já traz, achatadas, as cinco colunas de `products`
 * que o embed trazia. Remontamos `products` aqui para a tela e o
 * PainelDevolucao não precisarem saber de qual das duas portas vieram.
 */
export async function itensDaVendaParaGerencia(saleId: string): Promise<ItemDaVenda[]> {
  const linhas = ou(await supabase.rpc('itens_da_venda_para_gerencia', {
    p_sale_id: saleId,
  })) as ItemDaVendaParaGerencia[] | null;

  return (linhas ?? []).map((l) => ({
    id: l.id,
    quantidade: l.quantidade,
    preco_unit: l.preco_unit,
    subtotal: l.subtotal,
    product_id: l.product_id,
    descricao_avulsa: l.descricao_avulsa,
    codigo_avulso: l.codigo_avulso,
    veiculo_montadora: l.veiculo_montadora,
    veiculo_modelo: l.veiculo_modelo,
    // `sku` é not null em products: sku nulo quer dizer "não há linha de
    // produto", e não "produto sem código".
    products: l.sku === null ? null : {
      sku: l.sku,
      descricao: l.descricao ?? '',
      marca: l.marca,
      observacoes: l.observacoes,
      codigo_oem: l.codigo_oem,
      codigo_fabrica: l.codigo_fabrica,
    },
  }));
}

/**
 * Qual das duas leituras das peças usar — a decisão num lugar só.
 *
 * Existe porque a escolha se repete em quatro telas (a linha da aba Vendas, a
 * correção, o acerto do fiado e a devolução do fiado) e errar numa delas volta
 * a ser o bug silencioso de 28/09/2026: painel aberto, nenhuma peça, nenhum
 * aviso. Recebe o resultado de `podeGerenciarVendas(papel)` pronto — a régua
 * mora em lib/sessao, e este arquivo não importa de lá (é o contrário: sessao
 * importa daqui).
 *
 * Pura de propósito: devolve a função, não os dados, e por isso o teste
 * consegue perguntar "quem o gerente usa?" sem banco e sem tela.
 */
export function lerItensDaVenda(gerencia: boolean): (saleId: string) => Promise<ItemDaVenda[]> {
  return gerencia ? itensDaVendaParaGerencia : itensDaVenda;
}

/**
 * A LISTA das vendas de todo mundo, para quem gerencia (07/10/2026).
 *
 * O dono: "que ele visualize o valor de CADA venda para documentar, mas não o
 * VALOR TOTAL das vendas somadas". Esta é a porta que cumpre a primeira metade;
 * a segunda é a tela, que não desenha cartão de soma para ele.
 *
 * POR QUE NÃO É `listarVendas` COM OUTRA POLICY. Porque abrir a policy de
 * `sales` entregaria a LINHA INTEIRA — e nela vêm `conferida_por_nome` e
 * `valor_recebido`, que são a conferência de CAIXA, só do dono desde 28/09. A
 * RPC devolve exatamente as colunas desta lista e mais nenhuma.
 *
 * O mapeamento é CAMPO A CAMPO, como em `vendaPeloCodigo`, e é de propósito: é
 * o que impede uma coluna de entrar de carona no dia em que alguém mexer na
 * função. O que a RPC não devolve vira nulo explícito aqui embaixo, e o
 * typecheck quebra se ela passar a devolver.
 */
export async function listarVendasParaGerencia(f: {
  de: string; ate: string; vendedorId?: string | null; incluirCanceladas?: boolean;
}): Promise<VendaDetalhada[]> {
  const linhas = (ou(await supabase.rpc('vendas_para_gerencia', {
    p_de: f.de,
    p_ate: f.ate,
    p_vendedor_id: f.vendedorId || null,
    p_incluir_canceladas: Boolean(f.incluirCanceladas),
  })) as VendaParaGerencia[] | null) ?? [];

  return linhas.map((v) => ({
    id: v.id,
    codigo: v.codigo,
    vendida_em: v.vendida_em,
    cliente: v.cliente,
    customer_id: v.customer_id,
    vendedor: v.vendedor,
    vendedor_id: v.vendedor_id,
    total: Number(v.total),
    desconto: Number(v.desconto),
    forma_pagamento: v.forma_pagamento,
    observacao: v.observacao,
    cancelada_em: v.cancelada_em,
    motivo_cancelamento: v.motivo_cancelamento,
    entrega: v.entrega,
    entrega_endereco: v.entrega_endereco,
    entrega_contato: v.entrega_contato,
    entrega_observacao: v.entrega_observacao,
    entrega_forma_prevista: v.entrega_forma_prevista,
    entrega_troco_para: v.entrega_troco_para,
    entregue_em: v.entregue_em,
    entregue_por_nome: v.entregue_por_nome,
    entrega_taxa: Number(v.entrega_taxa),
    total_cobrado: Number(v.total_cobrado),
    veiculo_montadora: v.veiculo_montadora,
    veiculo_modelo: v.veiculo_modelo,
    veiculo_ano: v.veiculo_ano,
    // A tarja de "corrigida" VEM por esta porta: a lista de outra pessoa é
    // justamente onde uma correção invisível fica indistinguível de adulteração.
    versao: v.versao,
    corrigida_em: v.corrigida_em,
    motivo_correcao: v.motivo_correcao,
    // Só nas vendas DELE — a função devolve nulo nas dos colegas.
    conferida_em: v.conferida_em,
    conferida_por_nome: v.conferida_por_nome,
    // O que a RPC NÃO devolve, e por quê:
    //   valor_recebido — é caixa, e caixa é do dono desde 28/09;
    //   canal — vazio, que rotuloDoCanalDeVenda desenha como '—'. Chutar
    //       'balcao' escreveria na tela um fato que ninguém consultou;
    //   customer_vehicle_id — só serve ao painel de edição, que é do dono.
    valor_recebido: null,
    canal: '',
    customer_vehicle_id: null,
    // As peças, só com o que a BUSCA da tela usa (nome, sku, código da
    // diversa). O `products` é remontado como objeto, para a linha da venda não
    // precisar saber por qual porta as peças vieram. Preço e quantidade NÃO
    // vêm: isso entra quando a sanfona abre, por itensDaVendaParaGerencia.
    sale_items: (v.itens ?? []).map((i) => ({
      descricao_avulsa: i.descricao_avulsa,
      codigo_avulso: i.codigo_avulso,
      products: i.sku === null && i.descricao === null
        ? null
        : { sku: i.sku, descricao: i.descricao },
    })) as VendaDetalhada['sale_items'],
    sale_payments: (v.pagamentos ?? []).map((pg) => ({
      forma_pagamento: pg.forma_pagamento,
      valor: Number(pg.valor),
    })),
  }));
}

/**
 * Qual das duas leituras da LISTA usar — a decisão num lugar só, como em
 * `lerItensDaVenda`.
 *
 * Dono e vendedor leem a tabela (a policy já recorta cada um corretamente, e o
 * dono precisa da linha inteira, que o Caixa também usa); o gerente lê pela
 * RPC. Pura de propósito: devolve a FUNÇÃO, e por isso o teste consegue
 * perguntar "por qual porta o gerente entra?" sem banco e sem tela.
 */
export function lerVendas(
  verTodas: boolean, verFaturamento: boolean,
): (f: { de: string; ate: string; vendedorId?: string | null; incluirCanceladas?: boolean })
  => Promise<VendaDetalhada[]> {
  return verTodas && !verFaturamento ? listarVendasParaGerencia : listarVendas;
}


// --- Devoluções: troca e estorno (migração 033) --------------------------------

export interface DevolucaoDaVenda extends Devolucao {
  itens: DevolucaoItem[];
  /** O código do vale, quando a devolução virou troca. */
  codigo_vale: string | null;
  /**
   * Quanto desta devolução abateu o fiado da venda (065). Quando é o valor
   * inteiro, não houve vale nem estorno — a dívida comeu tudo, e o histórico
   * precisa dizer isso em vez de mostrar "Troca (vale )" sem código.
   */
  abatido_no_fiado: number;
}

/** As devoluções de uma venda, com itens e o código do vale. Gerência (RLS). */
export async function devolucoesDaVenda(saleId: string): Promise<DevolucaoDaVenda[]> {
  const linhas = ou(
    await supabase
      .from('devolucoes')
      // O abatimento no fiado (065) vem pelo relacionamento
      // contas_receber_abatimentos.devolucao_id → devolucoes.id. É uma lista
      // porque o PostgREST não sabe que há no máximo um por devolução; a soma
      // abaixo resolve sem depender disso.
      .select('*, devolucao_itens(*), store_credits(codigo), contas_receber_abatimentos(valor)')
      .eq('sale_id', saleId)
      .order('created_at', { ascending: false }),
  ) as unknown as Array<Devolucao & {
    devolucao_itens: DevolucaoItem[] | null;
    store_credits: { codigo: string } | Array<{ codigo: string }> | null;
    contas_receber_abatimentos: Array<{ valor: number }> | null;
  }> | null;

  return (linhas ?? []).map((
    { devolucao_itens, store_credits, contas_receber_abatimentos, ...d },
  ) => {
    const vale = Array.isArray(store_credits) ? store_credits[0] : store_credits;
    return {
      ...d,
      itens: devolucao_itens ?? [],
      codigo_vale: vale?.codigo ?? null,
      abatido_no_fiado: (contas_receber_abatimentos ?? [])
        .reduce((soma, a) => soma + Number(a.valor), 0),
    };
  });
}

export async function registrarDevolucao(p: {
  saleId: string;
  itens: Array<{ sale_item_id: string; quantidade: number }>;
  modo: 'estorno' | 'vale';
  motivo: string;
  forma?: PaymentMethod | null;
}): Promise<RegistrarDevolucaoRow> {
  const linhas = ou(await supabase.rpc('registrar_devolucao', {
    p_sale_id: p.saleId,
    p_itens: p.itens,
    p_modo: p.modo,
    p_motivo: p.motivo,
    p_forma: p.forma ?? null,
  })) as RegistrarDevolucaoRow[];
  const primeira = linhas?.[0];
  if (!primeira) throw new Error('A devolução não devolveu o registro.');
  return primeira;
}

/**
 * Corrige a DIGITAÇÃO de uma venda (gerente/dono): cliente, observação e forma
 * de pagamento. Itens e valores não passam por aqui — a RPC recusa mudar o
 * lastro; para isso existem devolução e cancelamento. O caixa acompanha a
 * troca de forma dentro da mesma transação.
 */
export async function editarVenda(p: {
  saleId: string;
  cliente?: string | null;
  customerId?: string | null;
  observacao?: string | null;
  forma?: PaymentMethod | null;
  /** 073 — o carro. Vem TODO junto, como os outros campos desta RPC: o que não
   *  for enviado é gravado como nulo, não preservado. */
  veiculoMontadora?: string | null;
  veiculoModelo?: string | null;
  veiculoAno?: number | null;
  veiculoId?: string | null;
}): Promise<void> {
  const { error } = await supabase.rpc('editar_venda', {
    p_sale_id: p.saleId,
    p_cliente: p.cliente ?? null,
    p_customer_id: p.customerId ?? null,
    p_observacao: p.observacao ?? null,
    p_forma: p.forma ?? null,
    p_veiculo_montadora: p.veiculoMontadora ?? null,
    p_veiculo_modelo: p.veiculoModelo ?? null,
    p_veiculo_ano: p.veiculoAno ?? null,
    p_customer_vehicle_id: p.veiculoId ?? null,
  });
  if (error) throw error;
}

/**
 * Reescreve o CONTEÚDO da venda (070) — peças, preços, desconto, forma. Só o
 * dono; o banco nega os outros pelo corpo da RPC.
 *
 * A venda mantém número e código: o recibo que o cliente levou continua
 * achando esta venda. A versão anterior vai inteira para o histórico, e o
 * caixa recebe só a DIFERENÇA, lançada hoje.
 *
 * `itens` é a lista NOVA inteira, não um delta: o que não vier some da venda.
 */
export async function corrigirVenda(p: {
  saleId: string;
  itens: Array<{ product_id: string; quantidade: number; preco_unit: number; descricao?: string | null }>;
  motivo: string;
  desconto?: number;
  forma?: PaymentMethod | null;
  cliente?: string | null;
  observacao?: string | null;
  entregaTaxa?: number | null;
}): Promise<void> {
  const { error } = await supabase.rpc('corrigir_venda', {
    p_sale_id: p.saleId,
    p_itens: p.itens,
    p_motivo: p.motivo,
    p_desconto: p.desconto ?? 0,
    p_forma: p.forma ?? null,
    p_cliente: p.cliente ?? null,
    p_observacao: p.observacao ?? null,
    p_entrega_taxa: p.entregaTaxa ?? null,
  });
  if (error) throw error;
}

export interface VersaoDaVenda {
  versao: number;
  total: number;
  desconto: number;
  entrega_taxa: number;
  forma_pagamento: PaymentMethod | null;
  cliente: string | null;
  observacao: string | null;
  itens: Array<{
    product_id: string;
    descricao: string | null;
    quantidade: number;
    preco_unit: number;
    custo_unit: number | null;
  }>;
  corrigida_em: string;
  corrigida_por_nome: string | null;
  motivo: string;
}

/** O histórico de correções de uma venda (070), da mais recente para trás. */
export async function versoesDaVenda(saleId: string): Promise<VersaoDaVenda[]> {
  return (ou(await supabase.rpc('versoes_da_venda', { p_sale_id: saleId })) ?? []) as VersaoDaVenda[];
}

export async function cancelarVenda(saleId: string, motivo: string): Promise<void> {
  const { error } = await supabase.rpc('cancelar_venda', {
    p_sale_id: saleId,
    p_motivo: motivo,
  });
  if (error) throw error;
}

// --- Orçamentos (documento p/ cliente; RPCs em core/sql/functions/orcamento.sql) ---

export async function emitirOrcamento(p: {
  itens: ItemVenda[];
  desconto?: number;
  clienteNome?: string | null;
  clienteContato?: string | null;
  validoAte?: string | null;   // 'YYYY-MM-DD'; null = o banco aplica 7 dias
  observacao?: string | null;
  /** 049 — de onde veio a cotação. */
  canal?: CanalOrcamento;
  /** 049 — o carro como o cliente falou. */
  veiculo?: string | null;
  /** 051 — o cliente cadastrado e qual carro dele a cotação atende. */
  customerId?: string | null;
  customerVehicleId?: string | null;
}): Promise<OrcamentoEmitidoRow> {
  const linhas = ou(
    await supabase.rpc('emitir_orcamento', {
      p_itens: p.itens,
      p_desconto: p.desconto ?? 0,
      p_cliente_nome: p.clienteNome ?? null,
      p_cliente_contato: p.clienteContato ?? null,
      p_valido_ate: p.validoAte ?? null,
      p_observacao: p.observacao ?? null,
      p_canal: p.canal ?? 'balcao',
      p_veiculo: p.veiculo ?? null,
      p_customer_id: p.customerId ?? null,
      p_customer_vehicle_id: p.customerVehicleId ?? null,
    }),
  ) as OrcamentoEmitidoRow[] | null;

  const emitido = linhas?.[0];
  if (!emitido) throw new Error('O banco não devolveu o orçamento emitido.');
  return emitido;
}

/** O orçamento com as peças penduradas — para o filtro da lista (ver ItemNaLista). */
export type QuoteNaLista = Quote & { quote_items: ItemNaLista[] };

export async function listarOrcamentos(f: { de: string; ate: string }): Promise<QuoteNaLista[]> {
  return ou(
    await supabase
      .from('quotes')
      .select('*, quote_items(descricao, sku, codigo_avulso)')
      .gte('created_at', f.de)
      .lt('created_at', f.ate)
      .order('created_at', { ascending: false })
      .limit(500),
  ) ?? [];
}

/** O item do orçamento com o cadastro da peça pendurado — só os códigos, e só
 *  para a tela da loja (o papel do cliente não leva nenhum deles). `produto` é
 *  nulo quando a peça saiu do catálogo: o documento continua valendo pelo que
 *  congelou na emissão. */
export interface ItemDoOrcamento extends QuoteItem {
  products?: CodigosDaPeca | CodigosDaPeca[] | null;
  produto?: CodigosDaPeca | null;
}

export interface CodigosDaPeca {
  sku: string | null;
  codigo_fabrica: string | null;
  codigo_oem: string | null;
}

export interface OrcamentoCompleto extends Quote { itens: ItemDoOrcamento[] }

export async function obterOrcamento(id: string): Promise<OrcamentoCompleto> {
  // O CADASTRO VEM JUNTO (10/09/2026) só pelos códigos: a tela mostra à equipe
  // o código do fabricante, que é o que se usa para pedir a peça ao fornecedor
  // — o `sku` congelado é o código da LOJA e não serve para comprar. Colunas
  // nomeadas, nunca `products(*)`: custo e preço de e-commerce são revogados de
  // authenticated e derrubariam a consulta inteira com 42501.
  const dados = ou(
    await supabase
      .from('quotes')
      .select('*, quote_items(*, products(sku, codigo_fabrica, codigo_oem))')
      .eq('id', id)
      .single(),
  ) as Quote & { quote_items: ItemDoOrcamento[] | null };

  const { quote_items, ...orcamento } = dados;
  return {
    ...orcamento,
    itens: (quote_items ?? [])
      .map((i) => ({
        ...i,
        // O embed do PostgREST devolve objeto OU array conforme a cardinalidade
        // que ele infere; normalizar aqui evita `produto?.[0]` espalhado na tela.
        produto: Array.isArray(i.products) ? i.products[0] ?? null : i.products ?? null,
      }))
      .sort((a, b) => a.ordem - b.ordem),
  };
}

/** Converte no preço PROMETIDO (validade em dia); a RPC cria a venda inteira
 *  numa transação — estoque, caixa e evento saem de registrar_venda. */
export async function converterOrcamento(p: {
  quoteId: string;
  forma: PaymentMethod;
  aVerId?: string | null;
}): Promise<string> {
  return ou(
    await supabase.rpc('converter_orcamento', {
      p_quote_id: p.quoteId,
      p_forma: p.forma,
      p_a_ver_id: p.aVerId ?? null,
    }),
  );
}

/** O ranking de venda por pessoa (v_vendas_por_vendedor). SÓ DONO desde
 *  28/09/2026 — é o "quanto cada um vende" que o dono mandou tirar da equipe.
 *  Quem chama passou a pedir só quando pode: Metas (tela do dono) e o sino
 *  dele. A view é recortada pela RLS por baixo, do mesmo jeito. */
export async function vendasPorVendedor(competencia?: string): Promise<VendasPorVendedorRow[]> {
  let q = supabase.from('v_vendas_por_vendedor').select('*')
    .order('total_vendido', { ascending: false });
  if (competencia) q = q.eq('competencia', competencia);
  return ou(await q) ?? [];
}

// --- "A ver" — crédito de loja (tela 14) -------------------------------------

/** Vale com o NOME de quem o emitiu, para o documento impresso identificar
 *  quem assina pela loja. O embed depende da policy de profiles (gerente/dono
 *  leem todos) — e só gerente/dono chegam nesta tela. Se vier vazio, o
 *  documento cai no rótulo genérico em vez de quebrar. */
export interface AVerComEmissor extends StoreCredit {
  emitido_por_nome: string | null;
  /** Nome ATUAL do cliente cadastrado, quando há vínculo (migração 024). Serve
   *  à tela; o documento continua imprimindo `cliente_nome`, o congelado. */
  cliente_cadastro_nome: string | null;
}

export async function listarAVer(): Promise<AVerComEmissor[]> {
  const linhas = ou(
    await supabase
      .from('store_credits')
      .select('*, profiles!store_credits_emitido_por_fkey(nome), customers(nome)')
      .order('created_at', { ascending: false })
      .limit(200),
  ) as unknown as Array<StoreCredit & {
    profiles: { nome: string } | Array<{ nome: string }> | null;
    customers: { nome: string } | Array<{ nome: string }> | null;
  }> | null;

  return (linhas ?? []).map(({ profiles, customers, ...c }) => {
    const p = Array.isArray(profiles) ? profiles[0] : profiles;
    const cl = Array.isArray(customers) ? customers[0] : customers;
    return { ...c, emitido_por_nome: p?.nome ?? null, cliente_cadastro_nome: cl?.nome ?? null };
  });
}

/** Busca por código (AV-000123) ou trecho do nome do cliente. */
export async function acharAVer(termo: string): Promise<StoreCredit[]> {
  const t = termo.trim();
  if (!t) return [];
  const porCodigo = /^av-?\d+$/i.test(t)
    ? `AV-${t.replace(/\D/g, '').padStart(6, '0')}`
    : null;
  let q = supabase.from('store_credits').select('*').limit(10);
  q = porCodigo ? q.eq('codigo', porCodigo) : q.ilike('cliente_nome', `%${t}%`);
  return ou(await q) ?? [];
}

export async function emitirAVer(p: {
  cliente_nome: string; valor: number; cliente_doc?: string | null;
  motivo?: string | null; product_id?: string | null; valido_ate?: string | null;
  /** De onde vem o crédito (058) — obrigatória na RPC. Encomenda não é opção:
   *  o caminho dela é a aba Encomendas. */
  origem: OrigemAVer;
  /** Data do FATO ('YYYY-MM-DD'), para transcrever vale de papel (falta de luz,
   *  passivo antigo). Vazio = agora. A RPC recusa data futura. */
  emitido_em?: string | null;
  /** Cliente cadastrado, quando houver (migração 024). O nome continua indo
   *  junto e congelado — é ele que sai no papel assinado. */
  customer_id?: string | null;
}): Promise<EmitirAVerRow> {
  const linhas = ou(await supabase.rpc('emitir_a_ver', {
    p_cliente_nome: p.cliente_nome,
    p_valor: p.valor,
    p_cliente_doc: p.cliente_doc ?? null,
    p_motivo: p.motivo ?? null,
    p_origem: p.origem,
    p_product_id: p.product_id ?? null,
    p_valido_ate: p.valido_ate ?? null,
    p_emitido_em: p.emitido_em || null,
    p_customer_id: p.customer_id ?? null,
  })) as EmitirAVerRow[];
  const primeiro = linhas?.[0];
  if (!primeiro) throw new Error('A emissão não devolveu o código.');
  return primeiro;
}

/**
 * Editar um vale emitido (decisão do dono, 11/08/2026: tudo editável).
 * A trava aritmética mora na RPC: o valor não pode ficar menor que o que já
 * foi usado — emitiu 100, usou 50, o piso do valor é 50. O saldo é recalculado
 * lá (valor novo − usado), nunca digitado.
 */
export async function editarAVer(id: string, p: {
  cliente_nome: string; valor: number; cliente_doc?: string | null;
  motivo?: string | null; valido_ate?: string | null; customer_id?: string | null;
}): Promise<void> {
  const { error } = await supabase.rpc('editar_a_ver', {
    p_credit_id: id,
    p_cliente_nome: p.cliente_nome,
    p_valor: p.valor,
    p_cliente_doc: p.cliente_doc ?? null,
    p_motivo: p.motivo ?? null,
    p_valido_ate: p.valido_ate || null,
    p_customer_id: p.customer_id ?? null,
  });
  if (error) throw error;
}

// --- Carrinho de balcão (migração 026) ----------------------------------------
// A peça no carrinho vira reserva de 30 min, visível aos colegas. A recusa por
// disputa ("no carrinho de Luiz") chega como exceção da RPC, já em português.

export async function segurarCarrinho(
  productId: string, quantidade: number, clienteNome?: string | null,
): Promise<ISODate> {
  return ou(await supabase.rpc('segurar_carrinho', {
    p_product_id: productId,
    p_quantidade: quantidade,
    p_cliente_nome: clienteNome ?? null,
  })) as ISODate;
}

/** Sem argumento: esvazia o carrinho inteiro. */
export async function soltarCarrinho(productId?: string): Promise<void> {
  const { error } = await supabase.rpc('soltar_carrinho', {
    p_product_id: productId ?? null,
  });
  if (error) throw error;
}

export async function renovarCarrinho(clienteNome?: string | null): Promise<ISODate> {
  return ou(await supabase.rpc('renovar_carrinho', {
    p_cliente_nome: clienteNome ?? null,
  })) as ISODate;
}

/** O que EU tinha reservado — é o que reconstrói o carrinho depois de um F5. */
export async function meuCarrinho(): Promise<MeuCarrinhoRow[]> {
  return (ou(await supabase.rpc('meu_carrinho')) as MeuCarrinhoRow[] | null) ?? [];
}

/** Em lote, pelos mesmos motivos de fornecedoresDePecas: a busca redigita a
 *  cada 250 ms e uma chamada por resultado seria uma rajada por tecla. */
export async function carrinhosDasPecas(productIds: string[]): Promise<CarrinhoDaPecaRow[]> {
  if (productIds.length === 0) return [];
  return (ou(await supabase.rpc('carrinhos_das_pecas', {
    p_product_ids: productIds,
  })) as CarrinhoDaPecaRow[] | null) ?? [];
}

export async function cancelarAVer(id: string, motivo: string): Promise<void> {
  const { error } = await supabase.rpc('cancelar_a_ver', { p_credit_id: id, p_motivo: motivo });
  if (error) throw error;
}

export async function usosDoAVer(creditId: string): Promise<StoreCreditUse[]> {
  return ou(
    await supabase.from('store_credit_uses').select('*')
      .eq('credit_id', creditId).order('created_at', { ascending: false }),
  ) ?? [];
}

// --- Busca guiada e placa (tela 2, modos novos) -------------------------------

export async function opcoesBuscaGuiada(f: {
  montadora?: string | null; modelo?: string | null; ano?: number | null;
}): Promise<OpcoesBuscaGuiada> {
  const bruto = ou(await supabase.rpc('opcoes_busca_guiada', {
    p_montadora: f.montadora ?? null,
    p_modelo: f.modelo ?? null,
    p_ano: f.ano ?? null,
  })) as Partial<OpcoesBuscaGuiada> | null;
  return {
    montadoras: bruto?.montadoras ?? [],
    modelos: bruto?.modelos ?? [],
    ano_min: bruto?.ano_min ?? null,
    ano_max: bruto?.ano_max ?? null,
    motorizacoes: bruto?.motorizacoes ?? [],
    categorias: bruto?.categorias ?? [],
    // Chaves da v2. O `?? []` não é cerimônia: enquanto o banco de uma máquina
    // estiver sem a função nova, a tela cai no comportamento antigo (só o que
    // a loja tem) em vez de quebrar com "undefined is not iterable".
    montadoras_catalogo: bruto?.montadoras_catalogo ?? [],
    modelos_catalogo: bruto?.modelos_catalogo ?? [],
  };
}

export async function buscarPorVeiculo(f: {
  montadora: string; modelo: string; ano?: number | null;
  motorizacao?: string | null; categoria?: string | null;
}): Promise<BuscarPecasRow[]> {
  return ou(await supabase.rpc('buscar_por_veiculo', {
    p_montadora: f.montadora,
    p_modelo: f.modelo,
    p_ano: f.ano ?? null,
    p_motorizacao: f.motorizacao ?? null,
    p_categoria: f.categoria ?? null,
  })) ?? [];
}

export async function acharPlaca(placaNormalizada: string): Promise<VehiclePlate | null> {
  return ou(
    await supabase.from('vehicle_plates').select('*')
      .eq('placa', placaNormalizada).maybeSingle(),
  );
}

/**
 * De onde veio a resposta da placa.
 * - base_propria: a loja já conhecia (inclusive na grafia antiga/Mercosul).
 * - api: consulta externa paga — palpite do órgão, para confirmar no balcão.
 * - nao_encontrada: a consulta externa não conhece a placa.
 * - teto_diario: limite do dia (nosso) ou do plano (do fornecedor).
 * - indisponivel: sem API configurada, ou ela não respondeu.
 */
export type FonteDaPlaca = 'base_propria' | 'api' | 'nao_encontrada' | 'teto_diario' | 'indisponivel';

/**
 * Consulta a placa: base da loja primeiro, API externa só se não conhecermos.
 *
 * Quem faz as duas coisas é o AGENTE da VPS (rota /infra/v1/placa, 17/09/2026),
 * que guarda o token da apiplacas fora do navegador e protege os créditos pagos
 * (teto diário, placa gêmea, repetição) — ver infra/agente/placa.mjs. Antes era
 * a Edge Function `placa-consulta`, que morreu no cutover sem ninguém notar: o
 * Supabase recusava o token da VPS, o erro caía no catch abaixo, e a tela
 * seguia funcionando só com a base da loja.
 *
 * O catch continua, pela mesma razão de antes: a consulta por placa nunca pode
 * parar por causa da parte opcional. Agente fora do ar vira leitura direta da
 * tabela, que a RLS já permite a qualquer perfil da equipe.
 *
 * `tratamento` (18/09/2026) é a leitura que o sistema faz do que a consulta
 * externa disse — quais versões FIPE o documento não descarta. Vem do agente,
 * que é quem decide; na leitura direta da tabela ele não existe, e a tela
 * mostra o que a consulta disse sem marcar versão nenhuma.
 */
export async function consultarPlaca(placaNormalizada: string): Promise<RespostaDaPlaca> {
  try {
    const { data } = await supabase.auth.getSession();
    const token = data.session?.access_token;
    if (!token) throw new Error('Sessão expirada.');
    const base = import.meta.env.VITE_SUPABASE_URL as string;
    const r = await fetch(`${base}/infra/v1/placa`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ placa: placaNormalizada }),
    });
    if (!r.ok) throw new Error(`O agente respondeu ${r.status}.`);
    return await r.json() as RespostaDaPlaca;
  } catch {
    const veiculo = await acharPlaca(placaNormalizada);
    return { fonte: veiculo ? 'base_propria' : 'indisponivel', veiculo, tratamento: null };
  }
}

export interface RespostaDaPlaca {
  fonte: FonteDaPlaca;
  veiculo: VehiclePlate | null;
  aviso?: string;
  tratamento?: TratamentoDaPlaca | null;
}

/**
 * Salva/atualiza o vínculo placa → veículo. É assim que a base própria cresce.
 *
 * `origem: 'balcao'` vai EXPLÍCITO: salvar é alguém da loja dizendo "conferi o
 * carro". Sem isso, confirmar uma placa que veio da API manteria a origem 'api'
 * (o upsert só troca as colunas enviadas), e o aviso de "confira com o cliente"
 * voltaria para sempre sobre um dado já conferido.
 */
export async function salvarPlaca(p: {
  placa: string; montadora: string; modelo: string;
  ano?: number | null; motorizacao?: string | null;
}): Promise<void> {
  const { data: sessao } = await supabase.auth.getUser();
  const { error } = await supabase.from('vehicle_plates').upsert({
    placa: p.placa,
    montadora: p.montadora,
    modelo: p.modelo,
    ano: p.ano ?? null,
    motorizacao: p.motorizacao ?? null,
    origem: 'balcao',
    criado_por: sessao.user?.id ?? null,
  }, { onConflict: 'placa' });
  if (error) throw error;
}

// --- Encomendas (migração 020) ------------------------------------------------

/** O cadastro embutido na peça da encomenda. O preço vem junto para a tela
 *  SUGERIR o valor de cada linha (056/066). */
export type PecaDaEncomenda = {
  sku: string;
  descricao: string;
  preco_balcao: number | null;
  /** Os códigos do cadastro (10/09/2026) — o que a equipe usa para pedir a peça
   *  ao fornecedor. Não vão para o papel do cliente. */
  codigo_fabrica?: string | null;
  codigo_oem?: string | null;
};

/** Uma peça da encomenda (066), com o cadastro já normalizado. */
export interface EncomendaItem extends ReservationItem {
  /** Embed de products (o PostgREST devolve objeto ou array). Nulo = peça
   *  excluída do catálogo; a linha continua legível pelo que foi combinado. */
  peca: PecaDaEncomenda | null;
}

export interface EncomendaLinha extends Reservation {
  /** As peças do pedido (066), na ordem em que entraram no balcão. */
  itens: EncomendaItem[];
}

/**
 * As encomendas que a RLS deixar ver: todas, para qualquer perfil ativo — mexer
 * é que continua sendo de quem separou (ou da gerência). A tela não filtra por
 * papel; o banco já entrega o recorte.
 */
export async function listarEncomendas(limite = 300): Promise<EncomendaLinha[]> {
  const linhas = ou(
    await supabase
      .from('reservations')
      // Os códigos vêm junto (10/09/2026): é o que a equipe usa para pedir a
      // peça ao fornecedor. Colunas nomeadas — `products(*)` traria custo, que
      // é revogado de authenticated e derrubaria a consulta com 42501.
      .select('*, reservation_items(*, products(sku, descricao, preco_balcao, codigo_fabrica, codigo_oem))')
      // MAIS RECENTE PRIMEIRO, casando com o padrão da tela (04/09/2026). A
      // ordem daqui não é cosmética: com `limite`, é ela que decide QUAIS
      // linhas chegam. Ordenado por `expira_em` como antes, a encomenda aberta
      // hoje — prazo mais longo, portanto no fim da fila — seria a primeira a
      // ficar de fora do corte, e o sintoma seria o pior de diagnosticar:
      // "abri a encomenda e ela não aparece". Cortar as MAIS ANTIGAS é o
      // comportamento que a tela promete.
      .order('created_at', { ascending: false })
      .limit(limite),
  ) as unknown as Array<Reservation & {
    reservation_items: Array<ReservationItem & {
      products: PecaDaEncomenda | PecaDaEncomenda[] | null;
    }> | null;
  }> | null;

  return (linhas ?? []).map(({ reservation_items, ...r }) => ({
    ...r,
    // O embed do PostgREST não promete ordem, e a ordem IMPORTA aqui: é a que
    // o cliente vai ler no papel e a que a venda vai registrar. `id` desempata
    // porque as peças de uma encomenda nova nascem no mesmo instante.
    itens: (reservation_items ?? [])
      .map(({ products, ...i }) => ({
        ...i,
        peca: Array.isArray(products) ? products[0] ?? null : products,
      }))
      .sort((a, b) => a.created_at.localeCompare(b.created_at) || a.id.localeCompare(b.id)),
  }));
}

/** O recado da encomenda (050). `para` nulo = todos da loja. */
export async function avisarEncomenda(p: {
  id: string; para?: string | null; texto: string;
}): Promise<void> {
  const { error } = await supabase.rpc('avisar_encomenda', {
    p_id: p.id, p_para: p.para ?? null, p_texto: p.texto,
  });
  if (error) throw error;
}

// --- O dinheiro da encomenda (056) -------------------------------------------
// Cada pagamento LANÇA no caixa na hora (foi o que a 054 não fazia); a soma
// deles é o que diz se a encomenda está paga — não existe coluna dizendo isso.

/** Os pagamentos das encomendas visíveis, para a tela somar por encomenda. */
export async function pagamentosDeEncomendas(
  ids: string[],
): Promise<ReservationPayment[]> {
  if (ids.length === 0) return [];
  return ou(await supabase
    .from('reservation_payments')
    .select('*')
    .in('reservation_id', ids)
    .order('recebido_em', { ascending: true })) as ReservationPayment[] ?? [];
}

export async function registrarPagamentoEncomenda(p: {
  id: string; valor: number; forma?: PaymentMethod; observacao?: string | null;
}): Promise<string> {
  return ou(await supabase.rpc('registrar_pagamento_encomenda', {
    p_id: p.id, p_valor: p.valor, p_forma: p.forma ?? 'dinheiro',
    p_observacao: p.observacao ?? null,
  }));
}

export async function estornarPagamentoEncomenda(
  pagamentoId: string, motivo?: string | null,
): Promise<void> {
  const { error } = await supabase.rpc('estornar_pagamento_encomenda', {
    p_pagamento_id: pagamentoId, p_motivo: motivo ?? null,
  });
  if (error) throw error;
}

/** Fecha a encomenda quitada como venda. Devolve o id da venda. */
export async function converterEncomendaEmVenda(id: string): Promise<string> {
  return ou(await supabase.rpc('converter_encomenda_em_venda', { p_id: id }));
}

/** Acerta o preço de balcão a partir da Venda (gerência) — o catálogo estava
 *  velho e quem descobriu foi o balcão. */
export async function atualizarPrecoBalcao(
  productId: string, novoPreco: number,
): Promise<void> {
  const { error } = await supabase.rpc('atualizar_preco_balcao', {
    p_product_id: productId, p_novo_preco: novoPreco, p_origem: 'venda',
  });
  if (error) throw error;
}

export async function limparAvisoEncomenda(id: string): Promise<void> {
  const { error } = await supabase.rpc('limpar_aviso_encomenda', { p_id: id });
  if (error) throw error;
}

/** Os recados ATIVOS que chegam a mim — para o sino. A RLS já recorta (só
 *  encomenda com recado, e só se for para mim ou para todos); o filtro daqui
 *  é para eu não ser avisado do MEU próprio recado. */
export async function avisosDeEncomenda(meuId: string): Promise<Array<{
  id: string; cliente_nome: string; aviso_texto: string | null;
  aviso_para: string | null; aviso_de: string | null; aviso_de_nome: string | null;
}>> {
  const linhas = ou(await supabase.from('reservations')
    .select('id, cliente_nome, aviso_texto, aviso_para, aviso_de, aviso_de_nome')
    .not('aviso_em', 'is', null)
    .eq('status', 'ativa')
    .limit(50)) ?? [];
  return (linhas as Array<{
    id: string; cliente_nome: string; aviso_texto: string | null;
    aviso_para: string | null; aviso_de: string | null; aviso_de_nome: string | null;
  }>).filter((l) => l.aviso_de !== meuId);
}

/** Uma peça a encomendar. `valor` é o combinado DA LINHA (não o unitário) e
 *  `descricao` é obrigatória na peça diversa — o banco recusa sem ela. */
export interface ItemNovoDeEncomenda {
  productId: string;
  quantidade: number;
  valor: number | null;
  descricao?: string | null;
  /** PEÇA DIVERSA (071): o código, de uso interno. Nunca vai ao papel que o
   *  cliente leva para retirar a peça. */
  codigo?: string | null;
}

/**
 * Abre a encomenda inteira: pedido + peças + sinal, numa transação só (066).
 *
 * Era um `insert` direto até a encomenda ser UMA peça. Com lista, três escritas
 * sem transação deixavam o pior estado possível numa queda de rede — pedido com
 * metade das peças, ou sinal cobrado do cliente sem peça anotada.
 */
export async function criarEncomenda(p: {
  itens: ItemNovoDeEncomenda[];
  clienteNome: string;
  clienteContato?: string | null; observacao?: string | null; expiraEm: string;
  /** Sinal deixado na hora — lança no caixa (056). */
  sinal?: number | null;
  sinalForma?: PaymentMethod;
  /** O cliente CADASTRADO (067), quando o balcão atrelou um. Sem nome digitado,
   *  o banco congela o do cadastro — e é ele que segue para a venda depois. */
  customerId?: string | null;
}): Promise<string> {
  return ou(await supabase.rpc('criar_encomenda', {
    p_cliente_nome: p.clienteNome.trim(),
    p_expira_em: p.expiraEm,
    p_itens: p.itens.map((i) => ({
      product_id: i.productId,
      quantidade: i.quantidade,
      valor: i.valor,
      descricao: i.descricao?.trim() || null,
    })),
    p_cliente_contato: p.clienteContato?.trim() || null,
    p_observacao: p.observacao?.trim() || null,
    p_sinal: p.sinal && p.sinal > 0 ? p.sinal : null,
    p_sinal_forma: p.sinalForma ?? 'dinheiro',
    p_customer_id: p.customerId ?? null,
  }));
}

// --- As peças de uma encomenda JÁ ABERTA (066) -------------------------------
// Aqui não há RPC: é update de tabela com a RLS decidindo, como o prazo e o
// status sempre foram. Quem mexe é quem separou (ou a gerência), e só enquanto
// a encomenda está aberta — depois de virar venda, a policy recusa (§14.1).

export async function adicionarItemEncomenda(p: {
  encomendaId: string;
} & ItemNovoDeEncomenda): Promise<void> {
  const { error } = await supabase.from('reservation_items').insert({
    reservation_id: p.encomendaId,
    product_id: p.productId,
    quantidade: p.quantidade,
    valor: p.valor,
    descricao_avulsa: p.descricao?.trim() || null,
    codigo_avulso: p.codigo?.trim() || null,
  });
  if (error) throw error;
}

export async function atualizarItemEncomenda(
  id: string,
  campos: Partial<Pick<ReservationItem, 'quantidade' | 'valor' | 'descricao_avulsa' | 'codigo_avulso'>>,
): Promise<void> {
  const { error } = await supabase.from('reservation_items').update(campos).eq('id', id);
  if (error) throw error;
}

/** Tirar uma linha do pedido aberto é o mesmo gesto de tirar item do carrinho —
 *  e por isso apaga de verdade, ao contrário da encomenda, que se cancela. */
export async function removerItemEncomenda(id: string): Promise<void> {
  const { error } = await supabase.from('reservation_items').delete().eq('id', id);
  if (error) throw error;
}

/** Editar/atender/cancelar. A RLS decide o alcance: o vendedor só nas dele. */
export async function atualizarEncomenda(
  id: string,
  campos: Partial<Pick<Reservation,
    'cliente_nome' | 'cliente_contato' | 'observacao' | 'expira_em' | 'status'>>,
): Promise<void> {
  const { error } = await supabase.from('reservations').update(campos).eq('id', id);
  if (error) throw error;
}

/**
 * Corrigir as datas da encomenda — SÓ O DONO (01/09/2026).
 *
 * Não é um update como o de cima, e por isso é RPC: `created_at` é carimbo, não
 * campo, e o banco precisa recusar a data que faria a encomenda nascer depois
 * do pagamento que ela já recebeu. O rastro de quem mudou o quê vai em `events`.
 *
 * Data nula = "não mexe nesta" — dá para corrigir só o prazo, só a abertura, ou
 * as duas de uma vez, sem a tela reenviar o que não mudou.
 */
export async function corrigirDatasDaEncomenda(p: {
  id: string;
  expiraEm?: string | null;
  criadaEm?: string | null;
  motivo?: string | null;
}): Promise<void> {
  const { error } = await supabase.rpc('corrigir_datas_da_encomenda', {
    p_id: p.id,
    p_expira_em: p.expiraEm ?? null,
    p_criada_em: p.criadaEm ?? null,
    p_motivo: p.motivo?.trim() || null,
  });
  if (error) throw error;
}

/**
 * Troca quem separou a encomenda (18/09/2026) — SÓ O DONO, e quem nega é o
 * banco (exige_papel em trocar_vendedor_da_encomenda). Muda a FK e o nome
 * congelado juntos; quem recebeu os pagamentos e a venda já registrada ficam.
 */
export async function trocarVendedorDaEncomenda(p: {
  id: string;
  vendedorId: string;
  motivo?: string | null;
}): Promise<void> {
  const { error } = await supabase.rpc('trocar_vendedor_da_encomenda', {
    p_id: p.id,
    p_vendedor_id: p.vendedorId,
    p_motivo: p.motivo?.trim() || null,
  });
  if (error) throw error;
}

/**
 * Passa as vendas da lista para outra pessoa da equipe (01/10/2026) — SÓ O
 * DONO, e quem nega é o banco (exige_papel em trocar_vendedor_das_vendas).
 * Muda a FK e o nome congelado juntos; peças, total, pagamento, estoque e caixa
 * ficam. Devolve quantas mudaram (a que já era da pessoa nova não conta).
 */
export async function trocarVendedorDasVendas(p: {
  vendas: string[];
  vendedorId: string;
  motivo?: string | null;
}): Promise<number> {
  const n = ou(await supabase.rpc('trocar_vendedor_das_vendas', {
    p_vendas: p.vendas,
    p_vendedor_id: p.vendedorId,
    p_motivo: p.motivo?.trim() || null,
  }));
  return Number(n ?? 0);
}

// --- O catálogo navegável (migração 092, ADR-009) ---------------------------
// "Calotas → Fiat → Palio" e "Fiat → Palio → óleo". As duas RPCs são invoker:
// a RLS do catálogo decide o que volta, e o parceiro B2B recebe vazio.

/** O próximo nível da navegação: tipos → montadoras → modelos, ou montadoras → modelos → tipos. */
export async function navegarCatalogo(p: {
  tipo?: string | null;
  montadora?: string | null;
  modelo?: string | null;
}): Promise<NavegacaoDoCatalogo> {
  return ou(await supabase.rpc('navegar_catalogo', {
    p_tipo: p.tipo ?? null,
    p_montadora: p.montadora ?? null,
    p_modelo: p.modelo ?? null,
  })) as NavegacaoDoCatalogo;
}

/** As peças de um tipo para um carro e, se o tipo é por especificação, o que o carro pede. */
export async function pecasParaOCarro(p: {
  tipo: string;
  modelo?: string | null;
  ano?: number | null;
}): Promise<PecasParaOCarro> {
  const r = ou(await supabase.rpc('pecas_para_o_carro', {
    p_tipo: p.tipo,
    p_modelo: p.modelo ?? null,
    p_ano: p.ano ?? null,
  })) as PecasParaOCarro | null;
  return r ?? { modo: null, especificacoes: [], pecas: [] };
}

/** Os tipos de peça, em ordem — para o cadastro escolher. */
export async function tiposDePeca(): Promise<PartType[]> {
  return ou(await supabase.from('part_types').select('*').order('ordem').order('nome')) ?? [];
}

/** Total prometido de UMA peça (todas as encomendas ativas, de todos), sem
 *  expor cliente nem vendedor — RPC definer, ver encomendas.sql. */
export async function reservadoDaPeca(productId: string): Promise<number> {
  const n = ou(await supabase.rpc('reservado_da_peca', { p_product_id: productId }));
  return Number(n ?? 0);
}

// --- Fila de impressão (migração 022) ----------------------------------------

/**
 * Manda o documento para a fila. Quem entrega é o agente da VPS — por isso a
 * tela não sabe (nem precisa saber) qual é a impressora.
 *
 * Isto NÃO substitui a janela de impressão do navegador: as duas convivem de
 * propósito. A janela serve ao computador que TEM a impressora instalada e
 * imprime na hora; a fila serve a todos os outros, que é o caso da loja. Quem
 * emite dispara as duas e fica com a que funcionar.
 */
export async function enfileirarImpressao(p: {
  titulo: string;
  documentoHtml: string;
  tipo?: PrintJob['tipo'];
  refTable?: string | null;
  refId?: string | null;
}): Promise<string> {
  return ou(await supabase.rpc('enfileirar_impressao', {
    p_tipo: p.tipo ?? 'a_ver',
    p_titulo: p.titulo,
    p_documento_html: p.documentoHtml,
    p_ref_table: p.refTable ?? null,
    p_ref_id: p.refId ?? null,
  }));
}

/**
 * O estado da fila, para a tela dizer se o papel saiu. Sem `documento_html`:
 * são kB por linha e a lista não o usa — o documento só interessa a quem
 * imprime.
 */
export async function listarImpressoes(limite = 20): Promise<Array<Omit<PrintJob, 'documento_html'>>> {
  // Literal em UMA linha de propósito: o supabase-js infere o tipo a partir do
  // TEXTO do select, e concatenar com `+` derruba a inferência (mesma armadilha
  // anotada em listarVendas).
  return ou(
    await supabase
      .from('print_jobs')
      .select('id, tipo, ref_table, ref_id, titulo, status, tentativas, erro, solicitante_id, solicitante_nome, created_at, enviado_em')
      .order('created_at', { ascending: false })
      .limit(limite),
  ) ?? [];
}

// --- Clientes (migração 023) --------------------------------------------------

/**
 * Acha o cliente por nome (digitação suja), CPF/CNPJ ou telefone. Aberta a todo
 * perfil ativo: é assim que o balcão vincula a venda a alguém.
 *
 * Termo vazio devolve os primeiros em ordem alfabética — o seletor abre com uma
 * lista em vez de um vazio que parece defeito.
 */
export async function buscarClientes(q = '', limite = 20): Promise<BuscarClientesRow[]> {
  return ou(await supabase.rpc('buscar_clientes', { q: q.trim(), limite })) ?? [];
}

export interface NovoCliente {
  nome: string;
  cpf?: string | null;
  telefone?: string | null;
  email?: string | null;
  observacao?: string | null;
}

/**
 * Cadastra. A validação de CPF/telefone é da tela (lib/cliente.ts), que explica
 * o erro; aqui só o que o banco precisa. Violação do índice único de CPF sobe
 * como erro do PostgREST e vira "já existe cliente com este CPF" pelo
 * mensagemDeErro().
 */
export async function criarCliente(c: NovoCliente): Promise<Customer> {
  const { data: sessao } = await supabase.auth.getUser();
  return ou(
    await supabase.from('customers').insert({
      nome: c.nome.trim(),
      cpf: c.cpf?.trim() || null,
      telefone: c.telefone?.trim() || null,
      email: c.email?.trim() || null,
      observacao: c.observacao?.trim() || null,
      criado_por: sessao.user?.id ?? null,
    }).select('*').single(),
  ) as Customer;
}

/** Editar é gerente/dono — quem nega é a RLS (§16), não esta função. */
export async function atualizarCliente(
  id: string,
  campos: Partial<Pick<Customer, 'nome' | 'cpf' | 'telefone' | 'email' | 'observacao' | 'ativo'>>,
): Promise<void> {
  const { error } = await supabase.from('customers').update(campos).eq('id', id);
  if (error) throw error;
}

export async function obterCliente(id: string): Promise<Customer | null> {
  return ou(await supabase.from('customers').select('*').eq('id', id).maybeSingle());
}

/**
 * O que o cliente comprou, no período. RPC definer que atravessa a policy de
 * `sales` de propósito: um histórico que mostrasse só as vendas de quem está
 * olhando pareceria completo e não seria.
 *
 * Por isso mesmo é SÓ DO DONO desde 28/09/2026 (era gerente/dono): ele soma o
 * que aquele cliente já gastou na loja, e faturamento por cliente é faturamento.
 * A tela de Clientes nem monta o bloco para quem não é dono — quem nega é o
 * exige_papel no corpo da função.
 */
export async function historicoDoCliente(p: {
  customerId: string; de?: string | null; ate?: string | null;
}): Promise<HistoricoClienteRow[]> {
  return ou(await supabase.rpc('historico_do_cliente', {
    p_customer_id: p.customerId,
    p_de: p.de || null,
    p_ate: p.ate || null,
  })) ?? [];
}

// --- Mapa da loja -------------------------------------------------------------

export async function zonasDaLoja(): Promise<StoreZone[]> {
  return ou(
    await supabase.from('store_zones').select('*').order('ordem'),
  ) ?? [];
}

// --- Mapa da loja em 3D (/mapa) ------------------------------------------------
// A zona (Z1…Z5) é o começo da localização da peça — o porquê em lib/zonas.ts.

export interface PecaNoMapa {
  product_id: string;
  sku: string;
  descricao: string;
  marca: string | null;
  categoria: string | null;
  quantidade: number;
  local: string | null;
  /** Um lugar EXTRA da peça (092, stock_lugares) — o principal é o de stock.local.
   *  A mesma peça aparece uma vez por lugar; quantidade é a do estoque, uma só. */
  extra?: boolean;
  /** Calota (15/10/2026): a área da marca dela. A linha continua sendo o endereço gravado. */
  area?: AreaDaMarca;
}

/**
 * Toda peça ATIVA que tem linha de estoque, com a localização. Em páginas
 * (emPaginas): o mapa conta peça por zona, e contar só as mil primeiras
 * mentiria calado.
 */
export async function pecasNoMapa(): Promise<PecaNoMapa[]> {
  const linhas = await emPaginas((de, ate) => supabase
    .from('stock')
    .select('product_id, quantidade, local, products!inner(sku, descricao, marca, categoria, ativo)')
    .eq('products.ativo', true)
    .order('product_id')
    .range(de, ate)) as unknown as Array<{
      product_id: string; quantidade: number; local: string | null;
      products: Pick<Product, 'sku' | 'descricao' | 'marca' | 'categoria'>
        | Array<Pick<Product, 'sku' | 'descricao' | 'marca' | 'categoria'>>;
    }>;

  const principais: PecaNoMapa[] = linhas.map((l) => {
    const p = Array.isArray(l.products) ? l.products[0] : l.products;
    return {
      product_id: l.product_id,
      sku: p?.sku ?? '',
      descricao: p?.descricao ?? '',
      marca: p?.marca ?? null,
      categoria: p?.categoria ?? null,
      quantidade: Number(l.quantidade),
      local: l.local,
    };
  });

  // Os lugares EXTRAS (092): a calota que também fica no chão aparece lá
  // também, com os dados da linha principal. Peça inativa não tem principal
  // aqui, e o extra dela some junto.
  const extras = await emPaginas((de, ate) => supabase
    .from('stock_lugares')
    .select('product_id, local')
    .order('product_id')
    .range(de, ate)) as unknown as Array<{ product_id: string; local: string }>;
  const porId = new Map(principais.map((p) => [p.product_id, p]));
  const outros: PecaNoMapa[] = [];
  for (const e of extras) {
    const base = porId.get(e.product_id);
    if (base) outros.push({ ...base, local: e.local, extra: true });
  }

  // CALOTA COM ÁREA DA MARCA (15/10/2026): as linhas continuam as do endereço
  // GRAVADO — é nelas que mudar de lugar, contar e renomear trabalham, e o banco
  // as conhece. A área vem junto em cada linha: a busca acende os ganchos da
  // marca, a lista da zona agrupa por área e o gancho tocado lista a marca.
  const todas = [...principais, ...outros];
  const areas = await areasDasCalotas();
  if (areas.size === 0) return todas;
  return todas.map((p) => {
    const area = areas.get(p.product_id);
    return area ? { ...p, area } : p;
  });
}

// --- A área da marca das calotas (15/10/2026) ---------------------------------
// A planta vigente diz que marca mora em cada gancho (objeto 'calotas' com
// `marca`); o catálogo diz em que carro cada calota serve. Os dois juntos dão
// o lugar da calota sem ninguém cadastrar gancho (lib/marcasDasCalotas.ts).

let areasEmCache: { em: number; areas: Promise<Map<MarcaDaCalota, AreaDaMarca>> } | null = null;
/** A planta muda raramente; um minuto basta, e salvar a planta zera o cache. */
const AREAS_VALEM_MS = 60_000;

/** As áreas que a planta VIGENTE marca. Sem planta salva, nenhuma: a do arquivo não tem marca. */
export function areasDasMarcas(): Promise<Map<MarcaDaCalota, AreaDaMarca>> {
  const agora = Date.now();
  if (areasEmCache && agora - areasEmCache.em < AREAS_VALEM_MS) return areasEmCache.areas;
  const areas = plantaDaLoja().then((salva) => (salva ? areasDaPlanta(lerPlanta(salva.planta)) : new Map<MarcaDaCalota, AreaDaMarca>()));
  areasEmCache = { em: agora, areas };
  areas.catch(() => { areasEmCache = null; });
  return areas;
}

/** Depois de salvar ou voltar a planta: a próxima consulta lê as áreas de novo. */
export function esquecerAreasDasMarcas(): void {
  areasEmCache = null;
}

/**
 * A área da marca de cada CALOTA — da lista, ou de todas sem lista. Peça que
 * não é calota, calota universal (sem carro) e marca sem gancho no mapa ficam
 * de fora, e valem o lugar de sempre. Falha aqui não derruba o "Onde fica":
 * sem área, a tela mostra o gancho gravado, como antes.
 */
export async function areasDasCalotas(productIds?: readonly string[]): Promise<Map<string, AreaDaMarca>> {
  const saida = new Map<string, AreaDaMarca>();
  if (productIds && productIds.length === 0) return saida;
  try {
    const areas = await areasDasMarcas();
    if (areas.size === 0) return saida;
    // Em páginas e em ordem: sem lista (o mapa inteiro), o teto de 1.000 linhas
    // do PostgREST cortaria em silêncio e a marca mudaria de uma carga a outra.
    const calotas = await emPaginas<{ id: string }>((de, ate) => {
      const q = supabase.from('products').select('id').eq('tipo', 'calota');
      return (productIds ? q.in('id', [...productIds]) : q).order('id').range(de, ate);
    });
    if (calotas.length === 0) return saida;
    const ids = calotas.map((c) => c.id);
    const aplicacoes = await emPaginas<{ product_id: string; montadora: string | null }>((de, ate) => supabase
      .from('product_applications').select('product_id, montadora')
      .in('product_id', ids).order('product_id').range(de, ate));
    const montadoras = new Map<string, Array<string | null>>();
    for (const a of aplicacoes) montadoras.set(a.product_id, [...(montadoras.get(a.product_id) ?? []), a.montadora]);
    for (const id of ids) {
      const marca = marcaDaCalota(montadoras.get(id) ?? []);
      const area = marca ? areas.get(marca) : undefined;
      if (area) saida.set(id, area);
    }
  } catch {
    return new Map();
  }
  return saida;
}

/** Os lugares extras de uma peça (092) — a ficha mostra "também em". */
export async function lugaresExtrasDaPeca(productId: string): Promise<string[]> {
  const linhas = ou(await supabase.from('stock_lugares').select('local')
    .eq('product_id', productId).order('local')) as Array<{ local: string }> | null;
  return (linhas ?? []).map((l) => l.local);
}

/**
 * Os lugares de VÁRIAS peças de uma vez — o principal (stock.local) e os
 * extras (092) —, para a faixa "Onde fica" da busca, do catálogo e do
 * carrinho (06/10/2026). Uma consulta por lista, nunca uma por linha: é o
 * mesmo motivo de fotosDasPecas.
 *
 * DOIS selects simples, SEM embed (lição de 11/09/2026: coluna de embed
 * ninguém confere). As duas tabelas são legíveis por todo perfil ativo
 * (rls.sql §26.1 e sel_stock_lugares): é leitura de balcão, para todo papel.
 */
export async function lugaresDasPecas(productIds: string[]): Promise<Map<string, LugaresDaPeca>> {
  if (productIds.length === 0) return new Map();
  const [principais, extras, areas] = await Promise.all([
    supabase.from('stock').select('product_id, local').in('product_id', productIds),
    supabase.from('stock_lugares').select('product_id, local').in('product_id', productIds).order('local'),
    areasDasCalotas(productIds),
  ]);
  const lugares = montarLugares(
    (ou(principais) as Array<{ product_id: string; local: string | null }> | null) ?? [],
    (ou(extras) as Array<{ product_id: string; local: string }> | null) ?? [],
  );
  // Calota com área da marca: a área manda; o gancho gravado fica só de reserva.
  for (const [id, area] of areas) lugares.set(id, { ...(lugares.get(id) ?? { principal: null, extras: [] }), area });
  return lugares;
}

/**
 * O TIPO de várias peças de uma vez (08/10/2026) — é dele que nasce o botão
 * "Jogo" na linha do carrinho.
 *
 * UMA consulta por lista, nunca uma por linha: a mesma régua de
 * `fotosDasPecas` e `lugaresDasPecas`, porque o carrinho redesenha a cada
 * bipe.
 *
 * POR QUE A TELA PERGUNTA, EM VEZ DE O TIPO VIR NO ITEM. Porque uma peça entra
 * no carrinho por doze caminhos — busca livre, bipe, por tipo, por carro, por
 * versão, por placa, o carrinho que renasce do banco, o rascunho de 12 horas
 * do aparelho, "emitir igual"... — e três RPCs diferentes alimentam esses
 * caminhos. Pôr `tipo` no retorno de uma e esquecer a outra faria o botão
 * existir na busca livre e sumir na busca por placa, sem erro e sem teste
 * vermelho. Pior: o rascunho gravado ANTES da mudança não teria o campo, e por
 * até 12 horas depois de publicar todo atendimento em curso ficaria sem o
 * botão. Perguntando aqui, os doze caminhos funcionam de uma vez.
 *
 * Leitura direta na tabela porque `products` é legível por qualquer perfil
 * ativo; só custo, margem alvo e preço de e-commerce são revogados.
 */
export async function tiposDasPecas(productIds: string[]): Promise<Map<string, string>> {
  if (productIds.length === 0) return new Map();
  const linhas = (ou(
    await supabase.from('products').select('id, tipo').in('id', productIds),
  ) as Array<{ id: string; tipo: string | null }> | null) ?? [];
  const mapa = new Map<string, string>();
  for (const l of linhas) if (l.tipo) mapa.set(l.id, l.tipo);
  return mapa;
}

/** Mais um lugar para a peça (092): ela continua no principal e passa a morar aqui também. */
export async function acrescentarLugarDaPeca(p: { productId: string; local: string }): Promise<void> {
  const { error } = await supabase.rpc('acrescentar_lugar_da_peca', {
    p_product_id: p.productId, p_local: p.local,
  });
  if (error) throw error;
}

/** Tira um lugar EXTRA da peça (092); o principal sai por definirEnderecoDaPeca. */
export async function tirarLugarDaPeca(p: { productId: string; local: string }): Promise<void> {
  const { error } = await supabase.rpc('tirar_lugar_da_peca', {
    p_product_id: p.productId, p_local: p.local,
  });
  if (error) throw error;
}

/**
 * Leva peças para uma zona: a zona entra no começo da localização de cada uma
 * (ver localNaZona). Um update por localização de ORIGEM, e só onde ela ainda
 * é a mesma — peça que alguém mudou de lugar no meio do caminho não é
 * atropelada. Quem nega é a RLS (update de stock é gerente/dono, rls.sql §1),
 * e negar lá é zero linhas sem erro: daí a contagem.
 */
export async function moverParaZona(p: {
  zona: CodigoZona;
  pecas: ReadonlyArray<Pick<PecaNoMapa, 'product_id' | 'local'>>;
}): Promise<number> {
  const porOrigem = new Map<string | null, string[]>();
  // Lugar EXTRA (092) não muda de zona por aqui: mexer em stock.local levaria
  // o lugar PRINCIPAL da peça junto. Ele se tira e se acrescenta pelo mapa.
  for (const peca of p.pecas) {
    if ((peca as Partial<PecaNoMapa>).extra) continue;
    porOrigem.set(peca.local, [...(porOrigem.get(peca.local) ?? []), peca.product_id]);
  }

  let movidas = 0;
  for (const [origem, ids] of porOrigem) {
    let consulta = supabase
      .from('stock')
      .update({ local: localNaZona(origem, p.zona), updated_at: new Date().toISOString() })
      .in('product_id', ids);
    consulta = origem === null ? consulta.is('local', null) : consulta.eq('local', origem);
    const { data, error } = await consulta.select('product_id');
    if (error) throw error;
    movidas += data?.length ?? 0;
  }
  if (porOrigem.size === 0 && p.pecas.length > 0) {
    throw new Error('Aqui só há lugares extras, e lugar extra não muda de zona por esta lista: '
      + 'tire pelo "Tirar daqui" do mapa e acrescente no lugar novo.');
  }
  if (movidas === 0 && p.pecas.length > 0) {
    throw new Error('Nenhuma peça mudou de zona — seu perfil não altera o estoque, '
      + 'ou elas já tinham mudado de lugar. Recarregue a tela e confira.');
  }
  return movidas;
}

// --- A planta da loja (mapa 3D, migração 082) --------------------------------

/**
 * A planta vigente: a versão de id maior em plantas_da_loja. `null` quando
 * ninguém salvou ainda — aí vale o planta.json que vai dentro do app.
 */
export async function plantaDaLoja(): Promise<PlantaDaLojaSalva | null> {
  const { data, error } = await supabase
    .from('plantas_da_loja')
    .select('id, planta, salva_em, salva_por, salva_por_nome, renomeados, pecas_renomeadas, origem, restaurada_de')
    .order('id', { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error) throw error;
  return (data as PlantaDaLojaSalva | null) ?? null;
}

/**
 * Grava a planta como versão nova — só se `base` ainda for a vigente (quem
 * salvou antes ganha; esta tela recebe o recado de recarregar) — e troca o
 * começo do endereço das peças de cada código de lugar em `renomear`. No
 * primeiro salvamento, `original` (a planta de onde a tela partiu) entra antes,
 * como a versão 'sistema' (084): é para ela que o dono pode voltar.
 */
export async function salvarPlantaDaLoja(p: {
  planta: unknown;
  base: number;
  renomear: ReadonlyArray<{ de: string; para: string }>;
  original?: unknown;
}): Promise<PlantaSalvaResultado> {
  const { data, error } = await supabase.rpc('salvar_planta_da_loja', {
    p_planta: p.planta, p_base: p.base, p_renomear: p.renomear,
    ...(p.original === undefined ? {} : { p_original: p.original }),
  });
  if (error) throw error;
  esquecerAreasDasMarcas();
  return data as PlantaSalvaResultado;
}

/** O histórico do mapa, da versão mais nova para a mais velha — sem a planta de cada uma (084). */
export async function versoesDaPlanta(limite = 40): Promise<VersaoDaPlanta[]> {
  const { data, error } = await supabase
    .from('plantas_da_loja')
    .select('id, salva_em, salva_por_nome, renomeados, pecas_renomeadas, origem, restaurada_de')
    .order('id', { ascending: false })
    .limit(limite);
  if (error) throw error;
  return (data as VersaoDaPlanta[] | null) ?? [];
}

/** A planta de UMA versão do histórico — para ver como o mapa era antes de voltar para ela. */
export async function plantaDaVersao(id: number): Promise<unknown> {
  const { data, error } = await supabase.from('plantas_da_loja').select('planta').eq('id', id).single();
  if (error) throw error;
  return (data as { planta: unknown }).planta;
}

/**
 * Volta o mapa para a versão `versao` — SÓ o dono (084). Ela é copiada como
 * versão nova, se `base` ainda for a vigente, e os lugares cujo código mudou
 * desde ela devolvem o endereço das peças.
 */
export async function restaurarPlantaDaLoja(p: { versao: number; base: number }): Promise<PlantaRestauradaResultado> {
  const { data, error } = await supabase.rpc('restaurar_planta_da_loja', { p_versao: p.versao, p_base: p.base });
  if (error) throw error;
  esquecerAreasDasMarcas();
  return data as PlantaRestauradaResultado;
}

/**
 * Põe UMA peça num lugar do mapa (01/10/2026): o código do lugar vira o
 * endereço dela (stock.local). `local` vazio tira a peça do lugar. Com
 * `localAntes` — o endereço que a tela mostrava quando a pessoa escolheu a
 * peça —, o banco recusa se ela já mudou de lugar no meio do caminho, em vez
 * de desfazer a troca de outra pessoa. Dono ou gerente (exige_papel no corpo).
 */
export async function definirEnderecoDaPeca(p: {
  productId: string;
  local: string | null;
  localAntes?: string | null;
}): Promise<EnderecoDaPecaResultado> {
  const { data, error } = await supabase.rpc('definir_endereco_da_peca', {
    p_product_id: p.productId,
    p_local: p.local ?? '',
    p_local_antes: p.localAntes ?? null,
  });
  if (error) throw error;
  return data as EnderecoDaPecaResultado;
}

// --- Metas por unidade (tela 13) ---------------------------------------------

export async function unidades(): Promise<Unit[]> {
  return ou(await supabase.from('units').select('*').eq('ativo', true).order('nome')) ?? [];
}

/** Progresso meta × realizado do trimestre. SÓ DONO desde 28/09/2026: a tela
 *  de Metas saiu da equipe, e o sino não pede isto para mais ninguém. */
export async function metasProgresso(ano: number, trimestre?: number): Promise<MetaProgressoRow[]> {
  return ou(await supabase.rpc('metas_progresso', {
    p_ano: ano,
    p_trimestre: trimestre ?? null,
  })) ?? [];
}

/** Metas cruas do período (para o editor do dono). */
export async function metasDoPeriodo(unitId: string, ano: number, trimestre: number): Promise<Goal[]> {
  return ou(
    await supabase.from('goals').select('*')
      .eq('unit_id', unitId).eq('ano', ano).eq('trimestre', trimestre),
  ) ?? [];
}

export async function salvarMeta(m: {
  unit_id: string; ano: number; trimestre: number;
  metrica: GoalMetric; valor_meta: number;
}): Promise<void> {
  const { data: sessao } = await supabase.auth.getUser();
  const { error } = await supabase.from('goals').upsert({
    ...m,
    criado_por: sessao.user?.id ?? null,
  }, { onConflict: 'unit_id,ano,trimestre,metrica' });
  if (error) throw error;
}

export async function removerMeta(id: string): Promise<void> {
  const { error } = await supabase.from('goals').delete().eq('id', id);
  if (error) throw error;
}

// --- Painel do dono (tela 12 — folha e custos) -------------------------------

export interface LinhaFolha extends PayrollEntry {
  profiles: { nome: string; papel: Papel } | null;
}

export async function folhaDoMes(competencia: string): Promise<LinhaFolha[]> {
  const dados = ou(
    await supabase
      .from('payroll_entries')
      .select('*, profiles(nome, papel)')
      .eq('competencia', competencia)
      .order('created_at'),
  ) as Array<PayrollEntry & {
    profiles: { nome: string; papel: Papel } | Array<{ nome: string; papel: Papel }> | null;
  }> | null;

  return (dados ?? []).map((d) => ({
    ...d,
    profiles: Array.isArray(d.profiles) ? d.profiles[0] ?? null : d.profiles,
  }));
}

export async function salvarLancamentoFolha(l: {
  id?: string;
  profile_id: string;
  competencia: string;
  salario_base: number;
  comissao: number;
  bonus: number;
  descontos: number;
  /** O dia em que esta pessoa recebe (083) — o fluxo de caixa usa ele. */
  vencimento: string;
  observacao?: string | null;
}): Promise<void> {
  const { error } = await supabase
    .from('payroll_entries')
    .upsert({
      profile_id: l.profile_id,
      competencia: l.competencia,
      salario_base: l.salario_base,
      comissao: l.comissao,
      bonus: l.bonus,
      descontos: l.descontos,
      vencimento: l.vencimento,
      observacao: l.observacao ?? null,
      // 094: salvar à mão TRAVA a linha contra a geração automática — daqui
      // em diante ela é decisão do dono, e mudar o combinado não a reescreve.
      prevista: false,
    }, { onConflict: 'profile_id,competencia' });
  if (error) throw error;
}

export async function removerLancamentoFolha(id: string): Promise<void> {
  const { error } = await supabase.from('payroll_entries').delete().eq('id', id);
  if (error) throw error;
}

/**
 * Dá baixa no lançamento da folha e lança a saída no caixa.
 *
 * `pagoEm` (083) é o dia em que o dinheiro saiu DE VERDADE — normalmente o
 * vencimento da pessoa, que já passou quando a folha é lançada depois. Sem
 * ele o livro carimbava a hora do clique, e o salário pago no dia 3 caía no
 * dia em que alguém lembrou de registrar. É o mesmo retroativo que
 * pagar_conta faz de propósito (059).
 */
export async function pagarFolha(
  id: string, forma: PaymentMethod, pagoEm?: string | null,
): Promise<void> {
  const { error } = await supabase.rpc('pagar_folha', {
    p_entry_id: id,
    p_forma: forma,
    // Data pura vira meio-dia: o fuso da loja é −03, e a meia-noite UTC
    // cairia no dia anterior no livro. HOJE vai com a hora real (094), como
    // pagar_conta faz: meio-dia UTC são 9h da loja, e o pagamento das 8h
    // ficaria carimbado uma hora no futuro.
    p_pago_em: pagoEm && pagoEm !== hojeISO() ? `${pagoEm}T12:00:00Z` : new Date().toISOString(),
  });
  if (error) throw error;
}

/** O caminho de volta do pagarFolha (094): a linha volta a "a pagar" e a
 *  saída some do caixa. Só dono — a RPC recusa os outros. */
export async function desfazerPagamentoFolha(id: string): Promise<void> {
  const { error } = await supabase.rpc('desfazer_pagamento_folha', { p_entry_id: id });
  if (error) throw error;
}

/**
 * Cria a folha do mês corrente e do seguinte a partir do combinado (094) e
 * devolve quantas linhas criou ou atualizou. Idempotente: com tudo em dia,
 * devolve 0. Só dono — por isso só as telas do dono chamam (useFolhaPrevista).
 */
export async function gerarFolhaPrevista(): Promise<number> {
  return Number(ou(await supabase.rpc('gerar_folha_prevista')) ?? 0);
}

/**
 * A folha inteira com o nome de cada pessoa, para Contas a pagar (094). As
 * colunas vêm de COLUNAS_DA_FOLHA — nada de comissão, bônus ou desconto, que
 * são do Painel. São cinco linhas por mês: cabe numa leitura.
 */
export async function folhaNasContas(): Promise<LinhaDaFolhaNasContas[]> {
  const dados = ou(
    await supabase
      .from('payroll_entries')
      .select(`${COLUNAS_DA_FOLHA.join(', ')}, profiles(nome)`)
      .order('competencia', { ascending: false })
      .limit(LIMITE_CONTAS),
  ) as unknown as Array<Omit<LinhaDaFolhaNasContas, 'profiles'> & {
    profiles: { nome: string } | Array<{ nome: string }> | null;
  }> | null;
  return (dados ?? []).map((d) => ({
    ...d,
    total: Number(d.total),
    profiles: Array.isArray(d.profiles) ? d.profiles[0] ?? null : d.profiles,
  }));
}

export async function folhaMensal(): Promise<FolhaMensalRow[]> {
  return ou(
    await supabase.from('v_folha_mensal').select('*')
      .order('competencia', { ascending: false }).limit(24),
  ) ?? [];
}

export async function custosMensais(competencia: string): Promise<CustosMensaisRow[]> {
  return ou(
    await supabase.from('v_custos_mensais').select('*')
      .eq('competencia', competencia)
      .order('total', { ascending: false }),
  ) ?? [];
}

export async function resultadoMensal(): Promise<ResultadoMensalRow[]> {
  return ou(
    await supabase.from('v_resultado_mensal').select('*')
      .order('competencia', { ascending: false }).limit(12),
  ) ?? [];
}

export async function categoriasExistentes(): Promise<string[]> {
  const dados = ou(
    await supabase.from('products').select('categoria').not('categoria', 'is', null).limit(1000),
  ) as Array<{ categoria: string }> | null;
  return [...new Set((dados ?? []).map((d) => d.categoria))].sort();
}

/** Contadores da tela de relatórios: qualidade do cadastro vira número visível. */
export async function resumoCadastro(): Promise<{ total: number; completos: number }> {
  // `select('id')` e não `'*'`: com as colunas de custo revogadas, pedir `*`
  // dá erro de permissão mesmo com head:true. Ver COLUNAS_PRODUTO.
  const total = await supabase.from('products').select('id', { count: 'exact', head: true }).eq('ativo', true);
  const completos = await supabase.from('products').select('id', { count: 'exact', head: true })
    .eq('ativo', true).eq('cadastro_completo', true);
  if (total.error) throw total.error;
  if (completos.error) throw completos.error;
  return { total: total.count ?? 0, completos: completos.count ?? 0 };
}

// --- Sistema: observabilidade (migração 019) ---------------------------------
//
// Duas fontes, e a diferença importa: as RPCs abaixo leem o que o agente JÁ
// GRAVOU no banco (histórico, agregados, deploys); as duas funções do fim do
// arquivo falam com o agente por HTTP e medem a máquina AGORA. Quem abre esta
// tela normalmente está desconfiando de alguma coisa neste minuto — e a última
// amostra pode ter até um minuto de atraso.
//
// Todas as RPCs são `security definer` com exige_papel(['dono']) dentro: quem
// nega é o banco, não a rota da tela.

export async function saudeSistema(): Promise<SaudeSistema | null> {
  const linhas = ou(await supabase.rpc('saude_sistema')) as SaudeSistema[] | null;
  const s = linhas?.[0];
  if (!s) return null;
  // `containers` vem como jsonb cru; o resto da tela conta com um array.
  return { ...s, containers: Array.isArray(s.containers) ? s.containers : [] };
}

export async function metricasNegocio(): Promise<MetricasNegocio | null> {
  const linhas = ou(await supabase.rpc('metricas_negocio')) as MetricasNegocio[] | null;
  return linhas?.[0] ?? null;
}

export async function acessosPorDia(dias = 14): Promise<AcessosPorDiaRow[]> {
  return ou(await supabase.rpc('acessos_por_dia', { p_dias: dias })) ?? [];
}

export async function acessosPorRota(p: {
  dias?: number; host?: string | null; limite?: number;
} = {}): Promise<AcessosPorRotaRow[]> {
  return ou(await supabase.rpc('acessos_por_rota', {
    p_dias: p.dias ?? 7, p_host: p.host ?? null, p_limite: p.limite ?? 20,
  })) ?? [];
}

export async function errosRecentes(limite = 50): Promise<ErroRecenteRow[]> {
  return ou(await supabase.rpc('erros_recentes', { p_limite: limite })) ?? [];
}

export async function vpsSerie(horas = 24): Promise<VpsSerieRow[]> {
  return ou(await supabase.rpc('vps_serie', { p_horas: horas })) ?? [];
}

export async function listarDeploys(limite = 20): Promise<DeployResumo[]> {
  return ou(await supabase.rpc('listar_deploys', { p_limite: limite })) ?? [];
}

/** A saída completa de UM deploy (até 64 kB) — só quando o dono abre a linha. */
export async function saidaDoDeploy(id: string): Promise<string> {
  return ou(await supabase.rpc('saida_do_deploy', { p_id: id })) ?? '';
}

// --- O agente da VPS, por HTTP ------------------------------------------------

/**
 * Chama o agente (infra/agente/) na mesma origem da API.
 *
 * Não passa pelo supabase-js porque não é PostgREST: é um processo nosso atrás
 * da ponte `/infra/v1/*` do Caddy. O token vai à mão, e é o `access_token` da
 * sessão — o agente revalida o papel no banco COM ELE, então um gerente que
 * chame isto leva 403 do outro lado, não daqui.
 */
async function chamarAgente<T>(caminho: string, metodo: 'GET' | 'POST' = 'GET'): Promise<T> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (!token) throw new Error('Sessão expirada — entre de novo.');

  const base = import.meta.env.VITE_SUPABASE_URL as string;
  const r = await fetch(`${base}/infra/v1${caminho}`, {
    method: metodo,
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
  });

  // O recado útil do agente vem no corpo; `r.statusText` é genérico.
  if (!r.ok) {
    const corpo = await r.json().catch(() => null) as { erro?: string } | null;
    throw new Error(corpo?.erro ?? `O agente respondeu ${r.status}.`);
  }
  return r.json() as Promise<T>;
}

/** Os sinais vitais AO VIVO (não a última linha do banco). */
export const statusDaVps = () => chamarAgente<AgenteStatus>('/status');

/** Dispara a publicação. 409 vira erro com "Já existe um deploy em andamento." */
export const dispararDeploy = () => chamarAgente<AgenteDeployAceito>('/deploy', 'POST');

// --- Agenda da equipe (migração 038) ------------------------------------------

/**
 * As ocorrências de uma janela (a RPC limita a 92 dias). `profileId` nulo é a
 * agenda de quem está pedindo; a gerência passa o id para ver a de um vendedor.
 */
export async function agendaDoPeriodo(
  de: string, ate: string, profileId?: string | null,
): Promise<AgendaOcorrencia[]> {
  return ou(await supabase.rpc('agenda_do_periodo', {
    p_de: de, p_ate: ate, p_profile_id: profileId ?? null,
  })) ?? [];
}

export interface NovaTarefa {
  id?: string | null;
  titulo: string;
  descricao?: string | null;
  dataInicio: string;
  dataFim?: string | null;
  hora?: string | null;
  duracaoMin?: number | null;
  recorrencia: AgendaRecorrencia;
  diasSemana?: number[];
  diaMes?: number | null;
  prioridade?: AgendaPrioridade;
  /** Vazio = tarefa da LOJA (vale para todos). A RPC exige gerência nesse caso. */
  participantes: string[];
}

export async function salvarTarefa(t: NovaTarefa): Promise<string> {
  return ou(await supabase.rpc('salvar_tarefa', {
    p_id: t.id ?? null,
    p_titulo: t.titulo,
    p_descricao: t.descricao ?? null,
    p_data_inicio: t.dataInicio,
    p_data_fim: t.dataFim ?? null,
    p_hora: t.hora ?? null,
    p_duracao_min: t.duracaoMin ?? null,
    p_recorrencia: t.recorrencia,
    p_dias_semana: t.diasSemana ?? [],
    p_dia_mes: t.diaMes ?? null,
    p_prioridade: t.prioridade ?? 'normal',
    p_participantes: t.participantes,
  }));
}

export async function excluirTarefa(id: string): Promise<void> {
  const { error } = await supabase.rpc('excluir_tarefa', { p_id: id });
  if (error) throw error;
}

/** Marca/desmarca a conclusão de UMA ocorrência (tarefa + pessoa + dia). */
export async function concluirOcorrencia(p: {
  tarefaId: string; profileId: string; dia: string; concluir: boolean;
}): Promise<void> {
  if (p.concluir) {
    const { error } = await supabase.from('agenda_conclusoes')
      .insert({ tarefa_id: p.tarefaId, profile_id: p.profileId, dia: p.dia });
    if (error) throw error;
  } else {
    const { error } = await supabase.from('agenda_conclusoes').delete()
      .eq('tarefa_id', p.tarefaId).eq('profile_id', p.profileId).eq('dia', p.dia);
    if (error) throw error;
  }
}

/** A tarefa completa, para abrir no formulário de edição. */
export async function obterTarefa(id: string): Promise<AgendaTarefa> {
  return ou(await supabase.from('agenda_tarefas').select('*').eq('id', id).single());
}

// --- Contas a pagar (migração 039) --------------------------------------------

/**
 * Teto de uma leitura de contas feita de uma vez só, sem páginas. NÃO é mais
 * o teto de contasAPagar, que lê em páginas desde 09/10/2026 (o aviso amarelo
 * de "mil primeiras" saiu junto).
 *
 * Continua exportada de propósito: a folha em Contas a pagar (folhaNasContas,
 * migração 094, PR #272, aberto em paralelo) usa `.limit(LIMITE_CONTAS)`.
 * Apagá-la não dá conflito no git — o merge passa limpo e só o typecheck da
 * soma quebra (TS2304), com a main vermelha (revisão do #273).
 */
export const LIMITE_CONTAS = 1000;

/**
 * O recado de Contas a pagar quando passa do teto de emPaginas. A tela não
 * tem filtro que diminua a leitura — categoria, texto e vencimento filtram
 * no navegador, depois de tudo lido —, então o recado não manda filtrar.
 */
const RECADO_DO_TETO_DAS_CONTAS =
  `São mais de ${TETO_DE_LINHAS.toLocaleString('pt-BR')} contas, o máximo que `
  + 'esta tela lê de uma vez. A lista não abre para não mostrar total pela '
  + 'metade: avise quem cuida do sistema.';

/**
 * A lista INTEIRA de contas, pagas inclusive — os cartões do topo de Contas a
 * pagar ("total em aberto", "vencem em 7 dias") somam tudo o que vem daqui.
 *
 * Em PÁGINAS (emPaginas) desde 09/10/2026. Antes era `.limit(1000)` com um
 * aviso amarelo, e o corte saía pelo FIM da ordem de vencimento: as contas
 * fixas futuras e os boletos novos em aberto. A compra paga na hora (095) mora
 * nesta tabela — dezenas por mês, cada uma ocupando uma vaga —, e o teto, que
 * dava anos de folga, passaria a chegar em meses (revisão do #273). O id entra
 * na ordem para as páginas não trocarem linhas entre si.
 */
export async function contasAPagar(): Promise<ContaPagar[]> {
  // O embed traz SÓ metadados (044): o binário (`dados`) NUNCA vem na lista —
  // seriam até 2 × 2,5 MB por conta, em milhares de contas; ele sai só pela RPC
  // obter_comprovante_conta. Desde a 080 são até DOIS anexos por conta (o
  // boleto e o comprovante), e o `tipo` diz qual é qual.
  type Linha = ContaPagar & { anexos?: AnexoDaConta[] | AnexoDaConta | null };
  const linhas = await emPaginas<Linha>((de, ate) => supabase.from('contas_pagar')
    .select('*, anexos:contas_pagar_comprovantes(tipo, content_type, bytes, nome)')
    .order('vencimento', { ascending: true })
    .order('id', { ascending: true })
    .range(de, ate) as unknown as PromiseLike<{ data: Linha[] | null; error: unknown }>,
  RECADO_DO_TETO_DAS_CONTAS);
  return linhas.map(({ anexos, ...c }) => {
    // A chave (conta, tipo) faz disto um 1-para-N: vem lista. Objeto solto ou
    // nulo também é aceito, para não depender do humor do PostgREST.
    const lista = Array.isArray(anexos) ? anexos : anexos ? [anexos] : [];
    const meta = (t: TipoDeAnexo) => {
      const a = lista.find((x) => x.tipo === t);
      return a ? { content_type: a.content_type, bytes: a.bytes, nome: a.nome } : null;
    };
    return { ...c, comprovante: meta('comprovante'), boleto: meta('boleto') };
  });
}

/**
 * Todas as contas EM ABERTO, para a lista que vai por WhatsApp (22/09/2026).
 *
 * Consulta própria, e não a da tela: contasAPagar traz também as pagas, a
 * observação e os anexos, que o papel não usa. Aqui vêm só as colunas do papel (COLUNAS_DA_LISTA). A RLS é
 * a do dono: para o gerente e o vendedor a lista volta vazia.
 *
 * Em PÁGINAS (emPaginas), e não com um limit: o servidor devolve no máximo
 * 1000 linhas por pedido, e um limit(1001) "para saber se cortou" voltaria
 * 1000 em silêncio — lista cortada mente no total (revisão de 22/09/2026).
 * Em 22/09/2026 eram 138 contas em aberto. A ordem inclui o id para as
 * páginas não trocarem linhas entre si.
 */
export async function contasEmAberto(): Promise<ContaParaLista[]> {
  const linhas = await emPaginas<ContaParaLista>((de, ate) => supabase.from('contas_pagar')
    .select(COLUNAS_DA_LISTA.join(', '))
    .is('pago_em', null)
    // 086: cancelada não é conta a pagar. Ela continua existindo (o documento
    // é a única cópia), mas fora desta lista e fora de toda soma.
    .is('cancelada_em', null)
    .order('vencimento', { ascending: true })
    .order('descricao', { ascending: true })
    .order('id', { ascending: true })
    .range(de, ate) as unknown as PromiseLike<{ data: ContaParaLista[] | null; error: unknown }>,
  RECADO_DO_TETO_DAS_CONTAS);
  return linhas.map((c) => ({
    ...c,
    // numeric chega como string em alguns caminhos do PostgREST.
    valor_previsto: c.valor_previsto === null ? null : Number(c.valor_previsto),
    valor_real: c.valor_real === null ? null : Number(c.valor_real),
  }));
}

export async function contasRecorrentes(): Promise<ContaRecorrente[]> {
  return ou(await supabase.from('contas_recorrentes').select('*').order('descricao')) ?? [];
}

export async function salvarContaRecorrente(r: {
  id?: string | null; descricao: string; categoria: CategoriaConta;
  /** Nulo = valor variável (046): a regra materializa só a data. */
  valor: number | null;
  diaVencimento: number; dataInicio: string; dataFim?: string | null;
  fornecedorId?: string | null; observacao?: string | null; ativa?: boolean;
}): Promise<string> {
  return ou(await supabase.rpc('salvar_conta_recorrente', {
    p_id: r.id ?? null, p_descricao: r.descricao, p_categoria: r.categoria,
    p_valor: r.valor, p_dia: r.diaVencimento, p_data_inicio: r.dataInicio,
    p_data_fim: r.dataFim ?? null, p_fornecedor_id: r.fornecedorId ?? null,
    p_observacao: r.observacao ?? null, p_ativa: r.ativa ?? true,
  }));
}

export async function salvarConta(c: {
  id?: string | null; descricao: string; categoria: CategoriaConta;
  /** NULL = "a definir" (046) — a conta de luz antes de a fatura chegar. */
  valorPrevisto: number | null; valorReal?: number | null; vencimento: string;
  fornecedorId?: string | null; observacao?: string | null;
}): Promise<string> {
  return ou(await supabase.rpc('salvar_conta', {
    p_id: c.id ?? null, p_descricao: c.descricao, p_categoria: c.categoria,
    p_valor_previsto: c.valorPrevisto, p_valor_real: c.valorReal ?? null,
    p_vencimento: c.vencimento, p_fornecedor_id: c.fornecedorId ?? null,
    p_observacao: c.observacao ?? null,
  }));
}

/** A baixa: lança a SAÍDA no caixa na mesma transação. */
export async function pagarConta(p: {
  id: string; valor: number; data?: string | null; forma?: PaymentMethod;
}): Promise<string> {
  return ou(await supabase.rpc('pagar_conta', {
    p_id: p.id, p_valor: p.valor, p_data: p.data ?? null, p_forma: p.forma ?? 'dinheiro',
  }));
}

/** O LOTE (23/08/2026): uma data e uma forma para várias contas. Cada conta
 *  continua com lançamento próprio no caixa; atômico — conta já paga ou sem
 *  valor definido recusa o lote inteiro. */
export async function pagarContas(p: {
  ids: string[]; data?: string | null; forma?: PaymentMethod;
}): Promise<PagamentoDeLote[]> {
  return ou(await supabase.rpc('pagar_contas', {
    p_ids: p.ids, p_data: p.data ?? null, p_forma: p.forma ?? 'dinheiro',
  })) ?? [];
}

export async function desfazerPagamento(id: string): Promise<void> {
  const { error } = await supabase.rpc('desfazer_pagamento', { p_id: id });
  if (error) throw error;
}

// --- Compra paga na hora (095) ------------------------------------------------

/**
 * "Paguei fornecedor": a conta de fornecedor JÁ PAGA e a saída no livro, numa
 * chamada só. Gerente e dono (a RPC confere); dia passado só o dono. Devolve
 * só o id da conta — nenhum total volta, porque o gerente não vê o mês.
 */
export async function registrarCompraNaHora(p: EnvioDaCompra): Promise<string> {
  return ou(await supabase.rpc('registrar_compra_na_hora', {
    p_valor: p.valor,
    p_forma: p.forma,
    p_fornecedor_id: p.fornecedorId,
    p_nome: p.nome,
    p_observacao: p.observacao,
    p_data: p.data,
  }));
}

/** Apaga a compra e o lançamento dela no livro. O gerente só desfaz a que ele
 *  lançou, nos primeiros 15 minutos; com anexo, o banco recusa. */
export async function desfazerCompraNaHora(id: string): Promise<void> {
  const { error } = await supabase.rpc('desfazer_compra_na_hora', { p_id: id });
  if (error) throw error;
}

/**
 * As compras pagas na hora de um mês ('YYYY-MM'), da mais nova para a mais
 * velha. Só o DONO lê — para os outros a RLS de contas_pagar devolve vazio, e a
 * tela nem dispara a consulta. Em páginas (emPaginas), pelo teto calado de
 * 1.000 linhas do servidor; o id fecha a ordem para as páginas não trocarem
 * linhas entre si.
 */
export async function comprasNaHora(mes: string): Promise<CompraNaHora[]> {
  const linhas = await emPaginas((de, ate) => supabase.from('contas_pagar')
    .select(COLUNAS_DA_COMPRA.join(', '))
    .eq('paga_na_hora', true)
    // 086: toda leitura de contas_pagar olha o cancelamento. A compra na hora
    // não se cancela (está paga), mas a regra vale sem exceção.
    .is('cancelada_em', null)
    .gte('pago_em', `${mes}-01`)
    .lt('pago_em', inicioDoMesSeguinte(mes))
    .order('pago_em', { ascending: false })
    .order('created_at', { ascending: false })
    .order('id', { ascending: true })
    .range(de, ate) as unknown as PromiseLike<{ data: CompraNaHora[] | null; error: unknown }>);
  // numeric chega como string em alguns caminhos do PostgREST.
  return linhas.map((l) => ({ ...l, valor_real: l.valor_real === null ? null : Number(l.valor_real) }));
}

/** A conta e a linha digitável numa transação só (078): se o banco recusar o
 *  código (boleto já lançado), a conta nova não fica gravada. `codigo` é o
 *  valor final do campo — nulo apaga. */
export async function salvarContaECodigo(c: {
  id?: string | null; descricao: string; categoria: CategoriaConta;
  valorPrevisto: number | null; valorReal?: number | null; vencimento: string;
  fornecedorId?: string | null; observacao?: string | null; codigo: string | null;
  /** Falso = o código não foi mexido: fica o que o banco tem. */
  mexerCodigo?: boolean;
}): Promise<string> {
  return ou(await supabase.rpc('salvar_conta_e_codigo', {
    p_id: c.id ?? null, p_descricao: c.descricao, p_categoria: c.categoria,
    p_valor_previsto: c.valorPrevisto, p_valor_real: c.valorReal ?? null,
    p_vencimento: c.vencimento, p_fornecedor_id: c.fornecedorId ?? null,
    p_observacao: c.observacao ?? null, p_codigo: c.codigo,
    p_mexer_codigo: c.mexerCodigo ?? true,
  }));
}

/** A linha digitável da conta (077) — já conferida por conferirCodigo, só
 *  números. Nulo apaga. */
export async function salvarCodigoBarrasConta(p: { id: string; codigo: string | null }): Promise<void> {
  const { error } = await supabase.rpc('salvar_codigo_barras_conta', {
    p_conta_id: p.id, p_codigo: p.codigo,
  });
  if (error) throw error;
}

// --- Pedidos de compra ao fornecedor (migração 040) ---------------------------
// Não confundir com Encomendas (reservations, 020), que é peça separada para
// um CLIENTE. Aqui é o que a LOJA pede ao fornecedor.

export async function listarPedidos(somenteAbertos = false): Promise<PedidoResumo[]> {
  return ou(await supabase.rpc('listar_pedidos', {
    p_somente_abertos: somenteAbertos, p_limite: 100,
  })) ?? [];
}

export async function itensDoPedido(pedidoId: string): Promise<PedidoItem[]> {
  return ou(await supabase.from('supplier_order_items').select('*')
    .eq('supplier_order_id', pedidoId).order('created_at')) ?? [];
}

export async function recebimentosDoPedido(pedidoId: string): Promise<PedidoRecebimento[]> {
  return ou(await supabase.from('supplier_order_receipts').select('*')
    .eq('supplier_order_id', pedidoId).order('recebido_em')) ?? [];
}

/** Quanto de cada item já chegou — a soma dos eventos de recebimento. */
export async function recebidoPorItem(pedidoId: string): Promise<Record<string, number>> {
  const itens = await itensDoPedido(pedidoId);
  if (!itens.length) return {};
  const linhas = ou(await supabase.from('supplier_order_receipt_items')
    .select('item_id, quantidade').in('item_id', itens.map((i) => i.id))) as
    Array<{ item_id: string; quantidade: number }> | null ?? [];
  const mapa: Record<string, number> = {};
  for (const l of linhas) mapa[l.item_id] = (mapa[l.item_id] ?? 0) + Number(l.quantidade);
  return mapa;
}

export interface ItemDoPedido {
  product_id?: string | null;
  descricao: string;
  codigo_fabricante?: string | null;
  quantidade: number;
  custo_unitario: number;
  tipo?: 'compra' | 'garantia' | 'bonificacao';
  encomenda_cliente_nome?: string | null;
  encomenda_cliente_telefone?: string | null;
  observacao?: string | null;
}

export async function salvarPedido(p: {
  id?: string | null; fornecedorId: string; dataPedido: string;
  previsao?: string | null; canal?: CanalPedido; numeroNoFornecedor?: string | null;
  condicao?: string | null; freteModalidade?: number | null; valorFrete?: number;
  desconto?: number; observacoes?: string | null; itens: ItemDoPedido[];
}): Promise<string> {
  return ou(await supabase.rpc('salvar_pedido', {
    p_id: p.id ?? null, p_fornecedor_id: p.fornecedorId, p_data_pedido: p.dataPedido,
    p_previsao: p.previsao ?? null, p_canal: p.canal ?? 'whatsapp',
    p_numero_fornecedor: p.numeroNoFornecedor ?? null, p_condicao: p.condicao ?? null,
    p_frete_modalidade: p.freteModalidade ?? null, p_valor_frete: p.valorFrete ?? 0,
    p_desconto: p.desconto ?? 0, p_observacoes: p.observacoes ?? null,
    p_itens: p.itens,
  }));
}

/** Recebe (total ou parcial): estoque, custo real e a conta a pagar do que chegou. */
export async function receberPedido(p: {
  pedidoId: string;
  itens: Array<{ item_id: string; quantidade: number; custo_unitario_recebido?: number | null }>;
  recebidoEm?: string | null; nfNumero?: string | null; nfSerie?: string | null;
  nfChave?: string | null; nfValor?: number | null; observacao?: string | null;
  parcelas?: number; primeiroVencimento?: string | null;
}): Promise<string> {
  return ou(await supabase.rpc('receber_pedido', {
    p_pedido_id: p.pedidoId, p_itens: p.itens,
    p_recebido_em: p.recebidoEm ?? null, p_nf_numero: p.nfNumero ?? null,
    p_nf_serie: p.nfSerie ?? null, p_nf_chave: p.nfChave ?? null,
    p_nf_valor: p.nfValor ?? null, p_observacao: p.observacao ?? null,
    p_parcelas: p.parcelas ?? 1, p_primeiro_vencimento: p.primeiroVencimento ?? null,
  }));
}

export async function cancelarSaldoItem(itemId: string): Promise<void> {
  const { error } = await supabase.rpc('cancelar_saldo_item', { p_item_id: itemId });
  if (error) throw error;
}

export async function avisarClienteDaEncomenda(itemId: string): Promise<void> {
  const { error } = await supabase.rpc('avisar_cliente_da_encomenda', { p_item_id: itemId });
  if (error) throw error;
}

export async function cancelarPedido(id: string, motivo: string): Promise<void> {
  const { error } = await supabase.rpc('cancelar_pedido', { p_id: id, p_motivo: motivo });
  if (error) throw error;
}

// --- Fiado: contas a receber (migração 042) -----------------------------------

/** O acerto de uma venda fiada, para o documento impresso (25/08/2026).
 *  Devolve null quando não há conta — venda não fiada, ou coberta por vale. */
export async function quitacaoDaVenda(saleId: string): Promise<QuitacaoDaVenda | null> {
  const linhas = ou(await supabase.rpc('quitacao_da_venda', { p_sale_id: saleId }));
  return (linhas as QuitacaoDaVenda[] | null)?.[0] ?? null;
}

/**
 * Quem deve (listar_fiados). Em PÁGINAS desde 26/09/2026, quando a visão "por
 * pessoa" passou a somar o histórico inteiro no navegador: com os quitados
 * eram 161 contas, e o teto calado de 1.000 linhas (ver emPaginas) chegaria em
 * alguns meses — o "já comprou fiado" de cada pessoa passaria a somar um
 * pedaço. A ordem inclui o id para as páginas não trocarem linhas entre si.
 */
export async function listarFiados(incluirQuitados = false): Promise<FiadoRow[]> {
  return emPaginas<FiadoRow>((de, ate) => supabase
    .rpc('listar_fiados', { p_incluir_quitados: incluirQuitados })
    .order('vendida_em', { ascending: true })
    .order('id', { ascending: true })
    .range(de, ate) as unknown as PromiseLike<{ data: FiadoRow[] | null; error: unknown }>);
}

/**
 * Os ACERTOS do fiado com o dia em que o cliente pagou (042/059) — só o que a
 * visão por pessoa precisa para o prazo médio e o último pagamento: de qual
 * conta, quanto e quando. Lido direto da tabela: a RLS é a da gerência
 * (sel_receber_recs), e para o vendedor a lista volta vazia.
 */
export async function acertosDoFiado(): Promise<AcertoDoFiado[]> {
  const linhas = await emPaginas<AcertoDoFiado>((de, ate) => supabase
    .from('contas_receber_recebimentos')
    .select(COLUNAS_DO_ACERTO.join(', '))
    .order('recebido_em', { ascending: true })
    .order('id', { ascending: true })
    .range(de, ate) as unknown as PromiseLike<{ data: AcertoDoFiado[] | null; error: unknown }>);
  // numeric chega como string em alguns caminhos do PostgREST.
  return linhas.map((a) => ({ ...a, valor: Number(a.valor) }));
}

/**
 * O acerto — parcial ou total. Lança a ENTRADA no caixa na mesma transação.
 *
 * Com `itens` (065), o acerto é POR PEÇA: quem calcula o valor é o servidor
 * (a soma dos subtotais congelados, limitada ao saldo) e o `valor` daqui é
 * ignorado. Sem eles, é o acerto por valor livre de sempre.
 */
export async function receberFiado(p: {
  contaId: string; valor: number; data?: string | null;
  forma?: PaymentMethod; observacao?: string | null; itens?: string[];
}): Promise<string> {
  return ou(await supabase.rpc('receber_fiado', {
    p_conta_id: p.contaId, p_valor: p.valor, p_data: p.data ?? null,
    p_forma: p.forma ?? 'dinheiro', p_observacao: p.observacao ?? null,
    p_itens: p.itens?.length ? p.itens : null,
  }));
}

/** O desconto do acerto com oficina (065): abate a DÍVIDA, não a venda, e não
 *  mexe no caixa. Gerente e dono; motivo opcional. */
export async function darDescontoFiado(p: {
  contaId: string; valor: number; motivo?: string | null;
}): Promise<string> {
  return ou(await supabase.rpc('dar_desconto_fiado', {
    p_conta_id: p.contaId, p_valor: p.valor,
    p_motivo: p.motivo?.trim() ? p.motivo.trim() : null,
  }));
}

export async function desfazerDescontoFiado(id: string): Promise<void> {
  const { error } = await supabase.rpc('desfazer_desconto_fiado', { p_desconto_id: id });
  if (error) throw error;
}

export async function desfazerRecebimentoFiado(id: string): Promise<void> {
  const { error } = await supabase.rpc('desfazer_recebimento_fiado', { p_recebimento_id: id });
  if (error) throw error;
}

/** Fecha a dívida sem dinheiro (dono): reconhecer prejuízo. Não mexe no caixa. */
export async function perderFiado(id: string, motivo: string): Promise<void> {
  const { error } = await supabase.rpc('perder_fiado', { p_conta_id: id, p_motivo: motivo });
  if (error) throw error;
}

export async function reabrirFiado(id: string): Promise<void> {
  const { error } = await supabase.rpc('reabrir_fiado', { p_conta_id: id });
  if (error) throw error;
}

// --- Saúde do negócio (migração 043) — só o dono -------------------------------

export async function resultadoOperacional(meses = 12): Promise<ResultadoOperacionalRow[]> {
  return ou(await supabase.rpc('resultado_operacional', { p_meses: meses })) ?? [];
}

export async function indicadoresDoDono(): Promise<IndicadoresDono> {
  const linhas = ou(await supabase.rpc('indicadores_do_dono')) as IndicadoresDono[] | null;
  if (!linhas?.[0]) throw new Error('Sem indicadores — o banco não devolveu nada.');
  return linhas[0];
}

export async function valorDoEstoque(): Promise<ValorDoEstoqueRow> {
  const linhas = ou(await supabase.rpc('valor_do_estoque')) as ValorDoEstoqueRow[] | null;
  if (!linhas?.[0]) throw new Error('Sem valorização — o banco não devolveu nada.');
  return linhas[0];
}

/**
 * As duas configurações do dono (043): capital investido e alíquota de
 * simulação. Update direto — a RLS de limites_operacao só deixa o dono.
 */
export async function salvarConfigFinanceira(p: {
  capitalInvestido: number | null;
  aliquotaImpostoPct: number | null;
  coringaMultiplicador: number;
}): Promise<void> {
  const { error } = await supabase.from('limites_operacao')
    .update({
      capital_investido: p.capitalInvestido,
      aliquota_imposto_pct: p.aliquotaImpostoPct,
      coringa_multiplicador: p.coringaMultiplicador,
    })
    .eq('id', 1);
  if (error) throw error;
}


// --- O lote de documentos (migração 086) -------------------------------------

/**
 * Lança as contas de um PDF do dia de uma vez. Ou entram TODAS, ou não entra
 * nenhuma — meio lote criado deixaria conta duplicada na segunda tentativa.
 */
export async function lancarLote(
  arquivo: string, paginas: number, contas: ContaDoLote[],
): Promise<number> {
  const { data, error } = await supabase.rpc('lancar_lote', {
    p_arquivo: arquivo, p_paginas: paginas, p_contas: contas,
  });
  if (error) throw error;
  return data as number;
}

/**
 * Cancela as contas de um lote e devolve o que NÃO deu, com o porquê: conta
 * paga e ocorrência de conta fixa ficam de pé. Cancelar não é silencioso.
 */
export async function cancelarLote(
  lote: number, motivo: string,
): Promise<ResultadoDoCancelamento[]> {
  return ou(await supabase.rpc('cancelar_lote', { p_lote: lote, p_motivo: motivo })) ?? [];
}

/** Volta atrás do cancelamento. É o que faz o cancelar ser reversível. */
export async function reativarConta(id: string): Promise<void> {
  const { error } = await supabase.rpc('reativar_conta', { p_id: id });
  if (error) throw error;
}

/** O feed das importações, da mais nova para a mais velha. Só dono. */
export async function historicoDeLotes(limite = 30): Promise<LoteDeDocumentos[]> {
  return ou(await supabase.rpc('historico_de_lotes', { p_limite: limite })) ?? [];
}

export async function excluirContaRecorrente(id: string): Promise<void> {
  const { error } = await supabase.rpc('excluir_conta_recorrente', { p_id: id });
  if (error) throw error;
}

/** Só avulsa NÃO paga — paga e ocorrência de regra o banco recusa com o
 *  caminho certo na mensagem. */
export async function excluirConta(id: string): Promise<void> {
  const { error } = await supabase.rpc('excluir_conta', { p_id: id });
  if (error) throw error;
}

// --- Anexos da conta: o comprovante (044) e o boleto (080) --------------------

export async function salvarComprovanteConta(p: {
  contaId: string; base64: string; contentType: 'image/jpeg' | 'application/pdf';
  nome?: string | null;
  /** Qual vaga (080). Sem ele, o comprovante — o que sempre foi. */
  tipo?: TipoDeAnexo;
}): Promise<void> {
  const { error } = await supabase.rpc('salvar_comprovante_conta', {
    p_conta_id: p.contaId, p_dados: p.base64,
    p_content_type: p.contentType, p_nome: p.nome ?? null,
    p_tipo: p.tipo ?? 'comprovante',
  });
  if (error) throw error;
}

export interface ComprovanteCompleto {
  content_type: string; nome: string | null; dados_base64: string;
}

export async function obterComprovanteConta(
  contaId: string, tipo: TipoDeAnexo = 'comprovante',
): Promise<ComprovanteCompleto> {
  const dados = ou(await supabase.rpc('obter_comprovante_conta', { p_conta_id: contaId, p_tipo: tipo })) as
    ComprovanteCompleto[] | ComprovanteCompleto;
  const linha = Array.isArray(dados) ? dados[0] : dados;
  if (!linha) throw new Error(`Esta conta não tem ${tipo} anexado.`);
  return linha;
}

export async function removerComprovanteConta(
  contaId: string, tipo: TipoDeAnexo = 'comprovante',
): Promise<void> {
  const { error } = await supabase.rpc('remover_comprovante_conta', { p_conta_id: contaId, p_tipo: tipo });
  if (error) throw error;
}

/** Passa o anexo da vaga `tipo` para a outra; com as duas ocupadas, troca os
 *  dois de lugar (080). É o conserto do que entrou como comprovante antes de
 *  haver a vaga do boleto. */
export async function mudarVagaDoAnexo(contaId: string, tipo: TipoDeAnexo): Promise<void> {
  const { error } = await supabase.rpc('mudar_vaga_do_anexo', { p_conta_id: contaId, p_tipo: tipo });
  if (error) throw error;
}

// --- Promoções com vigência (047) ---------------------------------------------

export async function criarPromocao(p: {
  productId: string; descontoPct: number; inicio?: string | null; fim: string;
}): Promise<string> {
  return ou(await supabase.rpc('criar_promocao', {
    p_product_id: p.productId, p_desconto_pct: p.descontoPct,
    p_inicio: p.inicio ?? null, p_fim: p.fim,
  }));
}

export async function encerrarPromocao(id: string): Promise<void> {
  const { error } = await supabase.rpc('encerrar_promocao', { p_id: id });
  if (error) throw error;
}

/** A vassoura das vigências — a tela de Promoções chama ao abrir. Falhar aqui
 *  não pode derrubar a tela: é manutenção, não consulta. */
export async function aplicarPromocoes(): Promise<void> {
  await supabase.rpc('aplicar_promocoes');
}

// --- As consultas LEVES do sino (17/08/2026) ----------------------------------
// O sino roda em toda tela a cada 10 min: cada consulta daqui pede só as
// colunas que o aviso usa. Quem recorta papel é a RLS — para quem não pode
// ver, a resposta é vazia, nunca erro.

/** Encomendas ATIVAS que vencem hoje. Vendedor recebe só as dele (RLS);
 *  gerência, todas. */
export async function encomendasVencendoEm(diaISO: string): Promise<Array<{
  id: string; cliente_nome: string; pecas: string[];
}>> {
  const linhas = ou(await supabase.from('reservations')
    .select('id, cliente_nome, reservation_items(descricao_avulsa, products(descricao))')
    .eq('status', 'ativa')
    .eq('expira_em', diaISO)
    .limit(50)) as unknown as Array<{
      id: string; cliente_nome: string;
      reservation_items: Array<{
        descricao_avulsa: string | null;
        products: { descricao: string } | Array<{ descricao: string }> | null;
      }> | null;
    }> | null;
  // 066: a encomenda é lista, e o sino diz QUAIS peças — sem isso o aviso
  // manda o vendedor abrir a tela para descobrir do que se trata.
  return (linhas ?? []).map((l) => ({
    id: l.id,
    cliente_nome: l.cliente_nome,
    pecas: (l.reservation_items ?? []).map((i) => nomeDaPecaEncomendada({
      descricao_avulsa: i.descricao_avulsa,
      peca: Array.isArray(i.products) ? i.products[0] ?? null : i.products,
    })),
  }));
}

/** Contas em aberto com vencimento até HOJE (atrasadas + vencendo hoje). */
export async function contasVencendoAteHoje(hojeISOStr: string): Promise<Array<{
  id: string; vencimento: string; valor_previsto: number | null; valor_real: number | null;
}>> {
  return ou(await supabase.from('contas_pagar')
    .select('id, vencimento, valor_previsto, valor_real')
    .is('pago_em', null)
    // 086: conta cancelada no alerta seria cobrança fantasma no sino.
    .is('cancelada_em', null)
    .lte('vencimento', hojeISOStr)
    .limit(100)) ?? [];
}

/** Orçamentos abertos que vencem entre hoje e amanhã. */
export async function orcamentosVencendoAte(deISO: string, ateISO: string): Promise<Array<{
  id: string; codigo: string; cliente_nome: string | null; valido_ate: string;
}>> {
  return ou(await supabase.from('quotes')
    .select('id, codigo, cliente_nome, valido_ate')
    .is('convertido_em', null)
    .gte('valido_ate', deISO)
    .lte('valido_ate', ateISO)
    .limit(50)) ?? [];
}

/** Promoções abertas terminando na janela (hoje..daqui a N dias). */
export async function promocoesTerminandoAte(deISO: string, ateISO: string): Promise<Array<{
  id: string; fim: string; products: { descricao: string } | null;
}>> {
  const linhas = ou(await supabase.from('promotions')
    .select('id, fim, products(descricao)')
    .is('encerrada_em', null)
    .gte('fim', deISO)
    .lte('fim', ateISO)
    .limit(50)) as unknown as Array<{
      id: string; fim: string;
      products: { descricao: string } | Array<{ descricao: string }> | null;
    }> | null;
  return (linhas ?? []).map((l) => ({
    ...l,
    products: Array.isArray(l.products) ? l.products[0] ?? null : l.products,
  }));
}

/** As promoções ABERTAS (datas/% por produto), para juntar à lista vigente. */
export async function promocoesAbertas(): Promise<Promotion[]> {
  return ou(await supabase.from('promotions')
    .select('*').is('encerrada_em', null).order('created_at', { ascending: false })) ?? [];
}

/** O HISTÓRICO: o que já saiu de vigor, mais novo primeiro. */
export async function promocoesEncerradas(limite = 100): Promise<Array<Promotion & {
  products: { sku: string; descricao: string; marca: string | null } | null;
}>> {
  const linhas = ou(await supabase.from('promotions')
    .select('*, products(sku, descricao, marca)')
    .not('encerrada_em', 'is', null)
    .order('encerrada_em', { ascending: false })
    .limit(limite)) as unknown as Array<Promotion & {
      products: { sku: string; descricao: string; marca: string | null }
        | Array<{ sku: string; descricao: string; marca: string | null }> | null;
    }> | null;
  return (linhas ?? []).map((l) => ({
    ...l,
    products: Array.isArray(l.products) ? l.products[0] ?? null : l.products,
  }));
}

// --- Os carros do cliente (051) -----------------------------------------------

export async function veiculosDoCliente(
  customerId: string, incluirInativos = false,
): Promise<CustomerVehicle[]> {
  return ou(await supabase.rpc('veiculos_do_cliente', {
    p_customer_id: customerId, p_incluir_inativos: incluirInativos,
  })) ?? [];
}

export async function salvarVeiculoCliente(v: {
  id?: string | null; customerId: string; placa?: string | null;
  montadora?: string | null; modelo?: string | null; ano?: number | null;
  motorizacao?: string | null; apelido?: string | null;
  observacao?: string | null; ativo?: boolean;
}): Promise<string> {
  return ou(await supabase.rpc('salvar_veiculo_cliente', {
    p_id: v.id ?? null,
    p_customer_id: v.customerId,
    p_placa: v.placa ?? null,
    p_montadora: v.montadora ?? null,
    p_modelo: v.modelo ?? null,
    p_ano: v.ano ?? null,
    p_motorizacao: v.motorizacao ?? null,
    p_apelido: v.apelido ?? null,
    p_observacao: v.observacao ?? null,
    p_ativo: v.ativo ?? true,
  }));
}

/** De quem é esta placa? O caminho inverso, para o balcão. */
export async function donoDaPlaca(placa: string): Promise<{
  customer_id: string; cliente: string; vehicle_id: string; apelido: string | null;
} | null> {
  const linhas = ou(await supabase.rpc('dono_da_placa', { p_placa: placa })) as Array<{
    customer_id: string; cliente: string; vehicle_id: string; apelido: string | null;
  }> | null;
  return linhas?.[0] ?? null;
}

// --- A lista de faxina do cadastro (120) --------------------------------------
// As contas são do BANCO, e não por gosto: a agregação do PostgREST está
// desligada nos dois composes, com teste (agregacaoDesligada.test.ts), porque
// ligá-la para contar "vezes" devolveria o faturamento somado a quem o dono
// tirou dele. E não pode ser leitura de tabela: a RLS de sale_items é "dono OU
// venda própria", e o gerente receberia uma lista PARCIAL sem erro nenhum.

export async function faxinaFamilias(dias = 90): Promise<FaxinaFamilia[]> {
  return ou(await supabase.rpc('faxina_familias', { p_dias: dias })) ?? [];
}

export async function faxinaItens(p?: {
  familia?: string | null; dias?: number; limite?: number; incluirDecididas?: boolean;
}): Promise<FaxinaItem[]> {
  return ou(await supabase.rpc('faxina_itens', {
    p_familia: p?.familia ?? null,
    p_dias: p?.dias ?? 90,
    p_limite: p?.limite ?? 40,
    p_incluir_decididas: p?.incluirDecididas ?? false,
  })) ?? [];
}

/** Grava a decisão sobre um texto. 'cadastrada' EXIGE a peça que nasceu — o
 *  banco recusa em P0001 (frase em português), não em 42501. */
export async function marcarFaxina(p: {
  chave: string; situacao: SituacaoFaxina; descricao: string;
  productId?: string | null; motivo?: string | null;
}): Promise<FaxinaDecisao> {
  return ou(await supabase.rpc('marcar_faxina', {
    p_chave: p.chave, p_situacao: p.situacao, p_descricao: p.descricao,
    p_product_id: p.productId ?? null, p_motivo: p.motivo ?? null,
  }));
}

// --- O fechamento do gerente (052) --------------------------------------------

export async function pecasVendidasNoDia(dia?: string): Promise<PecaVendidaNoDia[]> {
  return ou(await supabase.rpc('pecas_vendidas_no_dia', { p_dia: dia ?? null })) ?? [];
}

export async function decidirReposicao(p: {
  dia: string; chave: string; descricao: string; situacao: SituacaoReposicao;
  quantidade?: number; productId?: string | null; fornecedorId?: string | null;
}): Promise<string> {
  return ou(await supabase.rpc('decidir_reposicao', {
    p_dia: p.dia, p_chave: p.chave, p_descricao: p.descricao,
    p_situacao: p.situacao, p_quantidade: p.quantidade ?? 1,
    p_product_id: p.productId ?? null, p_fornecedor_id: p.fornecedorId ?? null,
  }));
}

export async function acrescentarReposicao(p: {
  descricao: string; quantidade?: number; fornecedorId?: string | null;
  productId?: string | null; dia?: string | null;
}): Promise<string> {
  return ou(await supabase.rpc('acrescentar_reposicao', {
    p_descricao: p.descricao, p_quantidade: p.quantidade ?? 1,
    p_fornecedor_id: p.fornecedorId ?? null, p_product_id: p.productId ?? null,
    p_dia: p.dia ?? null,
  }));
}

export async function reposicaoManualDoDia(dia?: string): Promise<ReposicaoDecisao[]> {
  return ou(await supabase.rpc('reposicao_manual_do_dia', { p_dia: dia ?? null })) ?? [];
}

/** Corrige o nome e grava o código do fabricante no item do carrinho, antes
 *  de o pedido nascer (063). Campo ausente = não mexe; texto vazio no código
 *  = limpa. */
export async function editarItemReposicao(p: {
  dia: string; chave: string;
  descricao?: string; codigoFabricante?: string;
}): Promise<void> {
  ou(await supabase.rpc('editar_item_reposicao', {
    p_dia: p.dia,
    p_chave: p.chave,
    p_descricao: p.descricao ?? null,
    p_codigo_fabricante: p.codigoFabricante ?? null,
  }));
}

/** A oferta: grava o código no CADASTRO da peça (só quando está vazio). */
export async function atualizarCodigoFabrica(
  productId: string, codigo: string,
): Promise<void> {
  ou(await supabase.rpc('atualizar_codigo_fabrica', {
    p_product_id: productId, p_codigo: codigo, p_origem: 'reposicao',
  }));
}

export async function gerarPedidoDaReposicao(p: {
  fornecedorId: string; dia?: string | null; previsao?: string | null;
  observacoes?: string | null;
}): Promise<string> {
  return ou(await supabase.rpc('gerar_pedido_da_reposicao', {
    p_fornecedor_id: p.fornecedorId, p_dia: p.dia ?? null,
    p_previsao: p.previsao ?? null, p_observacoes: p.observacoes ?? null,
  }));
}

// --- Sugestões do balcão (053) + o texto do pedido para o grupo ---------------

export async function sugerirPedido(p: {
  descricao: string; motivo: string; quantidade?: number; productId?: string | null;
}): Promise<string> {
  return ou(await supabase.rpc('sugerir_pedido', {
    p_descricao: p.descricao, p_motivo: p.motivo,
    p_quantidade: p.quantidade ?? 1, p_product_id: p.productId ?? null,
  }));
}

/** A RLS recorta: o vendedor recebe as dele; a gerência, todas. */
export async function sugestoesAbertas(limite = 100): Promise<SugestaoPedido[]> {
  return ou(await supabase.rpc('sugestoes_abertas', { p_limite: limite })) ?? [];
}

export async function responderSugestao(p: {
  id: string; acatar: boolean; resposta?: string | null; fornecedorId?: string | null;
}): Promise<void> {
  const { error } = await supabase.rpc('responder_sugestao', {
    p_id: p.id, p_acatar: p.acatar,
    p_resposta: p.resposta ?? null, p_fornecedor_id: p.fornecedorId ?? null,
  });
  if (error) throw error;
}

export interface LinhaPedidoTexto {
  descricao: string; codigo: string | null; quantidade: number;
}

export async function carrinhoParaWhatsApp(
  fornecedorId: string, dia?: string | null,
): Promise<LinhaPedidoTexto[]> {
  return ou(await supabase.rpc('carrinho_para_whatsapp', {
    p_fornecedor_id: fornecedorId, p_dia: dia ?? null,
  })) ?? [];
}

export async function pedidoParaWhatsApp(pedidoId: string): Promise<LinhaPedidoTexto[]> {
  return ou(await supabase.rpc('pedido_para_whatsapp', {
    p_supplier_order_id: pedidoId,
  })) ?? [];
}

// --- Controle da fila de impressão (19/08/2026) -------------------------------

export async function saudeDaFila(): Promise<SaudeDaFila> {
  const linhas = ou(await supabase.rpc('saude_da_fila')) as SaudeDaFila[] | null;
  return linhas?.[0] ?? {
    na_fila: 0, parados_ha_10min: 0, falhas: 0, ultimo_enviado_em: null,
  };
}

export async function cancelarImpressao(id: string): Promise<void> {
  const { error } = await supabase.rpc('cancelar_impressao', { p_id: id });
  if (error) throw error;
}

/** Manda de novo: serve para o que falhou e para 2ª via do que já saiu. */
export async function reenfileirarImpressao(id: string): Promise<void> {
  const { error } = await supabase.rpc('reenfileirar_impressao', { p_id: id });
  if (error) throw error;
}

// --- A gaveta do dia (055) ----------------------------------------------------

export async function caixaDoDia(dia?: string): Promise<CaixaDoDia> {
  const linhas = ou(await supabase.rpc('caixa_do_dia', { p_dia: dia ?? null })) as
    CaixaDoDia[] | null;
  return linhas?.[0] ?? {
    dia: dia ?? '', aberto: false, fundo_inicial: null, aberto_por_nome: null,
    fechado: false, valor_contado: null, valor_esperado_congelado: null,
    fechado_por_nome: null, esperado_agora: null,
    entradas_dinheiro: 0, saidas_dinheiro: 0,
    vendas_a_conferir: 0, vendas_conferidas: 0,
  };
}

export async function abrirCaixa(fundo: number, dia?: string): Promise<string> {
  return ou(await supabase.rpc('abrir_caixa', { p_fundo: fundo, p_dia: dia ?? null }));
}

/** Devolve a DIFERENÇA do fechamento (positiva sobrou, negativa faltou). */
export async function fecharCaixa(p: {
  contado: number; observacao?: string | null; dia?: string | null;
}): Promise<number> {
  return ou(await supabase.rpc('fechar_caixa', {
    p_contado: p.contado, p_observacao: p.observacao ?? null, p_dia: p.dia ?? null,
  }));
}

export async function conferirVenda(saleId: string, recebido?: number | null): Promise<void> {
  const { error } = await supabase.rpc('conferir_venda', {
    p_sale_id: saleId, p_recebido: recebido ?? null,
  });
  if (error) throw error;
}

export async function desconferirVenda(saleId: string): Promise<void> {
  const { error } = await supabase.rpc('desconferir_venda', { p_sale_id: saleId });
  if (error) throw error;
}

/** O dia da loja num objeto só (060) — a fonte do documento impresso. */
export async function resumoDoDia(dia?: string): Promise<ResumoDoDia> {
  return ou(await supabase.rpc('resumo_do_dia', { p_dia: dia ?? null }));
}

// --- A entrega da venda (061) -------------------------------------------------
// O endereço fica CONGELADO na venda: o papel que saiu com o entregador é fato
// daquele dia. O cadastro do cliente é só o padrão que preenche a tela.

export async function definirEntrega(p: {
  saleId: string; endereco: string; contato?: string | null;
  observacao?: string | null; formaPrevista?: PaymentMethod | null;
  trocoPara?: number | null;
}): Promise<void> {
  const { error } = await supabase.rpc('definir_entrega', {
    p_sale_id: p.saleId,
    p_endereco: p.endereco,
    p_contato: p.contato ?? null,
    p_observacao: p.observacao ?? null,
    p_forma_prevista: p.formaPrevista ?? null,
    p_troco_para: p.trocoPara ?? null,
  });
  if (error) throw error;
}

export async function cancelarEntrega(saleId: string): Promise<void> {
  const { error } = await supabase.rpc('cancelar_entrega', { p_sale_id: saleId });
  if (error) throw error;
}

export async function marcarEntregaFeita(saleId: string): Promise<void> {
  const { error } = await supabase.rpc('marcar_entrega_feita', { p_sale_id: saleId });
  if (error) throw error;
}

export async function desfazerEntregaFeita(saleId: string): Promise<void> {
  const { error } = await supabase.rpc('desfazer_entrega_feita', { p_sale_id: saleId });
  if (error) throw error;
}

/** A rota do dia: o que já saiu do estoque e ainda não chegou no cliente. */
export async function entregasPendentes(): Promise<EntregaPendente[]> {
  return ou(await supabase.rpc('entregas_pendentes')) ?? [];
}

/** A linha do tempo do dinheiro (23/08/2026) — a fonte da tela /fluxo.
 *  Realizado + previsto por dia, zero-fill já feito no banco. Só dono. */
export async function fluxoDeCaixa(
  diasPassado = 30, diasFuturo = 90,
): Promise<FluxoDia[]> {
  return ou(await supabase.rpc('fluxo_de_caixa', {
    p_dias_passado: diasPassado, p_dias_futuro: diasFuturo,
  })) ?? [];
}
// --- O simulador financeiro (migração 085) -----------------------------------
// A base MEDIDA numa chamada, e as premissas que o dono digita. Tudo só do
// dono: a RLS das duas tabelas e o exige_papel no corpo da RPC.

/**
 * A base do simulador: o que a loja fez, com a qualidade do dado ao lado.
 *
 * É CARA DE PROPÓSITO — a DRE que ela embute varre a história inteira. Chame
 * UMA vez e guarde; quem simula é `app/src/lib/simulacao.ts`, no aparelho.
 * Por isso a tela usa `staleTime` longo: mexer numa premissa não volta ao banco.
 */
export async function baseDaSimulacao(meses = 6): Promise<BaseDaSimulacao> {
  const { data, error } = await supabase.rpc('base_da_simulacao', { p_meses: meses });
  if (error) throw error;
  return data as BaseDaSimulacao;
}

/**
 * Os cenários (Base, Aperto, Folga), o padrão primeiro.
 *
 * `base_da_simulacao` já devolve os cenários dentro dela, mas não os AJUSTES DE
 * MÊS — e quem muda uma premissa precisa reler só isto, sem pagar a base de
 * novo. São duas tabelas, então duas leituras: a 085 criou `cenarios_financeiros`
 * E `cenario_meses`, e esquecer a segunda não dá erro nenhum (o `ajustes` do
 * motor tem default `[]`), só silenciosamente ignora o feriado de novembro.
 */
export async function cenariosFinanceiros(): Promise<CenarioFinanceiro[]> {
  const { data, error } = await supabase
    .from('cenarios_financeiros')
    .select('*')
    .order('padrao', { ascending: false })
    .order('nome');
  if (error) throw error;
  return (data as CenarioFinanceiro[] | null) ?? [];
}

/** Os meses que fogem da regra dentro de um cenário (feriado, 13º, o que não se repete). */
/**
 * A venda que o dono digitou para cada dia da semana, num cenário (091).
 *
 * Lista curta — no máximo sete linhas —, e de propósito FORA da
 * base_da_simulacao: mexer numa premissa não pode pagar o custo de refazer a
 * RPC inteira, que varre venda, conta, folha, fiado e estoque.
 */
export async function diasDaSemanaDoCenario(cenarioId: number): Promise<CenarioDiaDaSemana[]> {
  const { data, error } = await supabase
    .from('cenario_dias_da_semana')
    .select('*')
    .eq('cenario_id', cenarioId)
    .order('dia_semana');
  if (error) throw error;
  return (data as CenarioDiaDaSemana[] | null) ?? [];
}

/**
 * Grava os dias que o dono digitou, numa chamada só.
 *
 * UM upsert, não sete requisições: sem transação entre elas, uma falha no meio
 * deixaria a semana metade digitada e metade medida, e o dono não teria como
 * saber qual metade. `null` em `venda_por_dia` APAGA a linha — é como ele diz
 * "volta a usar o que foi medido", que é diferente de digitar zero ("não abro").
 */
export async function salvarDiasDaSemana(
  cenarioId: number,
  dias: ReadonlyArray<{ dia_semana: number; venda_por_dia: number | null }>,
): Promise<void> {
  const apagar = dias.filter((d) => d.venda_por_dia == null).map((d) => d.dia_semana);
  const gravar = dias
    .filter((d) => d.venda_por_dia != null)
    .map((d) => ({ cenario_id: cenarioId, dia_semana: d.dia_semana, venda_por_dia: d.venda_por_dia }));

  if (apagar.length > 0) {
    const { error } = await supabase.from('cenario_dias_da_semana')
      .delete().eq('cenario_id', cenarioId).in('dia_semana', apagar);
    if (error) throw error;
  }
  if (gravar.length > 0) {
    const { error } = await supabase.from('cenario_dias_da_semana')
      .upsert(gravar, { onConflict: 'cenario_id,dia_semana' });
    if (error) throw error;
  }
}

export async function mesesDoCenario(cenarioId: number): Promise<CenarioMes[]> {
  const { data, error } = await supabase
    .from('cenario_meses')
    .select('*')
    .eq('cenario_id', cenarioId)
    .order('competencia');
  if (error) throw error;
  return (data as CenarioMes[] | null) ?? [];
}
/**
 * As premissas de UM cenário, editadas pelo dono (085).
 *
 * Update direto, não RPC: é configuração, como o `limites_operacao` — a policy
 * `cenarios_do_dono` é `for all` e só passa dono, e a escrita não concede poder
 * a pessoa nomeada (que é o que obriga a passar por definer, o caso do
 * `limites_por_pessoa`).
 *
 * `updated_at` NÃO vai daqui: a trigger `trg_cenarios_financeiros_updated`
 * carimba. E `padrao` também não — o índice único `cenarios_financeiros_um_padrao`
 * recusa um segundo padrão, e trocar qual é o padrão exigiria limpar o antigo e
 * marcar o novo em DUAS requisições, sem transação entre elas: se a segunda
 * falhasse, a loja ficaria sem cenário padrão. Trocar o padrão precisa de uma
 * RPC atômica, e ela não existe ainda.
 */
export async function salvarPremissasDoCenario(
  id: number,
  premissas: Partial<Omit<CenarioFinanceiro, 'id' | 'padrao' | 'created_at' | 'updated_at' | 'criado_por'>>,
): Promise<void> {
  const { error } = await supabase.from('cenarios_financeiros').update(premissas).eq('id', id);
  if (error) throw error;
}

/**
 * O ajuste de UM mês dentro de um cenário: dias de loja, venda por dia,
 * entrada e saída que não se repetem.
 *
 * `upsert` com `onConflict` no par (cenario_id, competencia) — que é o unique
 * da tabela. Sem isso, marcar novembro duas vezes daria erro de chave em vez de
 * corrigir o que já estava lá.
 */
export async function salvarAjusteDoMes(ajuste: {
  cenario_id: number;
  competencia: string;
  dias_de_loja?: number | null;
  venda_por_dia_override?: number | null;
  entrada_extra?: number | null;
  saida_extra?: number | null;
  observacao?: string | null;
}): Promise<void> {
  const { error } = await supabase
    .from('cenario_meses')
    .upsert(ajuste, { onConflict: 'cenario_id,competencia' });
  if (error) throw error;
}

/** Tira o ajuste de um mês — o mês volta a seguir o cenário. */
export async function apagarAjusteDoMes(id: number): Promise<void> {
  const { error } = await supabase.from('cenario_meses').delete().eq('id', id);
  if (error) throw error;
}



// --- Parceiros B2B (migração 062) --------------------------------------------
// O pedido que o parceiro fez no portal. A RLS entrega tudo a qualquer perfil
// ativo (quem separa peça é o balcão); as transições são RPC com papel dentro.

export interface PedidoB2B extends PartnerOrder {
  partner_order_items: PartnerOrderItem[];
}

export async function listarPedidosB2B(): Promise<PedidoB2B[]> {
  return ou(await supabase
    .from('partner_orders')
    .select('*, partner_order_items(*)')
    .order('created_at', { ascending: false })
    .limit(400)) ?? [];
}

/** Só os que aguardam a loja — a fonte do sino (mais antigo primeiro). */
export async function pedidosB2BNovos(): Promise<Array<
  Pick<PartnerOrder, 'id' | 'codigo' | 'cliente_nome' | 'total' | 'created_at'>
>> {
  return ou(await supabase
    .from('partner_orders')
    .select('id, codigo, cliente_nome, total, created_at')
    .eq('situacao', 'novo')
    .order('created_at', { ascending: true })) ?? [];
}

export async function avancarPedidoB2B(
  id: string, situacao: SituacaoPedidoB2B, motivo?: string,
): Promise<void> {
  ou(await supabase.rpc('avancar_pedido_b2b', {
    p_id: id, p_situacao: situacao, p_motivo: motivo ?? null,
  }));
}

export async function resumoParceirosB2B(): Promise<ResumoParceiroB2B[]> {
  return ou(await supabase.rpc('resumo_parceiros_b2b')) ?? [];
}

/** Os logins existentes, com o nome do cliente — a tela de acessos. */
export interface ContaDeParceiro extends PartnerAccount {
  customers: { nome: string } | null;
}

export async function contasDeParceiros(): Promise<ContaDeParceiro[]> {
  return ou(await supabase
    .from('partner_accounts')
    .select('*, customers(nome)')
    .order('created_at', { ascending: false })) ?? [];
}

/** Só a coluna desconto_pct é concedida a update (rls.sql §17) — `ativo` anda
 *  junto com o banimento no GoTrue e só muda pelo agente. */
export async function atualizarDescontoParceiro(
  userId: string, descontoPct: number,
): Promise<void> {
  const { error } = await supabase
    .from('partner_accounts')
    .update({ desconto_pct: descontoPct })
    .eq('user_id', userId);
  if (error) throw error;
}

/**
 * Logins de parceiro são criados pelo AGENTE da VPS (/infra/v1/parceiros),
 * irmã da /infra/v1/equipe e pela mesma razão: criar conta exige a service
 * key, que nunca pode estar no navegador. O agente revalida o papel do
 * chamador no servidor antes de agir.
 */
async function chamarParceirosAdmin<T>(corpo: Record<string, unknown>): Promise<T> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (!token) throw new Error('Sessão expirada — entre de novo.');
  const base = import.meta.env.VITE_SUPABASE_URL as string;
  const r = await fetch(`${base}/infra/v1/parceiros`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(corpo),
  });
  const resposta = await r.json().catch(() => null) as { erro?: string } | null;
  if (!r.ok) throw new Error(resposta?.erro ?? `O agente respondeu ${r.status}.`);
  return resposta as T;
}

export const criarParceiro = (p: {
  customer_id: string; email: string; senha: string; desconto_pct?: number;
}) => chamarParceirosAdmin<{ ok: true; id: string; cliente: string }>({ acao: 'criar', ...p });

export const redefinirSenhaParceiro = (id: string, senha: string) =>
  chamarParceirosAdmin<{ ok: true }>({ acao: 'redefinir_senha', id, senha });

/** Desativar bane no GoTrue E marca a tabela na mesma ação (os dois estados
 *  não podem divergir — a lição do "demitido que ainda loga"). */
export const alternarAtivacaoParceiro = (id: string, desativar: boolean) =>
  chamarParceirosAdmin<{ ok: true; ativo: boolean }>({
    acao: desativar ? 'desativar' : 'reativar', id,
  });

export const excluirParceiro = (id: string) =>
  chamarParceirosAdmin<{ ok: true }>({ acao: 'excluir', id });

// --- O mural de notas (migração 072) -----------------------------------------
//
// Tudo por RPC, inclusive a LEITURA: a nota sozinha traz um uuid de autor, e
// nome e cor moram em `profiles`, onde o vendedor enxerga uma linha só — a
// dele (rls.sql §6). São as definer de core/sql/functions/notas.sql que
// atravessam essa fronteira, e por isso a tela não monta select nenhum aqui.

/** As notas PENDURADAS (a grade). `finalizadas` troca para o histórico. */
export async function muralDeNotas(finalizadas = false): Promise<NotaDoMural[]> {
  return ou(await supabase.rpc('mural_de_notas', {
    p_finalizadas: finalizadas,
    // O teto real é do banco (500); mandar o número daqui é só dizer de quanto
    // a tela precisa — a grade lê o dia, não o ano.
    p_limite: 200,
  })) ?? [];
}

/** As COLUNAS da grade: quem tem coluna, de que cor, com quantas penduradas. */
export async function colaboradoresDoMural(): Promise<ColaboradorDoMural[]> {
  return ou(await supabase.rpc('colaboradores_do_mural', {})) ?? [];
}

export async function criarNota(texto: string, urgente = false): Promise<string> {
  return ou(await supabase.rpc('criar_nota', { p_texto: texto, p_urgente: urgente }));
}

export async function editarNota(
  id: string, texto: string, urgente: boolean,
): Promise<void> {
  ou(await supabase.rpc('editar_nota', {
    p_id: id, p_texto: texto, p_urgente: urgente,
  }));
}

/** Tirar da grade — e, com `finalizar = false`, devolver a ela. */
export async function finalizarNota(id: string, finalizar = true): Promise<void> {
  ou(await supabase.rpc('finalizar_nota', { p_id: id, p_finalizar: finalizar }));
}

// --- Meu dinheiro: o botão do dono (migração 079) -----------------------------
//
// A ÚNICA peça do Meu dinheiro no banco da loja (ADR-008): "este papel pode
// usar?". O planejamento de cada pessoa mora no banco `pessoal` e passa por
// src/pessoal/api.ts — nunca por aqui, e nada daqui lê o que está lá.

/** Os três papéis e se cada um pode usar. Toda a equipe lê (o menu de cada um
 *  precisa saber se mostra o item); o parceiro B2B não vê linha (rls.sql §20). */
export async function liberacoesMeuDinheiro(): Promise<LiberacaoMeuDinheiro[]> {
  return ou(await supabase.from('personal_release').select('*').order('papel')) ?? [];
}

/** Liga ou desliga um papel. Só o dono: a RPC exige no corpo (regra 1). Não
 *  apaga nada no banco pessoal — desligar só nega o acesso. */
export async function liberarMeuDinheiro(papel: Papel, liberado: boolean): Promise<void> {
  ou(await supabase.rpc('liberar_meu_dinheiro', { p_papel: papel, p_liberado: liberado }));
}

/**
 * Avisa o agente da VPS que o botão mudou: ele relê personal_release, cruza com
 * `profiles` e manda ao banco pessoal a lista de quem pode usar (mão única, sem
 * nome). É o que faz o botão valer NA HORA; sem o aviso, o agente confere
 * sozinho a cada 5 minutos. O aviso não leva dado nenhum no corpo: o que vai
 * para o banco pessoal o agente lê do banco da loja, com o token de quem chamou
 * conferido do lado dele (como em /infra/v1/equipe).
 */
export async function sincronizarMeuDinheiro(): Promise<void> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (!token) throw new Error('Sessão expirada — entre de novo.');
  const base = import.meta.env.VITE_SUPABASE_URL as string;
  const r = await fetch(`${base}/infra/v1/pessoal/sincronizar`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
  });
  const resposta = await r.json().catch(() => null) as { erro?: string } | null;
  if (!r.ok) throw new Error(resposta?.erro ?? `O agente respondeu ${r.status}.`);
}
